import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { getRandomLightColor } from 'src/app/utils/utils.function';
import { JobDataService } from '../../sale/job/job.data.service';
import { CurrencyMaskOptions } from '../../shared/shared.constants';
import { ConfirmPurchaseOrderComponent } from '../confirm-purchase-order/confirm-purchase-order.component';
import { PurchaseOrderService } from '../purchase-order.service';

@Component({
    selector: 'app-purchase-order',
    templateUrl: './purchase-order.component.html',
    styleUrls: ['./purchase-order.component.scss'],
    standalone: false
})
export class PurchaseOrderComponent implements OnInit {
  showJobSearchInput: boolean = true;
  selectedJobItem: any = {};
  jobSearchParams = {
    type: '',
    module_id: '',
    search: ''
  };
  typeList = [{
    id: 1,
    name: 'Regular',
    value: 'regular'
  },
  {
    id: 2,
    name: 'Bulk',
    value: 'bulk'
  },
  ]
  jobId: any;
  exclude_date: number = 0
  exclude_date_boolean: boolean = false

  fromDate: any
  toDate: any
  jobDetail: any = ''
  scopeName: any = ''
  scopeData: any = null  // Store scope data from dialog
  jobFilterResponse: any = []
  materialList: any;
  scopeId: any;
  vendorList: any;
  materialVendorListMap: Map<number, any[]> = new Map();  // Store vendor lists per material ID
  vendorLoadingMaterialIds: Set<number> = new Set();      // Material IDs with an in-flight vendor fetch
  public currencyMaskOptions = CurrencyMaskOptions;
  vendorGroupList: any = [];
  materialSplitGroupList: any = [];
  separatePOJobList: any = [];
  pendingScopeData: any = null;  // Store scope data until PO edit items are loaded
  allMaterialSelected: boolean = false
  allSplitMaterialSelected: boolean = false
  calculation: any = {
    contract_price_total: 0,
    previous_total: 0,
    new_total: 0,
  }

  currencyOptions = {
    prefix: '$ ',
    thousands: ',',
    decimal: '.',
    precision: 2,
    allowNegative: false,
    suffix: '',
    align: 'center'
  };

  purchaseOrderObject: any = {
    price_engineering_flag: false,
    group_material_flag: false,
    type: 'regular',
  }

  vendorForm: FormGroup = new FormGroup({
    quote_number: new FormControl(''),
    amount: new FormControl(''),
    shipping_cost: new FormControl('')
  })

  vendorDetail: any
  vendorName: any  // For single vendor display in regular PO edit mode
  vendorId: any;
  preSelectedVendor: any = null  // Store pre-selected vendor from vendor tab
  vendorQuoteList: any = []  // Store selected vendor quotes
  vendorQuoteAdditionalParam: any = {}  // Params for vendor quote search (filtered by vendor)
  jobAdditionalParams: any = {}
  scopeAdditionalParam: any = {}
  selectedJobList: any[] = []
  selectedValue: boolean = false
  discountList: any[] = [];
  showDiscountConfetti = false;
  isDiscountApplied: any;
  job_name: string = ''
  isEditMode: boolean = false
  isFromVendorQuote: boolean = false  // Track if PO is being created from vendor quote
  purchaseOrderData: any = null
  materialDataFromDialog: any = null
  poEditItemsData: any = null  // Store PO edit items data
  pendingPOItemsForVendorGroups: any[] = []  // Store PO items for vendor group creation
  editModeScopeIds: number[] = []  // Store scope IDs from PO edit items API to disable them
  deselectedPOItems: any[] = []  // Track PO items that were deselected during editing
  showMaterialPanel: boolean = true;
  materialSearchQuery: string = '';
  expandedJobs: Set<string> = new Set();
  showOnlySelected: boolean = false;
  showReadyOnly: boolean = false;
  isMaterialContainerCollapsed: boolean = true;
  leftPanelVendorFilter: string = '';

  getSelectedCount(): number {
    return this.materialList?.filter((m: any) => m.isSelected)?.length || 0;
  }

  getSelectedValue(): number {
    return this.materialList
      ?.filter((m: any) => m.isSelected)
      ?.reduce((sum: number, m: any) => sum + (m.previous_quantity_total || 0), 0) || 0;
  }

  /** Returns true when some but not all materials are selected (for indeterminate checkbox). */
  get someSelected(): boolean {
    if (!this.materialList?.length) return false;
    const count = this.materialList.filter((m: any) => m.isSelected).length;
    return count > 0 && count < this.materialList.length;
  }

  /** Returns materialList filtered by search query, vendor filter, and ready-only toggle. */
  get filteredMaterialList(): any[] {
    if (!this.materialList?.length) return [];
    let list = this.materialList;
    if (this.showReadyOnly) {
      list = list.filter((m: any) => m.is_ready_to_order || m.status === 'ready-to-order' || m.is_approved);
    }
    if (this.leftPanelVendorFilter) {
      list = list.filter((m: any) => String(m.vendor_id) === String(this.leftPanelVendorFilter));
    }
    if (!this.materialSearchQuery?.trim()) return list;
    const q = this.materialSearchQuery.toLowerCase().trim();
    return list.filter((m: any) =>
      (m.product_name || '').toLowerCase().includes(q) ||
      (m.product_code || m.material_code || '').toLowerCase().includes(q) ||
      (m.vendor_name || '').toLowerCase().includes(q)
    );
  }

  /** Groups filteredMaterialList by job_number. */
  get materialsByJob(): Record<string, any[]> {
    const grouped: Record<string, any[]> = {};
    for (const m of this.filteredMaterialList) {
      const key = m.job_number || 'Unassigned';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    }
    return grouped;
  }

  /** Toggle expand/collapse for a job group in the material panel. */
  toggleJobExpand(jobNo: string): void {
    if (this.expandedJobs.has(jobNo)) {
      this.expandedJobs.delete(jobNo);
    } else {
      this.expandedJobs.add(jobNo);
    }
  }

  /** Check if a job group is expanded. */
  isJobExpanded(jobNo: string): boolean {
    return this.expandedJobs.has(jobNo);
  }

  /**
   * trackBy for the job groups. `materialsByJob` is a getter that returns a new
   * object every change-detection cycle, so without tracking by a stable key the
   * *ngFor would destroy/recreate the whole job list on each CD — making the job
   * header button (and its expand click) unreliable. Track by the job number so
   * Angular reuses the existing DOM.
   */
  trackByJobKey(_index: number, entry: { key: string }): string {
    return entry.key;
  }

  /** Count of selected materials within a specific job group. */
  getSelectedCountInJob(jobNo: string): number {
    const jobMats = this.materialsByJob[jobNo];
    if (!jobMats) return 0;
    return jobMats.filter((m: any) => m.isSelected).length;
  }

  /** Count of selected materials for left panel footer. */
  getSelectedMaterialCount(): number {
    return this.materialList?.filter((m: any) => m.isSelected)?.length || 0;
  }

  /** Sum of selected material contract values for left panel footer. */
  getSelectedMaterialValue(): number {
    return this.materialList
      ?.filter((m: any) => m.isSelected)
      ?.reduce((sum: number, m: any) => sum + (m.previous_quantity_total || m.contract_price || 0), 0) || 0;
  }

  // ---- GAP 3: Unit cost change handler ----
  /** Recalculates line total when unit_price is edited. */
  unitCostChange(item: any): void {
    const price = Number(item.unit_price || 0);
    const qty = Number(item.quantity || 0);
    item.contract_price = price;
    item.previous_quantity_total = price * qty;
    item.new_quantity_total = price * Number(item.new_quantity || qty);
    this.getQuoteAmount();
    this.calculateMaterialTotal();
    this.calculateVendorCalculation();
    this.calculateJobListrCalculation();
  }

  // ---- GAP 5: Global cost breakdown ----

  /**
   * Every group that will become a purchase order on submit — the main vendor groups
   * plus the split-off groups. Must stay in step with `createPurchaseOrder()`, which
   * submits `[...separatePOJobList, ...vendorGroupList]`; a split group is a real PO,
   * so the breakdown has to count it.
   */
  private get allPoGroups(): any[] {
    return [...(this.vendorGroupList || []), ...(this.separatePOJobList || [])];
  }

  /** Aggregates material cost across all vendor groups, including split POs. */
  getGlobalMaterialCost(): number {
    return this.allPoGroups.reduce(
      (sum: number, v: any) => sum + (Number(v.calculation?.materialCost) || 0), 0);
  }

  /** Aggregates cost saving across all vendor groups, including split POs. */
  getGlobalCostSaving(): number {
    return this.allPoGroups.reduce(
      (sum: number, v: any) => sum + (Number(v.calculation?.costSaving) || 0), 0);
  }

  /** Aggregates shipping across all vendor groups, including split POs. */
  getGlobalShipping(): number {
    return this.allPoGroups.reduce(
      (sum: number, v: any) => sum + (Number(v.calculation?.shippingCharges) || 0), 0);
  }

  /** Aggregates cost escalation across all vendor groups, including split POs. */
  getGlobalEscalation(): number {
    return this.allPoGroups.reduce(
      (sum: number, v: any) => sum + (Number(v.calculation?.cost_escalation) || 0), 0);
  }

  /** Subtotal across all vendor groups (material + shipping + vendor tax), including split POs. */
  getGlobalSubtotal(): number {
    return this.allPoGroups.reduce(
      (sum: number, v: any) => sum + (Number(v.calculation?.subTotalCost) || 0), 0);
  }

  /** Total cost across all vendor groups, including split POs. */
  getGlobalTotal(): number {
    return this.allPoGroups.reduce(
      (sum: number, v: any) => sum + (Number(v.calculation?.totalCost) || 0), 0);
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<PurchaseOrderComponent>,
    public jobDataService: JobDataService,
    public purchaseOrderService: PurchaseOrderService,
    public backApiService: BackendApiService,
    public dialog: NgDialogAnimationService,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService) {
    this.jobId = data?.jobId
    this.purchaseOrderObject.type = data?.type
    this.job_name = data?.job_name
    this.isEditMode = data?.isEditMode || false
    this.isFromVendorQuote = data?.fromVendorQuote || false
    this.purchaseOrderData = data?.purchaseOrderData || null
    this.materialDataFromDialog = data?.materialDataFromDialog || data?.materialData || null

    // Handle vendor quote data if coming from vendor quote
    if (data?.fromVendorQuote && data?.vendorQuoteData) {
      console.log('📋 === CREATING PO FROM VENDOR QUOTE ===');
      console.log('🔍 Full modal data:', JSON.stringify(data, null, 2));
      console.log('🔍 vendorQuoteData:', data.vendorQuoteData);
      console.log('🔍 Products count:', data.vendorQuoteData.products?.length);

      this.materialDataFromDialog = data.vendorQuoteData.products || [];
      console.log('🔍 Set materialDataFromDialog with', this.materialDataFromDialog.length, 'products');

      // Pre-fill quote details from vendor quote
      if (data.vendorQuoteData.quotation_reference) {
        this.purchaseOrderObject.quote_number = data.vendorQuoteData.quotation_reference;
        console.log('✅ Set quote_number:', this.purchaseOrderObject.quote_number);
      }
      if (data.vendorQuoteData.amount) {
        this.purchaseOrderObject.quote_amount = data.vendorQuoteData.amount;
        console.log('✅ Set quote_amount:', this.purchaseOrderObject.quote_amount);
      }
    }

    // Handle pre-selected vendor from vendor tab
    if (data?.preSelectVendor) {
      console.log('\n👤 === VENDOR PRE-SELECTION ===');
      console.log('🔍 Raw preSelectVendor data:', JSON.stringify(data.preSelectVendor));

      // Store vendor info to be selected after materials load
      this.preSelectedVendor = data.preSelectVendor;

      // Extract vendor_id from either vendor_id or id field
      const vendorId = data.preSelectVendor.vendor_id || data.preSelectVendor.id;
      console.log('🔍 Extracted vendor_id:', vendorId);

      // Format vendor data to match the dropdown component's expected format
      // The dropdown expects: {vendor_id, vendor_name} based on formDataMapper
      // IMPORTANT: Also include 'id' field because scrollable-multiple-search-field
      // uses item.id for matching in its selection logic
      const formattedVendor = {
        id: vendorId,  // Critical: dropdown component uses 'id' field for matching
        vendor_id: vendorId,
        vendor_name: data.preSelectVendor.name,
        name: data.preSelectVendor.name  // Keep for compatibility
      };

      this.vendorId = [vendorId];
      this.vendorName = data.preSelectVendor.name;
      this.vendorDetail = [formattedVendor];

      console.log('✅ Set vendorId:', this.vendorId);
      console.log('✅ Set vendorName:', this.vendorName);
      console.log('✅ Set vendorDetail:', JSON.stringify(this.vendorDetail));

      // Set vendor quote search params to filter by this vendor
      this.vendorQuoteAdditionalParam.vendor_id = vendorId;
      console.log('✅ Set vendorQuoteAdditionalParam:', this.vendorQuoteAdditionalParam);

      // Pre-select initial vendor quote if provided
      if (data?.initialVendorQuote) {
        this.vendorQuoteList = [{
          id: data.initialVendorQuote.id,
          quotation_reference: data.initialVendorQuote.quotation_reference
        }];
        console.log('✅ Set vendorQuoteList:', JSON.stringify(this.vendorQuoteList));
      } else {
        console.log('⚠️ No initialVendorQuote in data');
      }

      console.log('✅ === VENDOR PRE-SELECTION COMPLETE ===\n');
    } else {
      console.log('⚠️ No preSelectVendor in data');
    }
    
    // Debug logging to trace the jobId issue
    if (this.isEditMode) {
      console.log('Edit Mode Constructor - jobId from data:', data?.jobId);
      console.log('Edit Mode Constructor - type:', data?.type);
      console.log('Edit Mode Constructor - purchaseOrderData:', this.purchaseOrderData);
      console.log('Edit Mode Constructor - jobDetail:', data?.jobDetail);
      
      // If jobId is provided and valid, use it
      if (this.jobId) {
        this.jobAdditionalParams.job_id = Array.isArray(this.jobId) ? this.jobId.join(",") : this.jobId;
        this.scopeAdditionalParam.jobids = Array.isArray(this.jobId) ? this.jobId.join(",") : this.jobId;
      }
      
      // Pre-populate job field data in the correct format for search components
      this.setupEditModeJobData(data);
      
      // Pre-populate scope and vendor data if available
      this.setupEditModeScopeData();
      this.setupEditModeVendorData();
      
      // Pre-populate quote fields from purchaseOrderData if available
      if (this.purchaseOrderData) {
        if (this.purchaseOrderData.quote_number !== undefined) {
          this.purchaseOrderObject.quote_number = this.purchaseOrderData.quote_number;
        }
        if (this.purchaseOrderData.shipping_cost !== undefined) {
          this.purchaseOrderObject.shipping_cost = this.purchaseOrderData.shipping_cost;
        }
        if (this.purchaseOrderData.quote_amount !== undefined) {
          this.purchaseOrderObject.quote_amount = this.purchaseOrderData.quote_amount;
        }
        
        // Price engineering flag always starts as false (OFF) in edit mode
        // Removed: restoration of price_engineering_flag from saved PO data
        // The toggle will always start as OFF regardless of saved state
        
        console.log('Pre-populated quote fields from purchaseOrderData:', {
          quote_number: this.purchaseOrderObject.quote_number,
          shipping_cost: this.purchaseOrderObject.shipping_cost,
          quote_amount: this.purchaseOrderObject.quote_amount,
          price_engineering_flag: false // Always false in edit mode
        });
      }
    } else {
      // For create mode, set the parameters normally
      this.jobAdditionalParams.job_id = Array.isArray(this.jobId) ? this.jobId?.join(",") : this.jobId;
      this.scopeAdditionalParam.jobids = Array.isArray(this.jobId) ? this.jobId?.join(",") : this.jobId;
    }
    
    this.checkPoType(data)
  }

  /**
   * Setup job data for edit mode with proper format for search components
   */
  setupEditModeJobData(data: any): void {
    if (!data?.jobDetail) {
      console.warn('No jobDetail provided in data');
      return;
    }


    if (this.purchaseOrderObject.type === 'regular') {
      // For regular PO: single job -> string format for app-scrollable-search-field
      if (Array.isArray(data.jobDetail) && data.jobDetail.length > 0) {
        const job = data.jobDetail[0];
        // The search field expects 'name' property for display
        this.jobDetail = job.name || job.job_number || '';
        this.selectedJobItem = job;
       
      } else if (data.jobDetail) {
        // The search field expects 'name' property for display
        this.jobDetail = data.jobDetail.name || data.jobDetail.job_number || '';
        this.selectedJobItem = data.jobDetail;
     
      }
    } else if (this.purchaseOrderObject.type === 'bulk') {
      // For bulk PO: multiple jobs -> array format for app-scrollable-multiple-search-field
      if (Array.isArray(data.jobDetail)) {
        this.jobDetail = data.jobDetail.map((job: any) => ({
          id: job.id,
          // The search field expects 'name' property
          name: job.name || job.job_number || ''
        }));
     
      }
    }
    
    // Initialize selectedJobList as empty - it will be populated when materials are selected
    // This follows the same pattern as create mode
    this.selectedJobList = [];
  }

  /**
   * Setup scope data for edit mode by fetching from jobscopes API (DEPRECATED - now handled by optimized loading)
   */
  setupEditModeScopeData(): void {
    // This method is deprecated and now handled by startOptimizedEditModeLoading()
    console.log('setupEditModeScopeData called - now handled by optimized loading');
  }

  /**
   * Populate scope field with data from jobscopes API
   */
  populateScopeFieldFromAPI(scopesData: any[]): void {
    if (!scopesData || scopesData.length === 0) {
      return;
    }

    
    // Check if we have job_scope_ids from the edit-purchase-orders-item API
    if (this.isEditMode && this.poEditItemsData?.job_scope_ids) {
      
      // Filter scopes based on job_scope_ids from edit API
      const relevantScopes = scopesData.filter((scope: any) => {
        if (!scope.job_scope_ids || !Array.isArray(scope.job_scope_ids)) {
          return false;
        }
        
        // Check if any of the scope's job_scope_ids are in the edit API's job_scope_ids
        const hasMatch = scope.job_scope_ids.some((id: number) => 
          this.poEditItemsData.job_scope_ids.includes(Number(id))
        );
        
        
        return hasMatch;
      });

      
      // Debug: Log the structure of relevant scopes

      if (relevantScopes.length > 0) {
        this.setEditModeScopeData(relevantScopes);
      } else {
        console.warn('No matching scopes found for job_scope_ids:', this.poEditItemsData.job_scope_ids);
        // Don't fall back to all scopes - this is likely a data issue
        this.setEditModeScopeData([]);
      }
    } else if (this.isEditMode) {
      // Edit mode but no poEditItemsData yet - store for later processing
      this.pendingScopeData = scopesData;
      // Don't set any scopes yet
    } else {
      // Create mode - use all scopes
      this.setEditModeScopeData(scopesData);
    }
  }

  /**
   * Set scope data for edit mode with proper formatting
   */
  setEditModeScopeData(scopesArray: any[]): void {
    // Set scope IDs for material fetching - flatten all job_scope_ids
    this.scopeId = scopesArray.reduce((acc: number[], scope: any) => {
      return acc.concat(scope.job_scope_ids || []);
    }, []);
    
    // Both Regular and Bulk PO now support multiple scopes
    // Set as array format for app-scrollable-multiple-search-field
    this.scopeName = scopesArray;
    
    // Trigger change detection to update the search field
    setTimeout(() => {
      this.scopeName = [...scopesArray];
      this.cdr.detectChanges();
    }, 100);
  }

  /**
   * Fallback method to setup scope data from PO items
   */
  setupEditModeScopeDataFromPOItems(): void {
    if (!this.purchaseOrderData?.items) return;


    
    // Extract unique scope IDs from purchase order items
    const uniqueScopeIds = new Set<number>();
    this.purchaseOrderData.items.forEach((item: any) => {
      if (item.scope_id) {
        uniqueScopeIds.add(item.scope_id);
      }
    });

    // Set scope IDs for material fetching
    this.scopeId = Array.from(uniqueScopeIds);
    
    // Both Regular and Bulk now use array format for multiple selection
    this.scopeName = this.scopeId.map((id: number) => ({
      scope_name: `Scope ${id}`,
      job_scope_ids: [id]
    }));


  }

  /**
   * Setup vendor data for edit mode 
   * Note: For bulk POs, vendor data is now loaded from the vendor discount API
   * This method is kept for compatibility and handles regular POs
   */
  setupEditModeVendorData(): void {
    // For bulk POs, vendor data will be populated from getVendorDiscountList() API
    // For regular POs in edit mode, we need to set the vendor name
    if (this.isEditMode && this.purchaseOrderObject.type === 'regular' && this.purchaseOrderData) {
      if (this.purchaseOrderData.vendor) {
        this.vendorName = this.purchaseOrderData.vendor.name || this.purchaseOrderData.vendor.vendor_name || '';
        this.vendorId = this.purchaseOrderData.vendor_id;
      } else if (this.purchaseOrderData.vendor_id) {
        // If we only have vendor_id, we'll need to fetch vendor details
        this.vendorId = this.purchaseOrderData.vendor_id;
        this.vendorName = ''; // Will be populated later from API
      }
    }
  }

  checkPoType(data: any) {
    // This method is now mainly for compatibility
    if (data?.type === 'regular') {
      // Job data is now handled in setupEditModeJobData
      if (!this.isEditMode) {
        this.jobDetail = data?.job_name;
      }
    } else {
      // Bulk data is now handled in setupEditModeJobData
      if (!this.isEditMode) {
        this.jobDetail = data?.jobDetail;
      }
    }
  }

  /**
   * Fetch job details for edit mode to ensure we have all job information
   */
  fetchJobDetailsForEdit(): void {
    if (!this.isEditMode || !this.jobId) return;
    
    
    // Prepare the search parameters
    const searchParams = {
      job_id: Array.isArray(this.jobId) ? this.jobId[0] : this.jobId // API might need single ID
    };
    
    // Fetch job details
    this.purchaseOrderService.search(searchParams).subscribe(
      (response: any) => {
        
        if (response?.data && response.data.length > 0) {
          // If we have multiple job IDs, we need to handle them all
          if (Array.isArray(this.jobId)) {
            // For bulk PO, set jobDetail with all jobs
            const jobs = response.data.filter((job: any) => this.jobId.includes(job.id));
            if (jobs.length > 0) {
              this.jobDetail = jobs;
            }
          } else {
            // For regular PO, set single job
            const job = response.data.find((j: any) => j.id === this.jobId);
            if (job) {
              this.job_name = job.name;
            }
          }
        }
        
        // After fetching job details, proceed to fetch scopes and vendors
        // Skip if already handled by startOptimizedEditModeLoading
        if (!this.isEditMode) {
          this.autoFetchScopesAndVendors();
        }
      },
      (error: any) => {
        console.error('Error fetching job details:', error);
        // Still try to fetch scopes and vendors even if job fetch fails
        // Skip if already handled by startOptimizedEditModeLoading
        if (!this.isEditMode) {
          this.autoFetchScopesAndVendors();
        }
      }
    );
  }

  ngOnInit(): void {

    // In edit mode, we should already have jobId(s) from the constructor
    if (this.isEditMode && this.purchaseOrderData) {
      // The jobId should already be set from the dialog data
      if (this.jobId) {
        // Job-based PO: Start the optimized loading process immediately
        this.startOptimizedEditModeLoading();
      } else {
        // Vendor-based PO (no jobId): Load PO items directly
        console.log('Edit Mode - Vendor-based PO detected (no jobId), loading PO items...');
        this.startVendorBasedEditModeLoading();
      }
    } else if (!this.isEditMode && this.data?.fromVendorQuote && this.materialDataFromDialog) {
      // CREATE MODE FROM VENDOR QUOTE: Use materials from vendor quote
      console.log('Creating PO from Vendor Quote - processing vendor quote materials');
      this.processVendorQuoteMaterials();
    } else if (!this.isEditMode && this.data?.fromVendorQuote && !this.materialDataFromDialog) {
      // CREATE MODE FROM SIDEBAR (VENDOR TYPE): No materials yet, wait for vendor quote selection
      console.log('Creating vendor-based PO from sidebar - waiting for vendor quote selection');
      console.log('Vendor:', this.vendorName);
      // Materials will be loaded when user selects vendor quote(s)
    } else if (!this.isEditMode && this.jobId) {
      // CREATE MODE: Auto-load all materials for the selected job
      this.autoLoadMaterialsForJob();
    }

    // Vendor discounts are now handled in the optimized loading process for edit mode
    // For create mode, still call the vendor discount API
    if (!this.isEditMode && this.jobId && (this.jobAdditionalParams.job_id || this.scopeAdditionalParam.jobids)) {
      this.getVendorDiscountList();
    }

    // Ensure pre-selected vendor is set after view initialization (CREATE MODE ONLY)
    if (this.preSelectedVendor && !this.isEditMode && !this.isFromVendorQuote) {
      // Only trigger vendor selection for job-based PO creation
      // For vendor quote-based PO, materials are already loaded
      setTimeout(() => {
        console.log('Re-applying pre-selected vendor after view init (Create Mode):', this.vendorDetail);

        // Re-confirm the vendor data is set
        if (this.vendorDetail && this.vendorDetail.length > 0) {
          // Force a new array reference to trigger Angular change detection in the dropdown
          this.vendorDetail = [...this.vendorDetail];

          // Trigger the vendor selection change handler to ensure materials are filtered
          // This mimics what happens when user manually selects a vendor
          this.selectedValueChange(this.vendorDetail, 'vendor');
          console.log('Triggered selectedValueChange for pre-selected vendor in create mode');
        }

        // Trigger change detection to ensure the dropdown picks up the value
        this.cdr.detectChanges();
      }, 1000);
    } else if (this.preSelectedVendor && !this.isEditMode && this.isFromVendorQuote) {
      // For vendor quote mode, vendor is displayed as disabled text input
      // No need for complex dropdown binding
      console.log('✅ Vendor quote mode - vendor will be shown as disabled field:', this.vendorName);
    }
  }

  /**
   * Auto-load materials for selected job in create mode
   * This fetches all scopes first, then fetches all materials for those scopes
   * IMPORTANT: This is ONLY for CREATE mode, not EDIT mode
   */
  autoLoadMaterialsForJob(): void {
    // Safety check: Only run in CREATE mode
    if (this.isEditMode) {
      return;
    }
    
    
    if (!this.jobId) {
      console.warn('No jobId available for auto-loading materials');
      return;
    }

    // Step 1: Fetch all scopes for the job
    const scopeParams = {
      per_page: 100, // Get more scopes
      search: '',
      page: 1,
      jobids: Array.isArray(this.jobId) ? this.jobId.join(',') : this.jobId
    };
    
    
    this.purchaseOrderService.scopeSearch(scopeParams).subscribe(
      (response: any) => {

        
        let allScopeIds: number[] = [];
        const allScopes: any[] = [];

        if (response?.data?.data && Array.isArray(response.data.data)) {
          // Extract all scope IDs from the response
          response.data.data.forEach((scope: any) => {
            if (scope.job_scope_ids && Array.isArray(scope.job_scope_ids)) {
              allScopeIds = allScopeIds.concat(scope.job_scope_ids);
              allScopes.push(scope);
            }
          });
        } else if (response?.data && Array.isArray(response.data)) {
          // Handle different response structure
          response.data.forEach((scope: any) => {
            if (scope.job_scope_ids && Array.isArray(scope.job_scope_ids)) {
              allScopeIds = allScopeIds.concat(scope.job_scope_ids);
              allScopes.push(scope);
            }
          });
        }


        if (allScopeIds.length > 0) {
          const tempScopeIds = allScopeIds.join(',');

          // Show the scopes we just auto-applied in the header field. The scope dropdown
          // matches `inputVal` on `scope_name` and requires the items to have no `id`
          // (scrollable-multiple-search-field.component.ts:179-190), which is exactly the
          // shape jobscopes returns, so the raw response objects can be bound as-is.
          // It renders as "First scope (+N others)".
          this.scopeId = tempScopeIds;
          this.scopeName = allScopes;

          // Step 2: Fetch materials for all scopes
          this.fetchMaterialsWithJobAndScopes(tempScopeIds);
        } else {
          console.warn('No scopes found for the selected job');
          // Still try to fetch materials with just job ID
          this.fetchMaterialsWithJobOnly();
        }
      },
      (error: any) => {
        console.error('Error fetching scopes:', error);
        // Try to fetch materials with just job ID as fallback
        this.fetchMaterialsWithJobOnly();
      }
    );
  }

  /**
   * Fetch materials with job and scope IDs (for initial load)
   */
  fetchMaterialsWithJobAndScopes(scopeIds: string): void {

    
    let base_url = 'purchase/orders/material?';
    const queryParams = [];
    
    queryParams.push(`jobids=${this.jobId}`);
    queryParams.push(`jobscope_ids=${scopeIds}`);
    
    base_url += queryParams.join('&');
    
    console.log('Material API URL for initial load:', base_url);
    
    this.backApiService.getApi(base_url).subscribe(
      (response: any) => {
        console.log('Initial materials loaded:', response);
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          this.processMaterialsForInitialLoad(response.data.data);
        }
      },
      (error: any) => {
        console.error('Error fetching materials:', error);
        this.toasterService.showErrorMessage('Failed to load materials');
      }
    );
  }

  /**
   * Fallback: Fetch materials with just job ID
   */
  fetchMaterialsWithJobOnly(): void {
    console.log('=== FETCHING MATERIALS WITH JOB ONLY (FALLBACK) ===');
    
    let base_url = `purchase/orders/material?jobids=${this.jobId}`;
    
    console.log('Material API URL (fallback):', base_url);
    
    this.backApiService.getApi(base_url).subscribe(
      (response: any) => {
        console.log('Materials loaded (fallback):', response);
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          this.processMaterialsForInitialLoad(response.data.data);
        }
      },
      (error: any) => {
        console.error('Error fetching materials (fallback):', error);
      }
    );
  }

  /**
   * Process materials for initial load (without vendor grouping)
   */
  processMaterialsForInitialLoad(materials: any[]): void {
    console.log(`=== PROCESSING ${materials.length} MATERIALS FOR INITIAL LOAD ===`);
    
    // Initialize material list
    this.materialList = [];
    
    materials.forEach((material: any, index: number) => {
      // Set default properties
      material.originalColor = getRandomLightColor();
      // In create mode, don't set initial colors - keep them transparent/white
      material.color = 'transparent';
      
      // Use helper function to ensure all prices and totals are properly set
      this.ensureMaterialPricesAndTotals(material);
      
      material.isSelected = false; // Don't auto-select
      
      // Map scope to scope_name for display
      if (material.scope && !material.scope_name) {
        material.scope_name = material.scope;
      }
      
      this.materialList.push(material);
    });
    
    console.log('Materials loaded into list:', this.materialList.length);
    console.log('Material list:', this.materialList);

    // Initialize UI state
    this.allMaterialSelected = false;

    // Show the vendors these materials belong to in the header field.
    this.preSelectVendorsFromMaterials();

    // Calculate totals after loading materials
    this.calculateMaterialTotal();
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Log the result but don't show toast message
    if (this.materialList.length > 0) {
      console.log(`✅ ${this.materialList.length} materials loaded successfully for the selected job`);
      // Removed success toast message as per requirement
    } else {
      console.log('ℹ️ No materials found for the selected job');
      // Removed info toast message as well
    }
  }

  /**
   * Mirror the distinct vendors of the loaded materials into the header Vendor(s) field.
   *
   * The create path auto-loads every material for the job, so the vendors are implied
   * rather than chosen — this makes that visible instead of leaving the field on its
   * placeholder. The dropdown matches `inputVal` on `vendor_id` and labels by
   * `vendor_name` (scrollable-multiple-search-field.component.ts:183-196), so that pair
   * is the required shape; it renders as "First vendor (+N others)".
   *
   * Skipped when a vendor was pre-selected by the caller (vendor tab / vendor quote),
   * so we never overwrite an explicit choice.
   */
  private preSelectVendorsFromMaterials(): void {
    if (this.preSelectedVendor || this.isFromVendorQuote || this.isEditMode) {
      return;
    }

    // Never overwrite an existing selection. This runs two requests deep (scopes, then
    // materials), so the user can have picked a vendor by hand in the meantime —
    // `selectedValueChange(…, 'vendor')` would already have set `vendorDetail`, and
    // clobbering it would silently undo their choice and re-widen the material list.
    if (this.vendorDetail?.length) {
      return;
    }

    const seen = new Set<number>();
    const vendors: any[] = [];

    (this.materialList || []).forEach((material: any) => {
      const vendorId = Number(material?.vendor_id);
      if (!vendorId || seen.has(vendorId)) {
        return;
      }
      seen.add(vendorId);
      vendors.push({ vendor_id: vendorId, vendor_name: material?.vendor_name || '' });
    });

    if (!vendors.length) {
      return;
    }

    this.vendorId = vendors.map(v => v.vendor_id).join(',');
    this.vendorDetail = vendors;
  }

  /**
   * Process materials from vendor quote
   * This method handles materials that come from vendor quote in a plain array format
   */
  processVendorQuoteMaterials(): void {
    console.log('=== PROCESSING VENDOR QUOTE MATERIALS ===');
    console.log('🔍 Material data from vendor quote:', JSON.stringify(this.materialDataFromDialog, null, 2));

    if (!this.materialDataFromDialog || !Array.isArray(this.materialDataFromDialog)) {
      console.error('❌ Invalid vendor quote materials data');
      return;
    }

    console.log(`📦 Processing ${this.materialDataFromDialog.length} materials...`);

    // Initialize material list
    this.materialList = [];

    this.materialDataFromDialog.forEach((material: any, index: number) => {
      console.log(`\n🔍 Material ${index + 1}:`, {
        original_name: material.name,
        product_name: material.product_name,
        code: material.code,
        all_keys: Object.keys(material)
      });

      // Set default properties
      material.originalColor = getRandomLightColor();
      material.color = 'transparent'; // Keep transparent in create mode

      // Ensure material name is set (critical for display)
      // The table displays item.product_name, so we need to set that field
      if (!material.name && material.product_name) {
        console.log(`   ✏️ Using product_name as name: ${material.product_name}`);
        material.name = material.product_name;
      }
      if (!material.name && material.code) {
        console.log(`   ✏️ Using code as name: ${material.code}`);
        material.name = material.code; // Fallback to code if name is missing
      }

      // CRITICAL: Set product_name for table display
      if (!material.product_name && material.name) {
        material.product_name = material.name;
      }
      if (!material.product_name && material.code) {
        material.product_name = material.code;
      }

      console.log(`   ✅ Final name: ${material.name}, product_name: ${material.product_name}`);

      // Ensure all required properties exist
      // Map vendor quote product fields to PO material fields
      if (!material.contract_price && material.unit_price) {
        material.contract_price = material.unit_price;
      }
      if (!material.new_quantity && material.quantity) {
        material.new_quantity = material.quantity;
      }
      if (!material.order_quantity && material.quantity) {
        material.order_quantity = material.quantity;
      }

      // Calculate totals
      const quantity = material.quantity || material.order_quantity || 0;
      const price = material.contract_price || material.unit_price || 0;
      material.previous_quantity_total = Number(price) * Number(quantity);
      material.new_quantity_total = Number(price) * Number(quantity);
      material.extended_price = Number(price) * Number(quantity);

      // Set selection state - DO NOT auto-select materials from vendor quote
      material.isSelected = false;

      // Add vendor information to material if available
      if (this.preSelectedVendor) {
        material.vendor_id = this.preSelectedVendor.vendor_id || this.preSelectedVendor.id;
        material.vendor_name = this.preSelectedVendor.name;
      }

      this.materialList.push(material);
    });

    console.log(`\n✅ Loaded ${this.materialList.length} materials from vendor quote`);
    console.log('📋 Material list summary:', this.materialList.map((m: any) => ({
      name: m.name,
      code: m.code,
      quantity: m.quantity,
      price: m.unit_price,
      isSelected: m.isSelected
    })));

    // Set all materials as NOT selected
    this.allMaterialSelected = false;

    // Calculate totals
    this.calculateMaterialTotal();

    // Force change detection
    this.cdr.detectChanges();

    console.log('✅ Vendor quote materials processed successfully');
  }

  /**
   * Load materials from selected vendor quotes
   * Fetches full vendor quote details and adds their materials to the material list
   */
  loadMaterialsFromVendorQuotes(selectedQuotes: any[]): void {
    console.log('🔵 loadMaterialsFromVendorQuotes called');
    console.log('🔵 selectedQuotes param:', selectedQuotes);
    console.log('🔵 selectedQuotes type:', typeof selectedQuotes);
    console.log('🔵 selectedQuotes is array?', Array.isArray(selectedQuotes));

    if (!selectedQuotes || selectedQuotes.length === 0) {
      console.log('⚠️ No vendor quotes selected - returning early');
      return;
    }

    console.log('✅ === LOADING MATERIALS FROM VENDOR QUOTES ===');
    console.log('✅ Number of selected quotes:', selectedQuotes.length);
    console.log('✅ Selected quotes full data:', JSON.stringify(selectedQuotes, null, 2));

    // For each selected vendor quote, fetch its details including products
    selectedQuotes.forEach((quote: any, index: number) => {
      console.log(`\n📦 Processing quote ${index + 1}:`, quote);
      const quoteId = quote.id;
      console.log(`📦 Extracted quote ID: ${quoteId}`);

      if (!quoteId) {
        console.error('❌ No quote ID found in quote object:', quote);
        return;
      }

      const apiUrl = `vendor_quote/${quoteId}`;
      console.log(`📡 Fetching vendor quote from: ${apiUrl}`);

      // Fetch vendor quote details
      this.backApiService.getApi(apiUrl).subscribe(
        (response: any) => {
          console.log('✅ Vendor quote API response received:', response);
          console.log('✅ Response data:', response?.data);
          console.log('✅ Products in response:', response?.data?.products);

          if (response?.data?.products && Array.isArray(response.data.products)) {
            console.log(`✅ Found ${response.data.products.length} products in vendor quote`);
            // Add products from this vendor quote to material list
            this.addVendorQuoteProductsToMaterialList(response.data.products, response.data);
          } else {
            console.warn('⚠️ No products found in vendor quote response');
          }
        },
        (error: any) => {
          console.error('❌ Error fetching vendor quote details:', error);
          console.error('❌ Error details:', JSON.stringify(error, null, 2));
          this.toasterService.showError(`Failed to load materials from quote ${quote.quotation_reference}`);
        }
      );
    });
  }

  /**
   * Add products from a vendor quote to the material list
   */
  private addVendorQuoteProductsToMaterialList(products: any[], quoteData: any): void {
    console.log('Adding vendor quote products to material list:', products);
    console.log('Quote data:', quoteData);

    // ✅ Initialize materialList if not already initialized
    if (!this.materialList) {
      console.log('⚠️ materialList was undefined, initializing as empty array');
      this.materialList = [];
    }
    console.log('📊 Current materialList length BEFORE adding:', this.materialList.length);

    // ✅ Extract job information from quote reference
    const jobInfo = quoteData.reference ? {
      job_id: quoteData.reference.id,
      job_name: quoteData.reference.name,
      job_number: quoteData.reference.code
    } : null;

    // ✅ Extract scope information from quote scopes
    const scopeInfo = quoteData.scopes && quoteData.scopes.length > 0 ? {
      scope_id: quoteData.scopes[0].id,
      scope_name: quoteData.scopes[0].name,
      scope_code: quoteData.scopes[0].scope_code
    } : null;

    console.log('Extracted job info:', jobInfo);
    console.log('Extracted scope info:', scopeInfo);

    products.forEach((product: any) => {
      // Ensure name is set
      const materialName = product.name || product.product_name || product.code || 'Unnamed Product';

      // Transform vendor quote product to PO material format
      const material: any = {
        product_id: product.product_id,
        id: product.id,
        name: materialName,  // Ensure name is always set
        product_name: materialName,  // For table display
        code: product.code,
        unit: product.unit,
        manufacturer_name: product.manufacturer_name,
        category_name: product.category_name,
        quantity: product.quantity,
        unit_price: product.unit_price,
        contract_price: product.unit_price,
        description: product.description,
        item_type: product.item_type || 1,
        order_quantity: product.quantity,
        new_quantity: product.quantity,

        // Calculate totals
        previous_quantity_total: Number(product.unit_price) * Number(product.quantity),
        new_quantity_total: Number(product.unit_price) * Number(product.quantity),
        extended_price: Number(product.unit_price) * Number(product.quantity),

        // Set properties
        originalColor: getRandomLightColor(),
        color: 'transparent',
        isSelected: false,  // DO NOT auto-select

        // Vendor information
        vendor_id: this.preSelectedVendor?.vendor_id || this.preSelectedVendor?.id,
        vendor_name: this.preSelectedVendor?.name,

        // ✅ FIX: Add job information from quote
        ...(jobInfo || {}),

        // ✅ FIX: Add scope information from quote
        ...(scopeInfo || {}),

        // Track which vendor quote this came from
        vendor_quote_id: quoteData.id,
        vendor_quote_reference: quoteData.quotation_reference
      };

      // Check if material already exists in the list
      const exists = this.materialList.some((m: any) =>
        m.product_id === material.product_id && m.vendor_quote_id === material.vendor_quote_id
      );

      if (!exists) {
        this.materialList.push(material);
        console.log(`✅ Added material: ${material.product_name || material.name || material.code} from quote ${quoteData.quotation_reference}`);
        console.log(`✅ Material details:`, {
          product_id: material.product_id,
          product_name: material.product_name,
          name: material.name,
          code: material.code,
          vendor_name: material.vendor_name,
          quantity: material.quantity,
          job_id: material.job_id,
          job_number: material.job_number,
          scope_id: material.scope_id,
          scope_name: material.scope_name
        });
      } else {
        console.log(`⚠️ Material ${material.name} already in list from this quote`);
      }
    });

    console.log('\n🔶 === AFTER ADDING ALL PRODUCTS ===');
    console.log(`🔶 Total materials in materialList: ${this.materialList.length}`);
    console.log(`🔶 Full materialList array:`, this.materialList);

    // Recalculate totals
    this.calculateMaterialTotal();

    // Update selection state - materials should NOT be selected
    this.allMaterialSelected = this.materialList.every((m: any) => m.isSelected);

    // Force change detection
    this.cdr.detectChanges();

    console.log(`🔶 After change detection - materialList.length: ${this.materialList.length}`);
  }

  /**
   * Optimized loading process for edit mode - parallel API calls
   */
  startOptimizedEditModeLoading(): void {
    console.log('=== STARTING OPTIMIZED EDIT MODE LOADING ===');
    console.log('This replaces autoFetchScopesAndVendors for edit mode');

    // Start all API calls in parallel
    const apiCalls: Promise<any>[] = [];

    // 1. Fetch scopes (this will set scopeId)
    console.log('Starting scope fetch...');
    const scopePromise = this.fetchScopesForEditMode();
    apiCalls.push(scopePromise);

    // 2. Fetch vendor discounts (this will set vendorDetail and vendorId)
    console.log('Starting vendor discount fetch...');
    const vendorPromise = this.fetchVendorDiscountsForEditMode();
    apiCalls.push(vendorPromise);

    // 3. Fetch PO edit items data
    if (this.purchaseOrderData?.id) {
      console.log('Starting PO edit items fetch...');
      const poEditPromise = this.fetchPurchaseOrderEditItems(this.purchaseOrderData.id);
      apiCalls.push(poEditPromise);
    }
    
    // Wait for scopes and vendors to load, then fetch materials
    Promise.all(apiCalls).then(() => {
      console.log('Scopes and vendors loaded, now fetching materials...');
      console.log('Final scopeId:', this.scopeId);
      console.log('Final vendorId:', this.vendorId);
      console.log('Final scopeName:', this.scopeName);
      console.log('Final vendorName:', this.vendorName);
      console.log('Final vendorDetail:', this.vendorDetail);
      
      // Trigger change detection to ensure UI updates
      this.cdr.detectChanges();
      
      if (this.scopeId && this.scopeId.length > 0) {
        this.getMaterial();
        // Pre-population is now handled automatically in getMaterial() for edit mode
      } else {
        console.error('No scopes available after API calls');
      }
    }).catch((error) => {
      console.error('Error in parallel API calls:', error);
    });
  }

  /**
   * Loading process for vendor-based PO edit mode (no job scope items)
   * This handles POs created from vendor quotes that don't have associated jobs
   */
  startVendorBasedEditModeLoading(): void {
    console.log('=== STARTING VENDOR-BASED EDIT MODE LOADING ===');
    console.log('This is for vendor quote POs without job scope items');

    if (!this.purchaseOrderData?.id) {
      console.error('No purchase order ID available');
      return;
    }

    // Fetch PO edit items and vendor information
    const url = `purchase/orders/edit-purchase-orders-item/${this.purchaseOrderData.id}`;
    console.log('Calling PO edit items API:', url);

    this.backApiService.postApi(url, {}).subscribe(
      (response: any) => {
        console.log('PO edit items API response:', response);

        if (response?.data) {
          // Process vendor information from the PO data
          this.processVendorBasedPOEditData(response.data);
        } else {
          console.error('No data in PO edit response');
        }
      },
      (error: any) => {
        console.error('Error fetching vendor-based PO edit items:', error);
      }
    );
  }

  /**
   * Process vendor-based PO edit data and populate materials
   */
  processVendorBasedPOEditData(data: any): void {
    console.log('=== PROCESSING VENDOR-BASED PO EDIT DATA ===');
    console.log('Full data:', data);

    if (!data?.po_items || !Array.isArray(data.po_items)) {
      console.log('No PO items to process');
      return;
    }

    console.log('Processing', data.po_items.length, 'PO items for vendor-based PO');

    // Set quote number, shipping cost, and quote amount from the API response
    if (data.quote_number !== undefined && data.quote_number !== null) {
      this.purchaseOrderObject.quote_number = data.quote_number;
      if (this.vendorForm && this.vendorForm.get('quote_number')) {
        this.vendorForm.get('quote_number')?.setValue(data.quote_number);
      }
    }

    // Pre-select vendor quote if quotation_reference is available
    if (data.quotation_reference !== undefined && data.quotation_reference !== null && data.quotation_reference !== '' && data.quotation_reference !== '0') {
      console.log('Pre-selecting vendor quote with quotation_reference:', data.quotation_reference);
      // Use quotation_reference as ID since we don't have the actual vendor quote ID from the API
      this.vendorQuoteList = [{
        id: data.quotation_reference, // Use quotation_reference as unique identifier
        quotation_reference: data.quotation_reference
      }];
      console.log('✅ Pre-selected vendor quote:', this.vendorQuoteList);
    }

    if (data.shipping_cost !== undefined && data.shipping_cost !== null) {
      this.purchaseOrderObject.shipping_cost = data.shipping_cost;
      if (this.vendorForm && this.vendorForm.get('shipping_cost')) {
        this.vendorForm.get('shipping_cost')?.setValue(data.shipping_cost);
      }
    }
    if (data.quote_amount !== undefined && data.quote_amount !== null) {
      this.purchaseOrderObject.quote_amount = data.quote_amount;
      if (this.vendorForm && this.vendorForm.get('amount')) {
        this.vendorForm.get('amount')?.setValue(data.quote_amount);
      }
    }

    console.log('Set quote_number:', this.purchaseOrderObject.quote_number);
    console.log('Set quotation_reference vendor quote:', this.vendorQuoteList);
    console.log('Set shipping_cost:', this.purchaseOrderObject.shipping_cost);
    console.log('Set quote_amount:', this.purchaseOrderObject.quote_amount);

    // Get vendor information from purchaseOrderData or from PO items
    let vendorInfo: any = null;

    // Try to get vendor from purchaseOrderData first
    if (this.purchaseOrderData?.vendor) {
      vendorInfo = this.purchaseOrderData.vendor;
      console.log('Got vendor from purchaseOrderData:', vendorInfo);
    } else if (data.vendor) {
      vendorInfo = data.vendor;
      console.log('Got vendor from edit data:', vendorInfo);
    } else if (data.po_items.length > 0 && data.po_items[0].vendor) {
      vendorInfo = data.po_items[0].vendor;
      console.log('Got vendor from first PO item:', vendorInfo);
    }

    // Set vendor information
    if (vendorInfo) {
      this.vendorId = [vendorInfo.id];
      this.vendorName = vendorInfo.name || vendorInfo.vendor_name;
      this.preSelectedVendor = vendorInfo;

      // Create vendor detail array for dropdown
      this.vendorDetail = [{
        id: vendorInfo.id,
        name: vendorInfo.name || vendorInfo.vendor_name,
        vendor_id: vendorInfo.id,
        vendor_name: vendorInfo.name || vendorInfo.vendor_name
      }];

      console.log('Set vendor:', this.vendorName, 'ID:', this.vendorId);
    }

    // Initialize material list
    this.materialList = [];

    // Process each PO item and add to material list
    data.po_items.forEach((item: any, index: number) => {
      console.log(`Processing PO item ${index + 1}:`, item);

      // Create material object from PO item
      const material: any = {
        // IDs
        id: item.id,
        material_id: item.material_id || item.product_id,
        product_id: item.material_id || item.product_id,
        job_scope_item_id: item.job_scope_item_id, // Will be null for vendor-based POs
        scope_id: item.scope_id,
        job_id: item.job_id,

        // Names and descriptions
        name: item.material_name || item.product_name || item.name,
        product_name: item.material_name || item.product_name || item.name,
        code: item.material_code || item.code,
        description: item.material_description || item.description,

        // Job and scope info (may be null for vendor-based POs)
        job_number: item.job_number,
        scope_name: item.scope,

        // Quantities
        order_quantity: item.order_quantity || 0,
        new_quantity: item.order_quantity || 0,
        quantity: item.order_quantity || 0,
        quantity_on_hand: item.quantity_on_hand || 0,

        // Prices
        unit_cost: item.unit_cost || 0,
        contract_price: item.po_contract_cost || item.unit_cost || 0,
        po_contract_cost: item.po_contract_cost || 0,
        unit_price: item.unit_cost || 0,

        // Calculated totals
        new_quantity_total: item.po_contract_cost || item.total_cost || ((item.unit_cost || 0) * (item.order_quantity || 0)),
        previous_quantity_total: item.po_contract_cost || item.total_cost || ((item.unit_cost || 0) * (item.order_quantity || 0)),
        extended_price: (item.unit_cost || 0) * (item.order_quantity || 0),
        total_cost: item.total_cost || ((item.unit_cost || 0) * (item.order_quantity || 0)),

        // Vendor info
        vendor_id: vendorInfo?.id,
        vendor_name: vendorInfo?.name || vendorInfo?.vendor_name,

        // UI state
        isSelected: true, // Pre-select materials in edit mode
        color: getRandomLightColor(),
        originalColor: getRandomLightColor(),

        // Original PO item for reference
        _originalPoItem: item
      };

      console.log('Created material:', {
        name: material.product_name,
        code: material.code,
        quantity: material.order_quantity,
        unit_cost: material.unit_cost,
        total: material.new_quantity_total
      });

      this.materialList.push(material);
    });

    console.log(`✅ Loaded ${this.materialList.length} materials from vendor-based PO`);

    // Create vendor group for display
    if (vendorInfo && this.materialList.length > 0) {
      this.createVendorGroupForEditMode(vendorInfo, this.materialList);
    }

    // Set all materials as selected
    this.allMaterialSelected = true;

    // Calculate totals
    this.calculateMaterialTotal();

    // Force change detection
    this.cdr.detectChanges();

    console.log('✅ Vendor-based PO edit mode loading completed');
  }

  /**
   * Create vendor group for vendor-based PO edit mode
   */
  createVendorGroupForEditMode(vendorInfo: any, materials: any[]): void {
    console.log('Creating vendor group for edit mode');

    // Check if vendor group already exists
    const existingGroup = this.vendorGroupList.find((g: any) => g.id === vendorInfo.id);
    if (existingGroup) {
      console.log('Vendor group already exists, updating materials');
      existingGroup.materialList = [...materials];
      existingGroup.isExistingVendor = true; // Ensure flag is set for edit mode
      // Update PO number from the existing PO being edited
      existingGroup.po_number = this.purchaseOrderData?.po_number;
      existingGroup.existingPoNumber = this.purchaseOrderData?.po_number;
      existingGroup.po_id = this.purchaseOrderData?.id;
      this.recalcVendor(existingGroup);
      return;
    }

    // Calculate total vendor tax from materials
    const totalVendorTax = materials.reduce((sum: number, mat: any) => {
      return sum + (Number(mat.estimated_vendor_tax) || 0);
    }, 0);

    // Create new vendor group
    const vendorGroup: any = {
      id: vendorInfo.id,
      vendor_id: vendorInfo.id,
      name: vendorInfo.name || vendorInfo.vendor_name,
      vendor_name: vendorInfo.name || vendorInfo.vendor_name,
      materialList: [...materials],
      fee_ship: this.purchaseOrderObject.shipping_cost || 0,
      vendor_tax: totalVendorTax, // Initialize with calculated vendor tax
      quote_number: this.purchaseOrderObject.quote_number || '',
      quotation_reference: this.vendorQuoteList && this.vendorQuoteList.length > 0 ? this.vendorQuoteList[0].quotation_reference : '',
      quote_amount: this.purchaseOrderObject.quote_amount || 0,
      isSelected: true,
      isExistingVendor: true, // CRITICAL: Mark as existing vendor for edit mode
      // Add PO number from the existing PO being edited
      po_number: this.purchaseOrderData?.po_number,
      existingPoNumber: this.purchaseOrderData?.po_number,
      po_id: this.purchaseOrderData?.id,
      color: getRandomLightColor()
    };

    // Calculate vendor group totals
    this.recalcVendor(vendorGroup);

    // Add to vendor groups array
    this.vendorGroupList.push(vendorGroup);

    console.log('Created vendor group:', vendorGroup.name, 'with', vendorGroup.materialList.length, 'materials');
  }

  /**
   * Fetch scopes for edit mode and return a promise
   */
  fetchScopesForEditMode(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.jobId) {
        console.warn('No jobId available for scope setup');
        resolve(null);
        return;
      }

      const apiParams = {
        per_page: 20,
        search: '',
        page: 1,
        jobids: Array.isArray(this.jobId) ? this.jobId.join(',') : this.jobId
      };
      
      this.purchaseOrderService.scopeSearch(apiParams).subscribe(
        (response: any) => {
          console.log('Scope API response received');
          
          if (response?.data?.data && Array.isArray(response.data.data)) {
            this.populateScopeFieldFromAPI(response.data.data);
          } else if (response?.data && Array.isArray(response.data)) {
            this.populateScopeFieldFromAPI(response.data);
          } else {
            this.setupEditModeScopeDataFromPOItems();
          }
          resolve(response);
        },
        (error: any) => {
          console.error('Error fetching scopes:', error);
          this.setupEditModeScopeDataFromPOItems();
          reject(error);
        }
      );
    });
  }

  /**
   * Fetch PO edit items using the edit-purchase-orders-item API
   */
  fetchPurchaseOrderEditItems(poId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `purchase/orders/edit-purchase-orders-item/${poId}`;
      console.log('Calling PO edit items API:', url);
      
      this.backApiService.postApi(url, {}).subscribe(
        (response: any) => {
          console.log('PO edit items API response:', response);
          
          if (response?.data) {
            // Process the PO items and add them to material list at the top
            this.processPOEditItems(response.data);
          }
          
          resolve(response);
        },
        (error: any) => {
          console.error('Error fetching PO edit items:', error);
          reject(error);
        }
      );
    });
  }
  
  /**
   * Process PO edit items and add them to the material list at the top
   */
  processPOEditItems(data: any): void {
    if (!data?.po_items || !Array.isArray(data.po_items)) {
      console.log('No PO items to process');
      return;
    }
    
    console.log('Processing PO edit items:', data.po_items.length);
    console.log('PO items data:', data.po_items);
    
    // Log the first item to see what fields are available
    if (data.po_items.length > 0) {
      console.log('First PO item fields:', Object.keys(data.po_items[0]));
      console.log('First PO item details:', data.po_items[0]);
    }
    
    // Store PO items data for later processing
    this.poEditItemsData = data;
    
    // Process pending scope data if it exists
    if (this.pendingScopeData && this.pendingScopeData.length > 0) {
      console.log('Processing pending scope data now that poEditItemsData is available');
      this.populateScopeFieldFromAPI(this.pendingScopeData);
      this.pendingScopeData = null; // Clear pending data
    }
    
    // Set quote number, shipping cost, and quote amount from the API response
    if (data.quote_number !== undefined && data.quote_number !== null) {
      this.purchaseOrderObject.quote_number = data.quote_number;
      // Also set it in the form control if it exists
      if (this.vendorForm && this.vendorForm.get('quote_number')) {
        this.vendorForm.get('quote_number')?.setValue(data.quote_number);
      }
    }

    // Pre-select vendor quote if quotation_reference is available
    if (data.quotation_reference !== undefined && data.quotation_reference !== null && data.quotation_reference !== '' && data.quotation_reference !== '0') {
      console.log('Pre-selecting vendor quote with quotation_reference:', data.quotation_reference);
      // Use quotation_reference as ID since we don't have the actual vendor quote ID from the API
      this.vendorQuoteList = [{
        id: data.quotation_reference, // Use quotation_reference as unique identifier
        quotation_reference: data.quotation_reference
      }];
      console.log('✅ Pre-selected vendor quote:', this.vendorQuoteList);
    }

    if (data.shipping_cost !== undefined && data.shipping_cost !== null) {
      this.purchaseOrderObject.shipping_cost = data.shipping_cost;
      // Also set it in the form control if it exists
      if (this.vendorForm && this.vendorForm.get('shipping_cost')) {
        this.vendorForm.get('shipping_cost')?.setValue(data.shipping_cost);
      }
    }
    if (data.quote_amount !== undefined && data.quote_amount !== null) {
      this.purchaseOrderObject.quote_amount = data.quote_amount;
      // Also set it in the form control if it exists
      if (this.vendorForm && this.vendorForm.get('amount')) {
        this.vendorForm.get('amount')?.setValue(data.quote_amount);
      }
    }

    console.log('Set quote_number:', this.purchaseOrderObject.quote_number);
    console.log('Set quotation_reference vendor quote:', this.vendorQuoteList);
    console.log('Set shipping_cost:', this.purchaseOrderObject.shipping_cost);
    console.log('Set quote_amount:', this.purchaseOrderObject.quote_amount);
    
    // Fetch job details if we have job_ids
    if (data.job_ids && data.job_ids.length > 0 && (!this.purchaseOrderData || !this.purchaseOrderData.jobDetail)) {
      console.log('Fetching job details for job IDs:', data.job_ids);
      this.fetchJobDetailsForEditMode(data.job_ids);
    }
    
    // Extract scope IDs from PO items to disable them in dropdown
    this.editModeScopeIds = [];
    if (data.job_scope_ids && Array.isArray(data.job_scope_ids)) {
      this.editModeScopeIds = data.job_scope_ids;
    } else {
      // Extract unique scope IDs from po_items if not provided directly
      const scopeIdSet = new Set<number>();
      data.po_items.forEach((item: any) => {
        if (item.scope_id) {
          scopeIdSet.add(Number(item.scope_id));
        }
      });
      this.editModeScopeIds = Array.from(scopeIdSet);
    }
    
    console.log('Scope IDs to disable in dropdown:', this.editModeScopeIds);
    
    // We'll add these materials after the material list is initialized
    // For now, just store them
    console.log('PO edit items stored for later processing');
  }
  
  /**
   * Fetch job details for edit mode when not passed from detail component
   */
  fetchJobDetailsForEditMode(jobIds: number[]): void {
    const jobIdString = jobIds.join(',');
    const searchParams = {
      job_id: jobIdString
    };
    
    console.log('Fetching job details with params:', searchParams);
    
    this.purchaseOrderService.search(searchParams).subscribe(
      (response: any) => {
        console.log('Job details response:', response);
        if (response?.data) {
          // Store job details in purchaseOrderData if not present
          if (!this.purchaseOrderData) {
            this.purchaseOrderData = {};
          }
          
          if (!this.purchaseOrderData.jobDetail) {
            this.purchaseOrderData.jobDetail = response.data;
          }
          
          console.log('Updated purchaseOrderData with job details:', this.purchaseOrderData);
        }
      },
      (error: any) => {
        console.error('Error fetching job details:', error);
      }
    );
  }

  /**
   * Add PO edit items to the material list at the top
   */
  addPOEditItemsToMaterialList(): void {
    if (!this.poEditItemsData?.po_items) {
      console.log('No stored PO edit items to add');
      return;
    }
    
    console.log('Adding PO edit items to material list');
    console.log('Purchase order data with job details:', this.purchaseOrderData);
    
    // Build a map of job_id to job_number from purchaseOrderData
    const jobNumberMap = new Map<number, string>();
    
    // Check if we have job details in purchaseOrderData
    if (this.purchaseOrderData) {
      // For regular PO, jobDetail might be a single object or array
      const jobDetails = Array.isArray(this.purchaseOrderData.jobDetail) 
        ? this.purchaseOrderData.jobDetail 
        : (this.purchaseOrderData.jobDetail ? [this.purchaseOrderData.jobDetail] : []);
      
      jobDetails.forEach((job: any) => {
        if (job && job.id) {
          // Get job number - in the screenshot it shows "10600-Partitions"
          const jobNum = job.job_number || job.number || job.code || '';
          const jobName = job.name || job.job_name || '';
          
          // Format as shown in screenshot: combine job number with scope name
          // The scope name is "Partitions" in the screenshot
          // First try to use job_number alone
          let formattedJobNumber = jobNum;
          
          // If we have job_id 363, let's map it based on the scope
          // Since all items have scope "Partitions", we can format it as "JobNumber-Partitions"
          jobNumberMap.set(job.id, formattedJobNumber || `Job-${job.id}`);
          console.log(`Mapped job_id ${job.id} to job_number: ${formattedJobNumber}`);
        }
      });
      
      // Also check if there's jobs array
      if (this.purchaseOrderData.jobs && Array.isArray(this.purchaseOrderData.jobs)) {
        this.purchaseOrderData.jobs.forEach((job: any) => {
          if (job && job.id && !jobNumberMap.has(job.id)) {
            const jobNum = job.job_number || job.number || job.code || '';
            const jobName = job.name || job.job_name || '';
            // Combine job number and name (e.g., "10600-Partitions")
            const formattedJobNumber = jobNum || jobName || '';
            jobNumberMap.set(job.id, formattedJobNumber);
            console.log(`Mapped job_id ${job.id} to job_number: ${formattedJobNumber}`);
          }
        });
      }
    }
    
    // Create materials from PO items with pre-selection
    const poMaterials = this.poEditItemsData.po_items.map((item: any) => {
      const itemColor = getRandomLightColor(); // Generate color once for consistency
      
      // CRITICAL FIX: Get job_id from multiple sources
      let actualJobId = item.job_id;
      
      // If job_id is null, try to get it from poEditItemsData or purchaseOrderData
      if (!actualJobId && this.poEditItemsData.job_ids && this.poEditItemsData.job_ids.length > 0) {
        // Use the first job_id from the job_ids array if available
        actualJobId = this.poEditItemsData.job_ids[0];
        console.log(`Using job_id from poEditItemsData.job_ids: ${actualJobId}`);
      }
      
      // If still no job_id, try from purchaseOrderData
      if (!actualJobId && this.purchaseOrderData) {
        if (Array.isArray(this.purchaseOrderData.jobDetail) && this.purchaseOrderData.jobDetail.length > 0) {
          actualJobId = this.purchaseOrderData.jobDetail[0].id;
          console.log(`Using job_id from purchaseOrderData.jobDetail: ${actualJobId}`);
        } else if (this.purchaseOrderData.jobDetail && this.purchaseOrderData.jobDetail.id) {
          actualJobId = this.purchaseOrderData.jobDetail.id;
          console.log(`Using job_id from purchaseOrderData.jobDetail (single): ${actualJobId}`);
        }
      }
      
      // If still no job_id, try from jobId property
      if (!actualJobId && this.jobId) {
        // jobId might be a comma-separated string for bulk POs
        const jobIds = this.jobId.toString().split(',').map((id: string) => id.trim());
        if (jobIds.length > 0) {
          actualJobId = parseInt(jobIds[0]);
          console.log(`Using job_id from this.jobId: ${actualJobId}`);
        }
      }
      
      // Get job number from our map
      let jobNumber = jobNumberMap.get(actualJobId) || '';
      
      // If job number is still empty but we have scope, combine them
      // Based on screenshot, it should show "10600-Partitions"
      if (!jobNumber && item.scope) {
        // Try to get job ID and combine with scope
        jobNumber = `${actualJobId || ''}-${item.scope}`;
      } else if (jobNumber && item.scope) {
        // If we have job number but it doesn't include scope, add it
        if (!jobNumber.includes('-')) {
          jobNumber = `${jobNumber}-${item.scope}`;
        }
      }
      
      console.log(`Processing material: ${item.material_name}, original job_id: ${item.job_id}, actual job_id: ${actualJobId}, scope_id: ${item.scope_id}, scope: ${item.scope}, final job_number: ${jobNumber}`);
      
      // Get the best available price from the PO item
      // Try multiple fields to find the unit price
      const unitCost = item.unit_cost || item.po_contract_cost || item.contract_cost || 
                       item.unit_price || item.rate || item.cost ||
                       (item.total_cost && item.order_quantity ? item.total_cost / item.order_quantity : 0) || 0;
      
      console.log(`PO Item price calculation for ${item.material_name}:`, {
        unit_cost: item.unit_cost,
        po_contract_cost: item.po_contract_cost,
        contract_cost: item.contract_cost,
        total_cost: item.total_cost,
        order_quantity: item.order_quantity,
        calculated_unit_cost: unitCost
      });
      
      const newMaterial = {
        id: item.job_scope_item_id,  // MUST use job_scope_item_id only, never fallback to PO item ID
        po_item_id: item.id,  // Store the actual PO item ID for update
        material_id: item.material_id || null,  // Use the actual material_id from the response
        product_id: item.material_id || null,  // Same as material_id
        job_scope_item_id: item.job_scope_item_id,  // MUST be job_scope_item_id only, no fallback
        product_name: item.material_name || item.material_description || 'Unknown Material',
        material_code: item.material_code,
        material_description: item.material_description,
        scope: item.scope,
        scope_name: item.scope || '', // Add scope_name for display in the table
        scope_id: item.scope_id,
        job_id: actualJobId || null,  // Use the corrected job_id
        job_number: jobNumber, // Use formatted job number
        quantity_on_hand: item.quantity_on_hand || 0,
        unit_price: unitCost,  // Use the calculated unit cost
        // CONTRACT PRICE should be the TOTAL, not unit price
        contract_price: item.po_contract_cost || item.total_cost || (unitCost * (item.order_quantity || 0)),
        quantity: item.order_quantity || 0,
        new_quantity: item.order_quantity || 0,
        // Store original order quantity for tracking changes
        original_order_quantity: item.order_quantity || 0,
        // Set the totals for proper calculation
        previous_quantity_total: item.po_contract_cost || item.total_cost || (unitCost * (item.order_quantity || 0)),
        new_quantity_total: item.po_contract_cost || item.total_cost || (unitCost * (item.order_quantity || 0)),
        // Add pending_purchase_quantity from API for available quantity display
        pending_purchase_quantity: item.pending_purchase_quantity || 0,
        total_needed_quantity: item.total_needed_quantity || 0,
        // Calculate available quantity (what can still be ordered)
        available_quantity: item.pending_purchase_quantity || 0,
        isSelected: true, // Pre-select items from edit-purchase-orders-item API
        color: itemColor, // Set color for pre-selected items
        originalColor: itemColor, // Use same color for consistency
        isPOItem: true, // Mark as PO item for identification
        vendor_id: this.poEditItemsData.vendor_id,
        vendor_name: this.purchaseOrderData?.vendor?.name || '', // Add vendor name if available
        // Stage 6.7: Include tax fields - estimated_use_tax from PO item (already saved)
        actual_vendor_tax_paid: item.actual_vendor_tax_paid || 0,
        estimated_use_tax: item.estimated_use_tax || 0, // Already stored in PO item during creation
        vendor_state_code: item.vendor_state_code || null,
        vendor_jurisdiction: item.vendor_jurisdiction || null
      };

      // Use helper function to ensure all prices and totals are properly set
      this.ensureMaterialPricesAndTotals(newMaterial);
      
      return newMaterial;
    });
    
    // Initialize materialList if it's not already
    if (!this.materialList) {
      this.materialList = [];
    }
    
    // Add PO materials at the beginning of the list
    this.materialList = [...poMaterials, ...this.materialList];
    
    console.log(`Added ${poMaterials.length} PO items to the top of material list (pre-selected)`);
    console.log('Total materials in list:', this.materialList.length);
    console.log('Material list after adding PO items:', this.materialList);
    
    // Store PO materials for later vendor group creation
    // Don't create vendor groups here as it interferes with getMaterial flow
    this.pendingPOItemsForVendorGroups = poMaterials;
  }

  /**
   * Create vendor groups for pre-selected PO items
   */
  createVendorGroupsForPOItems(poMaterials: any[]): void {
    if (!poMaterials || poMaterials.length === 0) {
      console.log('No PO materials to create vendor groups for');
      return;
    }
    
    console.log('Creating vendor groups for PO items...');
    
    // Group materials by vendor
    const materialsByVendor = new Map<number, any[]>();
    
    poMaterials.forEach((material: any) => {
      // Get vendor info - could be from material or from PO data
      const vendorId = material.vendor_id || this.poEditItemsData?.vendor_id || this.purchaseOrderData?.vendor?.id;
      const vendorName = material.vendor_name || this.purchaseOrderData?.vendor?.name || 'Unknown Vendor';
      
      // Set vendor info on material
      material.vendor_id = vendorId;
      material.vendor_name = vendorName;
      
      // Group by vendor
      if (!materialsByVendor.has(vendorId)) {
        materialsByVendor.set(vendorId, []);
      }
      materialsByVendor.get(vendorId)?.push(material);
    });
    
    // Create vendor groups for each vendor
    materialsByVendor.forEach((materials, vendorId) => {
      const vendorName = materials[0]?.vendor_name || 'Unknown Vendor';
      
      // Check if vendor group already exists
      let vendorGroup = this.vendorGroupList.find((group: any) => 
        group.vendor_id === vendorId
      );
      
      if (!vendorGroup) {
        // Create new vendor group
        const vendorColor = getRandomLightColor();
        
        // ONLY in edit mode, check if this vendor is the existing vendor
        let isExistingVendor = false;
        let existingPoNumber = null;
        let existingPoId = null;
        
        if (this.isEditMode && this.purchaseOrderData) {
          isExistingVendor = (vendorId === this.purchaseOrderData.vendor_id || 
                             vendorId === this.purchaseOrderData.vendor?.id);
          if (isExistingVendor) {
            existingPoNumber = this.purchaseOrderData.po_number;
            existingPoId = this.purchaseOrderData.id;
          }
        }
        
        // Calculate total vendor tax for this vendor's materials
        const totalVendorTax = materials.reduce((sum: number, mat: any) => {
          return sum + (Number(mat.estimated_vendor_tax) || 0);
        }, 0);

        vendorGroup = {
          id: vendorId,
          vendor_id: vendorId,
          name: vendorName,
          vendor_name: vendorName,
          color: vendorColor,
          materialList: [],
          // Use values from PO edit data if available, otherwise defaults
          quote_amount: this.poEditItemsData?.quote_amount || 0,
          fee_ship: this.poEditItemsData?.shipping_cost || 0,
          vendor_tax: totalVendorTax, // Initialize with calculated vendor tax
          is_free_shipping: this.poEditItemsData?.is_free_shipping ?? (this.poEditItemsData?.shipping_cost === 0 || false),
          is_discount: this.poEditItemsData?.is_discount ?? false,
          quote_number: this.poEditItemsData?.quote_number || '',
          quotation_reference: this.poEditItemsData?.quotation_reference || (this.vendorQuoteList && this.vendorQuoteList.length > 0 ? this.vendorQuoteList[0].quotation_reference : ''),
          // ONLY add these properties in edit mode
          ...(this.isEditMode ? {
            isExistingVendor: isExistingVendor,
            existingPoNumber: existingPoNumber,
            existingPoId: existingPoId
          } : {}),
          // Show PO number only for existing vendor in edit mode
          po_number: this.isEditMode && isExistingVendor ? existingPoNumber : null,
          calculation: {
            materialCost: 0, // Will be calculated by recalcVendor
            shippingCharges: this.isEditMode ? (this.poEditItemsData?.shipping_cost || 0) : 0,
            cost_escalation: 0,
            subTotalCost: this.isEditMode ? (this.poEditItemsData?.shipping_cost || 0) : 0, // Initial subtotal = shipping
            totalCost: this.isEditMode ? (this.poEditItemsData?.shipping_cost || 0) : 0, // Initial total = shipping
            costSaving: 0,
            vendorTax: totalVendorTax // Initialize calculation vendor tax
          }
        };

        this.vendorGroupList.push(vendorGroup);
        console.log('✅ Created vendor group with required fields:', {
          name: vendorGroup.name,
          is_free_shipping: vendorGroup.is_free_shipping,
          is_discount: vendorGroup.is_discount
        });
      }
      
      // Add materials to the vendor group
      materials.forEach((material: any) => {
        // Ensure material has the correct color from vendor group
        material.color = vendorGroup.color;
        
        // Use helper function to ensure all prices and totals are properly set
        this.ensureMaterialPricesAndTotals(material);
        
        // Check if material already exists in the vendor group
        const existsInGroup = vendorGroup.materialList.some((m: any) => m.id === material.id);
        
        if (!existsInGroup) {
          // Add material to vendor group
          vendorGroup.materialList.push(material);
          console.log(`Added material ${material.product_name} to vendor group ${vendorName} with unit_price: ${material.unit_price}, total: ${material.new_quantity_total}`);
        }
      });
      
      // Recalculate this vendor group's totals after adding all materials
      this.recalcVendor(vendorGroup);
      
      // Distribute shipping costs across materials if there's a shipping fee
      if (vendorGroup.fee_ship > 0) {
        this.distributeShippingCost(vendorGroup);
      }
    });
    
    // Also update job list if needed for bulk PO - MUST be done BEFORE calculations
    if (this.purchaseOrderObject.type === 'bulk') {
      poMaterials.forEach((material: any) => {
        // Ensure the material has proper totals before adding to job list
        this.ensureMaterialPricesAndTotals(material);
        this.appendJobList(material, this.vendorGroupList.find((v: any) => v.vendor_id === material.vendor_id));
      });
    }
    
    // Calculate quote amounts first, then update calculations for all vendor groups
    this.getQuoteAmount(); // Calculate quote amounts for vendor groups
    this.calculateMaterialTotal();
    this.calculateVendorCalculation();
    this.calculateJobListrCalculation(); // This needs selectedJobList to be populated first
    
    console.log('Vendor groups created successfully');
    console.log('Total vendor groups:', this.vendorGroupList.length);
    this.vendorGroupList.forEach((group: any) => {
      console.log(`Vendor: ${group.name}, Materials: ${group.materialList.length}, Calculation:`, group.calculation);
    });
    
    // Force change detection to update the UI
    this.cdr.detectChanges();
  }

  /**
   * Fetch vendor discounts for edit mode and return a promise  
   */
  fetchVendorDiscountsForEditMode(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.jobId) {
        console.warn('No jobId available for vendor discount fetch');
        resolve(null);
        return;
      }

      let base_url = `purchase/orders/vendors-discount?jobids=${this.jobId}`;
      this.backApiService.getApi(base_url).subscribe(
        (response: any) => {
          console.log('Vendor discount API response received');
          
          this.discountList = response.data?.data || [];
          if (this.discountList.length > 0) {
            this.showDiscountConfetti = true;
            setTimeout(() => this.showDiscountConfetti = false, 800);
          }
          this.discountList.forEach((x: any) => {
            x.isSelected = false;
          });

          // Populate vendor field for both bulk and regular POs in edit mode
          if (this.isEditMode && this.discountList.length > 0) {
            this.populateVendorFieldFromDiscountData();
          }
          
          resolve(response);
        },
        (error: any) => {
          console.error('Error fetching vendor discounts:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Pre-populate materials from existing PO data
   */
  prePopulateMaterialsFromPOData(): void {
    if (!this.isEditMode || !this.purchaseOrderData?.items || !this.materialList) {
      console.log('Cannot pre-populate materials:', {
        isEditMode: this.isEditMode,
        hasItems: !!this.purchaseOrderData?.items,
        hasMaterialList: !!this.materialList,
        materialListLength: this.materialList?.length || 0
      });
      return;
    }

    console.log('=== PRE-POPULATING MATERIALS ===');
    console.log('PO items count:', this.purchaseOrderData.items.length);
    console.log('Available materials count:', this.materialList.length);
    console.log('PO items:', this.purchaseOrderData.items);

    const poItems = this.purchaseOrderData.items;
    let selectedCount = 0;

    // Create a comprehensive map of PO materials for faster lookup
    const poMaterialMap = new Map();
    poItems.forEach((item: any, index: number) => {
      // Try multiple possible material ID fields
      const materialId = item.material_id || item.product_id || item.id;
      
      console.log(`PO Item ${index}:`, {
        material_id: item.material_id,
        product_id: item.product_id,
        id: item.id,
        product_name: item.product_name,
        order_quantity: item.order_quantity,
        unit_cost: item.unit_cost,
        vendor_id: item.vendor_id
      });
      
      if (materialId) {
        poMaterialMap.set(Number(materialId), {
          quantity: item.order_quantity || item.quantity || 1,
          unitCost: item.unit_cost,
          totalCost: item.total_cost,
          poContractCost: item.po_contract_cost,
          vendorId: item.vendor_id || this.purchaseOrderData.vendor_id,
          jobId: item.job_id,
          scopeId: item.scope_id,
          productName: item.product_name || item.material_name
        });
      }
    });

    console.log('PO Material Map:', poMaterialMap);

    // Pre-select materials that exist in PO
    this.materialList.forEach((material: any, index: number) => {
      // Try multiple possible material ID fields for matching
      const materialId = Number(material.material_id || material.product_id || material.id);
      
      console.log(`Checking material ${index}: ${material.product_name} (ID: ${materialId})`);
      
      if (poMaterialMap.has(materialId)) {
        const poData = poMaterialMap.get(materialId);
        
        console.log(`✓ MATCHING MATERIAL FOUND: ${material.product_name} (ID: ${materialId})`);
        
        // Mark as selected
        material.isSelected = true;
        selectedCount++;
        
        // Update quantities and prices from PO data
        material.new_quantity = poData.quantity;
        material.quantity = poData.quantity;
        
        if (poData.unitCost) {
          material.unit_price = poData.unitCost;
        }
        
        const editTotal = poData.poContractCost || poData.totalCost;
        if (editTotal) {
          material.new_quantity_total = editTotal;
          material.previous_quantity_total = editTotal;
        }

        console.log(`✓ Pre-selected material: ${material.product_name} (ID: ${materialId}) - Quantity: ${poData.quantity}`);
        
        // Trigger material selection to create vendor groups
        this.selectedMaterial(true, material, index);
      } else {
        console.log(`✗ Material NOT in PO: ${material.product_name} (ID: ${materialId})`);
      }
    });

    console.log(`=== PRE-SELECTION COMPLETE ===`);
    console.log(`Pre-selected ${selectedCount} out of ${this.materialList.length} materials`);

    // Update the select all checkbox state
    this.allMaterialSelected = selectedCount === this.materialList.length && selectedCount > 0;
    
    console.log('All materials selected?', this.allMaterialSelected);

    // Recalculate totals
    this.calculateMaterialTotal();
    this.calculateVendorCalculation();
    
    // Force UI update
    setTimeout(() => {
      console.log('Final vendor group list:', this.vendorGroupList);
      console.log('Final material selection state:', this.materialList?.map((m: any) => ({
        name: m.product_name,
        selected: m.isSelected
      })) || []);
    }, 500);
  }
  
  /**
   * Auto-fetch and pre-select scopes and vendors in edit mode
   */
  autoFetchScopesAndVendors(): void {
    if (!this.isEditMode || !this.purchaseOrderData) {
      console.log('Not in edit mode or no PO data available');
      return;
    }

    console.log('=== Starting Auto-fetch for Edit PO ===');
    console.log('Material data from dialog:', this.materialDataFromDialog);
    
    // Extract job IDs from PO data
    const jobIds = this.extractJobIds();
    
    if (jobIds.length === 0) {
      console.warn('No job IDs found in PO data');
      return;
    }
    
    // Set jobId for getMaterial to use
    this.jobId = jobIds.join(',');
    
    // Update the additional params with job IDs
    this.jobAdditionalParams = { job_id: jobIds.join(',') };
    this.scopeAdditionalParam = { jobids: jobIds.join(',') };
    
    console.log('Set jobId for materials:', this.jobId);
    console.log('Set scope params:', this.scopeAdditionalParam);
    
    // Check if we have pre-fetched material data
    if (this.materialDataFromDialog) {
      console.log('Using pre-fetched material data from dialog');
      this.processMaterialDataFromDialog();
    } else {
      // Fetch scopes using job IDs (old approach)
      this.fetchScopes(jobIds);
    }
  }

  /**
   * Extract job IDs from PO items
   */
  private extractJobIds(): number[] {
    const poItems = this.purchaseOrderData.items || [];
    const jobIds = new Set<number>();
    
    poItems.forEach((item: any) => {
      if (item.job_id) {
        jobIds.add(item.job_id);
      }
      if (item.job?.id) {
        jobIds.add(item.job.id);
      }
    });
    
    const jobIdArray = Array.from(jobIds);
    console.log('Extracted job IDs:', jobIdArray);
    return jobIdArray;
  }

  /**
   * Fetch scopes using job IDs
   */
  private fetchScopes(jobIds: number[]): void {
    const params = { jobids: jobIds.join(',') };
    console.log('Fetching scopes with params:', params);
    
    this.purchaseOrderService.scopeSearch(params).subscribe(
      (response: any) => {
        console.log('Scope search response:', response);
        
        if (response?.data && response.data.length > 0) {
          // Pre-select scopes and get scope IDs
          const scopeIds = this.preSelectScopesAndGetIds(response.data);
          
          // Now fetch materials with both job IDs and scope IDs
          setTimeout(() => {
            this.fetchMaterialsWithIds(jobIds, scopeIds);
          }, 300);
        }
      },
      (error: any) => {
        console.error('Error fetching scopes:', error);
      }
    );
  }

  /**
   * Pre-select scopes and return the scope IDs
   */
  private preSelectScopesAndGetIds(scopes: any[]): string {
    console.log('Pre-selecting scopes and extracting IDs:', scopes);
    
    let scopeIdString = '';
    
    // Both Regular and Bulk PO now support multiple scope selection
    const allScopeIds = scopes.reduce((acc: any[], scope: any) => {
      return acc.concat(scope.job_scope_ids || []);
    }, []);
    
    scopeIdString = allScopeIds.join(',');
    this.scopeId = scopeIdString;
    this.scopeName = scopes; // Set as array for both Regular and Bulk
    
    console.log(`Set ${scopes.length} scopes for ${this.purchaseOrderObject.type} PO`);
    console.log('Scope IDs:', this.scopeId);
    console.log('Scope names:', scopes.map(s => s.scope_name));
    
    return scopeIdString;
  }

  /**
   * Fetch materials with specific job and scope IDs
   */
  private fetchMaterialsWithIds(jobIds: number[], scopeIds: string): void {
    console.log('=== Fetching Materials with IDs ===');
    console.log('Job IDs:', jobIds);
    console.log('Scope IDs:', scopeIds);
    
    // Ensure scope IDs are set
    this.scopeId = scopeIds;
    
    // First API call: Get materials with job IDs and scope IDs
    const materialWithScopesUrl = `purchase/orders/material?jobids=${this.jobId}&jobscope_ids=${this.scopeId}`;
    console.log('Calling material API with scopes:', materialWithScopesUrl);
    
    this.backApiService.getApi(materialWithScopesUrl).subscribe(
      (response: any) => {
        console.log('Materials with scopes response:', response);
        this.processMaterialResponse(response);
        
        // Fetch vendors and then materials with vendor IDs
        if (this.purchaseOrderObject.type === 'bulk') {
          this.fetchVendorsAndMaterials(jobIds);
        } else {
          // For regular PO, also fetch materials with vendor IDs from PO data
          this.fetchMaterialsWithVendors(jobIds);
        }
      },
      (error: any) => {
        console.error('Error fetching materials with scopes:', error);
      }
    );
  }
  
  /**
   * Fetch materials with vendor IDs
   */
  private fetchMaterialsWithVendors(jobIds: number[]): void {
    // For regular PO in edit mode, also fetch ALL vendors using vendors-by-product API
    console.log('Fetching vendors for regular PO in edit mode');
    
    const params = { jobids: jobIds.join(',') };
    
    this.purchaseOrderService.vendorSearch(params).subscribe(
      (response: any) => {
        console.log('Vendor search response for regular PO:', response);
        
        if (response?.data && response.data.length > 0) {
          // Auto-select ALL vendors from the API response
          this.autoSelectAllVendors(response.data);
          
          // Now fetch materials with ALL vendor IDs
          const vendorIds = response.data.map((v: any) => v.vendor_id || v.id);
          const vendorIdString = vendorIds.join(',');
          
          // API call with vendor IDs
          const materialWithVendorsUrl = `purchase/orders/material?jobids=${this.jobId}&vendor_ids=${vendorIdString}`;
          console.log('Calling material API with ALL vendors for regular PO:', materialWithVendorsUrl);
          
          this.backApiService.getApi(materialWithVendorsUrl).subscribe(
            (materialResponse: any) => {
              console.log('Materials with vendors response:', materialResponse);
              this.processMaterialResponse(materialResponse);
              
              // After loading vendor materials, also ensure PO materials are in the list
              this.ensurePOMaterialsAreLoaded();
            },
            (error: any) => {
              console.error('Error fetching materials with vendors:', error);
              // Still ensure PO materials are loaded
              this.ensurePOMaterialsAreLoaded();
            }
          );
        } else {
          // If no vendors returned, still ensure PO materials are loaded
          console.log('No vendors found, ensuring PO materials are loaded');
          this.ensurePOMaterialsAreLoaded();
        }
      },
      (error: any) => {
        console.error('Error fetching vendors:', error);
        // On error, still ensure PO materials are loaded
        this.ensurePOMaterialsAreLoaded();
      }
    );
  }
  
  /**
   * Fetch vendors and then materials with vendor IDs
   */
  private fetchVendorsAndMaterials(jobIds: number[]): void {
    const params = { jobids: jobIds.join(',') };
    console.log('Fetching vendors with params:', params);
    
    this.purchaseOrderService.vendorSearch(params).subscribe(
      (response: any) => {
        console.log('Vendor search response:', response);
        
        if (response?.data && response.data.length > 0) {
          // Auto-select ALL vendors from the API response in edit mode
          this.autoSelectAllVendors(response.data);
          
          // Now fetch materials with vendor IDs
          const vendorIds = response.data.map((v: any) => v.vendor_id || v.id);
          const vendorIdString = vendorIds.join(',');
          
          // API call with vendor IDs
          const materialWithVendorsUrl = `purchase/orders/material?jobids=${this.jobId}&vendor_ids=${vendorIdString}`;
          console.log('Calling material API with ALL vendors:', materialWithVendorsUrl);
          
          this.backApiService.getApi(materialWithVendorsUrl).subscribe(
            (materialResponse: any) => {
              console.log('Materials with vendors response:', materialResponse);
              this.processMaterialResponse(materialResponse);
              
              // After loading vendor materials, also ensure PO materials are in the list
              this.ensurePOMaterialsAreLoaded();
            },
            (error: any) => {
              console.error('Error fetching materials with vendors:', error);
              // Still ensure PO materials are loaded
              this.ensurePOMaterialsAreLoaded();
            }
          );
        }
      },
      (error: any) => {
        console.error('Error fetching vendors:', error);
      }
    );
  }
  
  /**
   * Process pre-fetched material data from dialog
   */
  private processMaterialDataFromDialog(): void {
    if (!this.materialDataFromDialog) return;
    
    console.log('Processing material data from dialog:', this.materialDataFromDialog);
    
    const { scopeMaterials, vendorMaterials, scopeMaterialsMap, vendorMaterialsMap } = this.materialDataFromDialog;
    
    // Process scope materials
    if (scopeMaterials && scopeMaterials.length > 0) {
      console.log('Processing scope materials:', scopeMaterials.length);
      
      // Initialize material list if needed
      if (!this.materialList) {
        this.materialList = [];
      }
      
      // Add scope materials
      scopeMaterials.forEach((material: any) => {
        const materialData = material.data?.data || material.data || material;
        if (Array.isArray(materialData)) {
          materialData.forEach((item: any) => {
            this.addMaterialToList(item);
          });
        } else if (materialData) {
          this.addMaterialToList(materialData);
        }
      });
    }
    
    // Process vendor materials
    if (vendorMaterials && vendorMaterials.length > 0) {
      console.log('Processing vendor materials:', vendorMaterials.length);
      
      if (!this.materialList) {
        this.materialList = [];
      }
      
      vendorMaterials.forEach((material: any) => {
        const materialData = material.data?.data || material.data || material;
        if (Array.isArray(materialData)) {
          materialData.forEach((item: any) => {
            this.addMaterialToList(item);
          });
        } else if (materialData) {
          this.addMaterialToList(materialData);
        }
      });
    }
    
    // Pre-select scopes and vendors based on PO data
    this.preSelectScopesAndVendorsFromPO();
    
    // Materials are loaded but NOT pre-selected in edit mode
    console.log('Materials loaded from dialog data without pre-selection');
  }
  
  /**
   * Add a material to the list avoiding duplicates
   */
  private addMaterialToList(material: any): void {
    const exists = this.materialList.some((m: any) => 
      (m.product_id === material.product_id) || 
      (m.material_id === material.material_id) ||
      (m.id === material.id)
    );
    
    if (!exists) {
      // CRITICAL FIX: Ensure job_scope_item_id is always set
      // The API returns 'id' field which is the job_scope_item_id
      if (!material.job_scope_item_id && material.id) {
        material.job_scope_item_id = material.id;
      }
      
      // Set initial properties
      material.originalColor = getRandomLightColor();
      material.new_quantity = material.quantity;
      material.previous_quantity_total = Number(material.contract_price) * Number(material.quantity) || 0;
      material.new_quantity_total = Number(material.contract_price) * Number(material.quantity) || 0;
      
      // Map scope to scope_name for display in the table
      if (material.scope && !material.scope_name) {
        material.scope_name = material.scope;
      }
      
      this.materialList.push(material);
    }
  }
  
  /**
   * Pre-select scopes and vendors from PO data
   */
  private preSelectScopesAndVendorsFromPO(): void {
    if (!this.purchaseOrderData) return;
    
    const poItems = this.purchaseOrderData.items || [];
    
    // Extract unique scope IDs and vendor IDs
    const scopeIds = new Set<number>();
    const vendorIds = new Set<number>();
    
    poItems.forEach((item: any) => {
      if (item.scope_id) {
        scopeIds.add(item.scope_id);
      }
    });
    
    // Set vendor from main PO data
    if (this.purchaseOrderData.vendor_id) {
      vendorIds.add(this.purchaseOrderData.vendor_id);
    }
    
    console.log('Scope IDs to pre-select:', Array.from(scopeIds));
    console.log('Vendor IDs to pre-select:', Array.from(vendorIds));
    
    // TODO: Actually pre-select the scopes and vendors in the UI
    // This would depend on how the scope and vendor selection components work
  }

  /**
   * Process material API response and update material list
   */
  private processMaterialResponse(response: any): void {
    if (!response?.data?.data) return;
    
    const newMaterials = response.data.data;
    console.log('Processing materials:', newMaterials.length);
    
    // If materialList doesn't exist, create it
    if (!this.materialList) {
      this.materialList = [];
    }
    
    // Add new materials to the list (avoid duplicates)
    newMaterials.forEach((newMaterial: any) => {
      const exists = this.materialList.some((m: any) => 
        (m.product_id === newMaterial.product_id) || 
        (m.material_id === newMaterial.material_id) ||
        (m.id === newMaterial.id)
      );
      
      if (!exists) {
        // CRITICAL FIX: Ensure job_scope_item_id is always set
        // The API returns 'id' field which is the job_scope_item_id
        if (!newMaterial.job_scope_item_id && newMaterial.id) {
          newMaterial.job_scope_item_id = newMaterial.id;
        }
        
        // Set initial properties
        newMaterial.originalColor = getRandomLightColor();
        newMaterial.new_quantity = newMaterial.quantity;
        newMaterial.previous_quantity_total = Number(newMaterial.contract_price) * Number(newMaterial.quantity) || 0;
        newMaterial.new_quantity_total = Number(newMaterial.contract_price) * Number(newMaterial.quantity) || 0;
        
        // Map scope to scope_name for display in the table
        if (newMaterial.scope && !newMaterial.scope_name) {
          newMaterial.scope_name = newMaterial.scope;
        }
        
        // Preserve quantity_on_hand if it exists
        if (!newMaterial.quantity_on_hand && newMaterial.quantity_on_hand !== 0) {
          newMaterial.quantity_on_hand = 0;
        }
        
        // Preserve job_number if it exists
        if (!newMaterial.job_number && newMaterial.job_name) {
          newMaterial.job_number = newMaterial.job_name;
        }
        
        this.materialList.push(newMaterial);
      }
    });
    
    console.log('Total materials in list:', this.materialList.length);
    
    // After processing materials, add PO edit items if they exist
    if (this.isEditMode && this.poEditItemsData) {
      console.log('Adding PO edit items to material list after processing materials');
      this.addPOEditItemsToMaterialList();
    }
  }



  /**
   * Pre-select vendors in the UI
   */
  private preSelectVendors(vendors: any[]): void {
    console.log('Pre-selecting vendors:', vendors);
    
    const vendorIds = vendors.map(v => v.vendor_id || v.id);
    // Set vendorId as comma-separated string for the API
    this.vendorId = vendorIds.join(',');
    this.vendorDetail = vendors;
    console.log('Set vendor IDs:', this.vendorId);
    
    // Don't call selectedValueChange as it will override our values
    // The vendor IDs are already set correctly for the API
  }

  /**
   * Ensure all materials from the PO are loaded in the material list
   */
  private ensurePOMaterialsAreLoaded(): void {
    if (!this.isEditMode || !this.purchaseOrderData?.items) {
      console.log('Not in edit mode or no PO items to load');
      return;
    }
    
    console.log('=== ENSURING PO MATERIALS ARE LOADED ===');
    const poItems = this.purchaseOrderData.items || [];
    console.log(`Checking ${poItems.length} PO items`);
    
    let addedCount = 0;
    poItems.forEach((poItem: any) => {
      const materialId = poItem.material_id || poItem.product_id || poItem.id;
      
      // Check if this material is already in the list
      const existsInList = this.materialList.some((mat: any) => {
        const matId = mat.material_id || mat.product_id || mat.id;
        return matId === materialId;
      });
      
      if (!existsInList) {
        // Material from PO is not in the list, add it
        console.log(`Adding PO material to list: ${poItem.product_name || poItem.material_name} (ID: ${materialId})`);
        
        const newMaterial = {
          ...poItem,
          id: materialId,
          material_id: materialId,
          product_id: materialId,
          product_name: poItem.product_name || poItem.material_name || 'Unknown Material',
          vendor_name: poItem.vendor?.name || poItem.vendor_name || '',
          vendor_id: poItem.vendor_id || poItem.vendor?.id,
          unit_price: poItem.unit_cost || poItem.unit_price || 0,
          quantity: poItem.order_quantity || poItem.quantity || 0,
          new_quantity: poItem.order_quantity || poItem.quantity || 0,
          quantity_on_hand: poItem.quantity_on_hand || 0, // Add quantity on hand
          job_number: poItem.job_number || poItem.job_name || '', // Add job_number
          contract_price: poItem.unit_cost || poItem.unit_price || 0,
          new_quantity_total: poItem.total_cost || 0,
          previous_quantity_total: poItem.total_cost || 0,
          isSelected: false, // DO NOT pre-select
          color: '',
          job_id: poItem.job_id,
          scope_id: poItem.scope_id,
          scope_name: poItem.scope?.name || poItem.scope_name || '', // Add scope_name for display
          job_scope_item_id: poItem.job_scope_item_id
        };
        
        // Calculate totals if not present
        if (!newMaterial.new_quantity_total) {
          newMaterial.new_quantity_total = Number(newMaterial.contract_price) * Number(newMaterial.quantity) || 0;
        }
        
        this.materialList.push(newMaterial);
        addedCount++;
      }
    });
    
    console.log(`Added ${addedCount} materials from PO to the list`);
    console.log(`Total materials in list: ${this.materialList.length}`);
    console.log('=== END ENSURING PO MATERIALS ===');
  }

  /**
   * Auto-select ALL vendors from the vendors-by-product API in edit mode
   */
  private autoSelectAllVendors(vendors: any[]): void {
    console.log('=== AUTO-SELECTING ALL VENDORS IN EDIT MODE ===');
    console.log('Vendors to select:', vendors);
    
    // Format vendors for the UI field
    const formattedVendors = vendors.map((vendor: any) => ({
      vendor_id: vendor.vendor_id || vendor.id,
      vendor_name: vendor.vendor_name || vendor.name || ''
    }));
    
    // Set vendor IDs for API calls
    const vendorIds = vendors.map(v => v.vendor_id || v.id);
    this.vendorId = vendorIds.join(',');
    
    if (this.purchaseOrderObject.type === 'bulk') {
      // For bulk PO, set multiple vendors
      this.vendorDetail = formattedVendors;
      console.log('Set vendorDetail for bulk PO:', this.vendorDetail);
    } else if (this.purchaseOrderObject.type === 'regular' && this.isEditMode) {
      // For regular PO in edit mode, show all vendors but as a comma-separated string
      const vendorNames = formattedVendors.map(v => v.vendor_name).join(', ');
      this.vendorName = vendorNames;
      this.vendorDetail = formattedVendors; // Keep the array for internal use
      console.log('Set vendorName for regular PO:', this.vendorName);
    }
    
    console.log('All vendors selected. Vendor IDs:', this.vendorId);
    console.log('=== END AUTO-SELECTION ===');
  }

  /**
   * Check and select materials based on PO data
   */
  private checkAndSelectMaterials(): void {
    if (!this.materialList || this.materialList.length === 0) {
      console.warn('Material list not loaded yet, retrying...');
      setTimeout(() => this.checkAndSelectMaterials(), 1000);
      return;
    }
    
    console.log('Checking and selecting materials');
    const poItems = this.purchaseOrderData.items || [];
    
    // Create a map of material IDs from PO
    const poMaterialIds = new Set();
    poItems.forEach((item: any) => {
      if (item.material_id) poMaterialIds.add(item.material_id);
      if (item.product_id) poMaterialIds.add(item.product_id);
    });
    
    // Check materials that match PO items
    let checkedCount = 0;
    this.materialList.forEach((material: any) => {
      const materialId = material.product_id || material.material_id || material.id;
      
      if (poMaterialIds.has(materialId)) {
        material.isSelected = true;
        checkedCount++;
        
        // Update quantity from PO if available
        const poItem = poItems.find((item: any) => 
          item.material_id === materialId || item.product_id === materialId
        );
        
        if (poItem && poItem.order_quantity) {
          material.quantity = poItem.order_quantity;
          material.new_quantity = poItem.order_quantity;
        }
      }
    });
    
    console.log(`Checked ${checkedCount} materials`);
    
    // Update select all checkbox
    this.allMaterialSelected = checkedCount === this.materialList.length && checkedCount > 0;
    
    // Trigger material selection to group by vendor
    if (checkedCount > 0) {
      // Process checked materials for vendor grouping
      this.materialList.forEach((material: any, index: number) => {
        if (material.isSelected) {
          this.selectedMaterial(true, material, index);
        }
      });
    }
  }
  

  /**
   * Pre-select scopes in the UI (legacy method for compatibility)
   */
  preSelectScopes_legacy(scopes: any[]): void {
    console.log('Pre-selecting scopes in UI:', scopes);
    console.log('Current PO type:', this.purchaseOrderObject.type);
    
    if (!scopes || scopes.length === 0) {
      console.warn('No scopes to pre-select');
      return;
    }
    
    // For edit mode, select ALL scopes regardless of PO type
    // This ensures all scopes from the jobs are available
    if (this.purchaseOrderObject.type === 'bulk' || scopes.length > 1) {
      // For bulk PO or multiple scopes, select all
      console.log('Selecting all scopes for bulk/multi-scope PO');
      
      // Use reduce to flatten arrays since flatMap may not be available
      this.scopeId = scopes.reduce((acc: any[], scope: any) => {
        return acc.concat(scope.job_scope_ids || []);
      }, []);
      this.scopeName = scopes;
      
      console.log('Set scopeId to:', this.scopeId);
      console.log('Set scopeName to:', this.scopeName);
      
      // Trigger the scope selection handler with all scopes
      this.selectedValueChange(scopes, 'scope');
    } else {
      // For regular PO with single scope
      console.log('Selecting single scope for regular PO');
      const firstScope = scopes[0];
      this.scopeName = firstScope.scope_name;
      this.scopeId = firstScope.job_scope_ids || [];
      
      console.log('Set scopeId to:', this.scopeId);
      console.log('Set scopeName to:', this.scopeName);
      
      // Trigger the scope selection handler
      this.selectedValueChange(firstScope, 'scope');
    }
    
    // After setting scopes, fetch materials
    console.log('Fetching materials after scope selection');
    setTimeout(() => {
      this.getMaterial();
      
      // Materials are loaded but NOT pre-selected in edit mode
      if (this.isEditMode) {
        console.log('Edit mode: Materials loaded without pre-selection');
      }
    }, 1000);
  }
  
  /**
   * Pre-select vendor in the UI (for non-edit mode)
   */
  preSelectVendor(vendor: any): void {
    console.log('Pre-selecting vendor in UI:', vendor);
    
    if (!vendor) {
      console.warn('No vendor to pre-select');
      return;
    }
    
    // Set vendor values - ensure vendor_id is properly accessed
    const vendorId = vendor.vendor_id || vendor.id;
    console.log('Setting vendor ID:', vendorId);
    
    this.vendorId = [vendorId];
    this.vendorDetail = [vendor];
    
    console.log('Set vendorId to:', this.vendorId);
    console.log('Set vendorDetail to:', this.vendorDetail);
    
    // Trigger the vendor selection handler
    this.selectedValueChange([vendor], 'vendor');
    
    // After setting vendor, fetch materials if scopes are also set
    if (this.scopeId && this.scopeId.length > 0) {
      console.log('Scopes already set, fetching materials');
      setTimeout(() => {
        this.getMaterial();
        
        // Materials are loaded but NOT pre-selected in edit mode
        if (this.isEditMode) {
          console.log('Edit mode: Materials loaded without pre-selection');
        }
      }, 1000);
    } else {
      console.log('Waiting for scopes to be set before fetching materials');
    }
  }
  
  /**
   * Pre-populate materials from existing PO data in edit mode
   */
  prePopulateMaterials(): void {
    if (!this.isEditMode || !this.purchaseOrderData?.items) return;
    
    console.log('Pre-populating materials from PO data');
    
    // Use the new checkAndSelectMaterials method
    this.checkAndSelectMaterials();
  }
  
  /**
   * Add material to vendor group for display
   */
  addMaterialToVendorGroup(material: any, vendor: any): void {
    // Find or create vendor group
    let vendorGroup = this.vendorGroupList.find((v: any) => 
      (v.vendor_id || v.id) === (vendor.vendor_id || vendor.id)
    );
    
    if (!vendorGroup) {
      vendorGroup = {
        ...vendor,
        id: vendor.vendor_id || vendor.id,
        vendor_id: vendor.vendor_id || vendor.id,
        vendor_name: vendor.vendor_name || vendor.name,
        materialList: [],
        color: getRandomLightColor(),
        vendor_tax: 0, // Initialize vendor tax
        is_free_shipping: vendor.is_free_shipping ?? false,
        is_discount: vendor.is_discount ?? false,
        quotation_reference: this.vendorQuoteList && this.vendorQuoteList.length > 0 ? this.vendorQuoteList[0].quotation_reference : ''
      };
      this.vendorGroupList.push(vendorGroup);
    }
    
    // Add material to vendor's material list
    if (!vendorGroup.materialList.some((m: any) => m.id === material.id)) {
      vendorGroup.materialList.push(material);
      material.color = vendorGroup.color; // Set same color for grouping
    }
    
    // Recalculate vendor totals
    this.recalcVendor(vendorGroup);
  }

  createPurchaseOrder() {
    // Prepare base data for the confirmation dialog
    const dialogData: any = {
      info: [...this.separatePOJobList, ...this.vendorGroupList],
      jobIdList: this.purchaseOrderObject.type === 'bulk' ? this.jobDetail?.map((x: any) => x.id) : [this.jobId],
      purchaseOrderObject: this.purchaseOrderObject,
      priceEngineeringFlag: this.purchaseOrderObject.price_engineering_flag
    };

    // Add edit mode specific data only when in edit mode
    if (this.isEditMode) {
      dialogData.isEditMode = true;
      dialogData.purchaseOrderId = this.purchaseOrderData?.id || null;
      dialogData.deselectedPOItems = this.deselectedPOItems; // Pass deselected items for status reset
      dialogData.materialList = this.materialList; // Pass material list for status updates
    }

    const dialogRef = this.dialog.open(ConfirmPurchaseOrderComponent, {
      width: '700px',
      minHeight: '700px',
      data: dialogData,
      backdropClass: 'po-confirm-backdrop',
      panelClass: 'po-confirm-dialog-panel'
    });
    
    dialogRef.afterClosed().subscribe(result => {
      console.log('PurchaseOrderComponent received result from ConfirmDialog:', result);
      
      if (result == 'close') {
        console.log('Closing with "close" status');
        this.dialogRef.close('close')
      } else if (result == 'updated') {
        // Pass the updated status back to parent (PurchaseOrderDetail)
        console.log('Closing with "updated" status (old format)');
        this.dialogRef.close('updated')
      } else if (result?.status === 'updated') {
        // Pass the updated data back to parent - THIS IS THE IMPORTANT ONE
        console.log('Closing with updated data object, triggering parent reload');
        this.dialogRef.close(result)
      }
    });
  }

  allSplitMaterialSelection(event: any) { }


  selectedSplitMaterial(event: boolean, vendor: any, material: any) {
    if (event) {
      vendor.isGroupMaterial = true;

      let existingVendor = this.materialSplitGroupList.find((v: any) => v.id === vendor.id);

      if (existingVendor) {
        // Same vendor → sirf material merge karo
        if (!existingVendor.materialList.some((m: any) => m.id === material.id)) {
          existingVendor.materialList.push(material);
        }
        this.recalcVendor(existingVendor);
      } else {
        // Vendor different hai → new vendor entry banao
        const vendorClone = {
          ...vendor,
          materialList: [material]
        };
        this.recalcVendor(vendorClone);


        this.materialSplitGroupList.push(vendorClone);
      }

    } else {
      // Material unselect ho raha hai
      material.isGroupMaterial = false;

      let existingVendor = this.materialSplitGroupList.find((v: any) => v.id === vendor.id);
      if (existingVendor) {
        existingVendor.materialList = existingVendor.materialList.filter((m: any) => m.id !== material.id);
        if (existingVendor.materialList.length === 0) {
          this.materialSplitGroupList = this.materialSplitGroupList.filter((v: any) => v.id !== vendor.id);
        } else {
          this.recalcVendor(existingVendor);
        }
      }
    }

    // VendorGroupList update
    let vendorInMain = this.vendorGroupList.find((v: any) => v.id === vendor.id);
    if (vendorInMain) {
      this.recalcVendor(vendorInMain);
    }

    // SeparatePOJobList update
    let vendorInSeparate = this.separatePOJobList.find((v: any) => v.id === vendor.id);
    if (vendorInSeparate) {
      this.recalcVendor(vendorInSeparate);
    }
  }

  undoSplit(selectedJobs: any) {
    if (!selectedJobs || !selectedJobs.length) return;

    // Group by vendorId to keep jobs of the same vendor together
    const groupedByVendor = selectedJobs.reduce((acc: any, job: any) => {
      const vendorId = job.vendorId || job.vendor_id; // Adjust according to your data
      if (!acc[vendorId]) {
        acc[vendorId] = [];
      }
      acc[vendorId].push(job);
      return acc;
    }, {});

    // Move each vendor's jobs back into vendorGroupList
    Object.keys(groupedByVendor).forEach(vendorId => {
      const jobs = groupedByVendor[vendorId];

      // Find vendor in vendorGroupList or create new one
      let vendorGroup = this.vendorGroupList.find((v: any) => v.vendorId === vendorId);
      if (!vendorGroup) {
        vendorGroup = {
          vendorId: vendorId,
          vendor_id: vendorId,
          materialList: [],
          quote_amount: 0,
          vendor_tax: 0, // Initialize vendor tax
          is_free_shipping: false,
          is_discount: false
        };
        this.vendorGroupList.push(vendorGroup);
      }

      // Merge jobs back
      vendorGroup.materialList.push(...jobs);

      // Recalculate quote_amount
      vendorGroup.quote_amount = vendorGroup.materialList.reduce((total: number, mat: any) => {
        return total + (Number(mat.new_quantity_total) || 0);
      }, 0);

      // Recalculate vendor_tax
      vendorGroup.vendor_tax = vendorGroup.materialList.reduce((total: number, mat: any) => {
        return total + (Number(mat.estimated_vendor_tax) || 0);
      }, 0);
    });

    // Remove the selected jobs from separatedJobList
    this.separatePOJobList = this.separatePOJobList.filter((job: any) => !selectedJobs.includes(job));
  }


  recalculateVendorTotals(vendor: any) {
    vendor.quote_amount = vendor.materialList?.reduce((total: any, material: any) => {
      return total + (Number(material.new_quantity_total) || 0);
    }, 0) || 0;

    // Also recalculate vendor_tax
    vendor.vendor_tax = vendor.materialList?.reduce((total: number, mat: any) => {
      return total + (Number(mat.estimated_vendor_tax) || 0);
    }, 0) || 0;

    if (vendor.calculation) {
      vendor.calculation.vendorTax = vendor.vendor_tax;
    }
  }


  private recalcVendor(vendor: any) {
    // In edit mode, use fee_ship for shipping charges if available
    const shippingCharges = this.isEditMode && vendor.fee_ship ?
      Number(vendor.fee_ship) :
      (vendor.calculation?.shippingCharges || 0);

    // Material cost calculation - use the actual totals from materials
    const materialCost = vendor.materialList.reduce((sum: number, mat: any) => {
      // Use new_quantity_total or previous_quantity_total which are already calculated totals
      const matTotal = Number(mat.new_quantity_total) ||
                      Number(mat.previous_quantity_total) ||
                      (Number(mat.unit_price || mat.unit_cost || 0) * Number(mat.new_quantity || mat.quantity || 0));
      return sum + matTotal;
    }, 0);

    // Quote amount calculation (sum of material totals)
    const quoteAmount = vendor.materialList.reduce((total: number, mat: any) => {
      return total + (Number(mat.new_quantity_total) || Number(mat.previous_quantity_total) || 0);
    }, 0);

    // Calculate total vendor tax from all materials
    const totalVendorTax = vendor.materialList.reduce((sum: number, mat: any) => {
      return sum + (Number(mat.estimated_vendor_tax) || 0);
    }, 0);

    // Set quote_amount - in edit mode, preserve backend value if it exists
    if (this.isEditMode && this.poEditItemsData?.quote_amount && !vendor.quote_amount) {
      vendor.quote_amount = this.poEditItemsData.quote_amount;
    } else if (!vendor.quote_amount) {
      vendor.quote_amount = quoteAmount;
    }

    // Update vendor_tax field with the calculated total
    vendor.vendor_tax = totalVendorTax;

    // Update calculation object with proper values
    vendor.calculation = {
      ...vendor.calculation,
      shippingCharges: shippingCharges,
      materialCost: materialCost,
      subTotalCost: materialCost + shippingCharges,
      totalCost: materialCost + shippingCharges,
      costSaving: vendor.calculation?.costSaving || 0,
      cost_escalation: vendor.calculation?.cost_escalation || 0,
      vendorTax: totalVendorTax  // Use calculated total vendor tax
    };

    console.log(`Recalculated vendor ${vendor.name || vendor.vendor_name}:`, {
      materialCost: materialCost,
      shippingCharges: shippingCharges,
      totalCost: materialCost + shippingCharges,
      vendorTax: totalVendorTax,
      quote_amount: vendor.quote_amount
    });
  }





  groupMaterial(item: any) {
    console.log(item);
  }

  //#region Filter Part HERE
  close = (data: any = '') => {
    this.dialogRef.close(data);
  }

  onJobItemSelect(event: any) {
    this.selectedJobItem = event;
    this.showJobSearchInput = false;
  }

  updateSearchInputFlag() {
    this.showJobSearchInput = true;
  }

  dateChange() {
    // Use setTimeout to ensure ngModel binding is complete before checking values
    setTimeout(() => {
      console.log('dateChange called:', {
        fromDate: this.fromDate,
        toDate: this.toDate,
        jobId: this.jobId,
        willCallGetMaterial: !!(this.jobId && this.fromDate && this.toDate)
      });

      this.jobAdditionalParams.purchase_order_date_start = this.fromDate
      this.jobAdditionalParams.purchase_order_date_end = this.toDate
      this.scopeAdditionalParam.purchase_order_date_start = this.fromDate
      this.scopeAdditionalParam.purchase_order_date_end = this.toDate

      // Refresh material list only when BOTH dates are selected
      if (this.jobId && this.fromDate && this.toDate) {
        console.log('Calling getMaterial with both dates');
        this.getMaterial();
      } else {
        console.log('Skipping getMaterial - waiting for both dates');
      }
    }, 0);
  }

  onChangeExcludeDate(event: any) {
    this.exclude_date_boolean = event
    this.exclude_date = this.exclude_date_boolean ? 1 : 0
    this.jobAdditionalParams.exclude_date = this.exclude_date
    this.scopeAdditionalParam.exclude_date = this.exclude_date

    // Refresh material list only when both dates are selected
    if (this.jobId && this.fromDate && this.toDate) {
      this.getMaterial();
    }
  }

  onTypeChange(value: any) {
    this.materialList = []
    this.vendorGroupList = []
    this.materialSplitGroupList = []
    this.selectedJobItem = []
    this.scopeId = []
    this.scopeName = null
    this.allMaterialSelected = false
    this.allSplitMaterialSelected = false
    if (value == 'bulk') {
      this.jobDetail = [{ id: this.jobId, name: this.job_name }]
    } else {
      this.jobDetail = this.job_name || this.jobDetail[0]?.name
      this.jobId = this.jobDetail[0]?.id
    }
  }
  //#endregion

  selectedValueClear(event: boolean, type: string = '') {
    // In edit mode, prevent clearing scopes that are in the PO
    if (this.isEditMode && type === 'scope' && this.editModeScopeIds.length > 0) {
      console.log('Cannot clear scope in edit mode - scopes are locked');
      return;
    }

    if (type === 'scope') {
      this.scopeId = [];
      this.scopeName = null;
      // Always fetch materials when scope is cleared
      this.getMaterial(); // Will fetch all materials without scope filter
    } else if (type === 'vendor') {
      this.vendorId = [];
      this.vendorDetail = null;
      // Fetch materials with current scope filter (if any) but no vendor filter
      this.getMaterial();
    } else if (type === 'vendorQuote') {
      console.log('Vendor quote cleared');
      this.vendorQuoteList = [];
      // Keep only the initial vendor quote materials (if any)
      if (this.isFromVendorQuote && this.materialDataFromDialog) {
        this.processVendorQuoteMaterials();
      }
    }
  }

  onClearScope() {
    // Don't clear materials - instead fetch all materials without scope filter
    console.log('onClearScope called - fetching all materials');
    this.scopeId = [];
    this.scopeName = null;
    // Don't clear materialList here - getMaterial will refresh it
    // this.materialList = []; // REMOVED
    // this.vendorGroupList = []; //Vendor PO List
    // this.selectedJobList = []; //Job PO Summary
    this.allMaterialSelected = false;
    // Fetch all materials without scope filter
    this.getMaterial();
  }

  //#region  Api Calling Part
  selectedValueChange(event: any, type: string) {
    // Don't block in edit mode during initial setup
    console.log('====== DROPDOWN SELECTION CHANGE ======');
    console.log('Type:', type);
    console.log('Event data:', event);
    console.log('Event data stringified:', JSON.stringify(event));
    if (type === 'scope' && event && event.length > 0) {
      console.log('First selected scope structure:', event[0]);
      console.log('First selected scope keys:', Object.keys(event[0]));
    }
    console.log('====== END DROPDOWN CHANGE ======');

    if (type == 'job') {
      // Prevent job changes in edit mode
      if (this.isEditMode) {
        console.log('Job field is disabled in edit mode - ignoring change');
        return;
      }

      // Check if job is being deselected (empty or null)
      if (!event || (Array.isArray(event) && event.length === 0)) {
        console.log('Job deselected - clearing all data');
        this.jobId = null;
        this.jobDetail = null;
        this.jobAdditionalParams.job_id = null;
        this.scopeAdditionalParam.jobids = null;

        // Clear all related data
        this.materialList = [];
        this.vendorGroupList = [];
        this.separatePOJobList = [];
        this.selectedJobList = [];
        this.scopeId = [];
        this.scopeName = null;
        this.vendorId = [];
        this.vendorDetail = null;
        this.allMaterialSelected = false;
        this.allSplitMaterialSelected = false;

        // Reset calculations
        this.calculation = {
          contract_price_total: 0,
          previous_total: 0,
          new_total: 0,
        };

        console.log('All data cleared due to job deselection');
        return;
      }

      // Job selected - update jobId and related params
      // Handle both single job (regular PO) and multiple jobs (bulk PO)
      if (Array.isArray(event)) {
        // Bulk PO: multiple jobs
        this.jobId = event.map((x: any) => x.id);
        this.jobDetail = event;
      } else {
        // Regular PO: single job
        this.jobId = event?.id ? [event.id] : null;
        // For regular PO, jobDetail should be the job name string for display
        this.jobDetail = event?.name || event?.job_number || '';
        this.selectedJobItem = event; // Store full object separately
      }
      this.jobAdditionalParams.job_id = Array.isArray(this.jobId) ? this.jobId?.join(",") : this.jobId;
      this.scopeAdditionalParam.jobids = Array.isArray(this.jobId) ? this.jobId?.join(",") : this.jobId;

      // Clear previous data when job changes
      this.materialList = [];
      this.vendorGroupList = [];
      this.separatePOJobList = [];
      this.selectedJobList = [];
      this.scopeId = [];
      this.scopeName = null;
      this.allMaterialSelected = false;

      console.log('Job changed - data cleared, ready to load new materials');

      // Auto-load materials for the newly selected job
      this.autoLoadMaterialsForJob();

      // Also fetch vendor discounts for the new job
      if (this.jobId && (this.jobAdditionalParams.job_id || this.scopeAdditionalParam.jobids)) {
        this.getVendorDiscountList();
      }
    }
    if (type == 'scope') {
      // In edit mode, prevent clearing the scope that's already in the PO
      if (!event || (Array.isArray(event) && event.length === 0)) {
        if (this.isEditMode && this.editModeScopeIds.length > 0) {
          console.log('Cannot clear scope in edit mode - maintaining existing scope');
          return;
        }
        // When no scope selected, clear scopeId but still fetch all materials
        console.log('No scope selected - fetching ALL materials');
        this.scopeId = [];
        this.scopeName = null;
        this.getMaterial(); // This will now fetch all materials without scope filter
        return;
      };
      
      // In edit mode, filter out any scope that shouldn't be removable
      if (this.isEditMode && this.editModeScopeIds.length > 0) {
        if (this.purchaseOrderObject.type === 'bulk' && Array.isArray(event)) {
          // For bulk, ensure we don't remove scopes that are in the PO
          const filteredScopes = event.filter((scope: any) => {
            // Check if this scope has any job_scope_ids that are in editModeScopeIds
            const hasPOScope = scope.job_scope_ids?.some((id: number) => 
              this.editModeScopeIds.includes(Number(id))
            );
            // Always keep scopes that are in the PO
            return hasPOScope || !this.editModeScopeIds.includes(scope.scope_id);
          });
          
          // If we filtered out all scopes, maintain at least the PO scopes
          if (filteredScopes.length === 0 && this.scopeName) {
            console.log('Cannot remove all scopes that are in the PO');
            return;
          }
        }
      }
      
      // Handle multiple scope selection for both Regular and Bulk PO
      if (Array.isArray(event)) {
        // Multiple scopes selected (both Regular and Bulk now support this)
        this.scopeId = event.reduce((acc: any[], scope: any) => {
          if (scope.job_scope_ids && Array.isArray(scope.job_scope_ids)) {
            return acc.concat(scope.job_scope_ids);
          }
          return acc;
        }, []);
        this.scopeName = event;
        console.log('Multiple scopes selected:', event.length, 'Total scope IDs:', this.scopeId.length);
      } else if (event) {
        // Single scope selected (backward compatibility)
        this.scopeName = event.scope_name;
        this.scopeId = event.job_scope_ids || [];
        console.log('Single scope selected:', this.scopeName, 'Scope IDs:', this.scopeId);
      }
      this.getMaterial()
    }
    if (type == 'vendor') {
      this.vendorId = event?.map((x: any) => x.vendor_id)
      this.vendorDetail = event
      // Always fetch materials when vendor changes (with or without vendor filter)
      this.getMaterial()
    }
    if (type == 'vendorQuote') {
      console.log('🟢 === VENDOR QUOTE SELECTION CHANGED ===');
      console.log('🟢 Event received:', event);
      console.log('🟢 Event type:', typeof event);
      console.log('🟢 Event is array?', Array.isArray(event));
      console.log('🟢 Event stringified:', JSON.stringify(event, null, 2));

      this.vendorQuoteList = event;
      console.log('🟢 Updated vendorQuoteList:', this.vendorQuoteList);

      // Load materials from selected vendor quotes
      console.log('🟢 Calling loadMaterialsFromVendorQuotes...');
      this.loadMaterialsFromVendorQuotes(event);
    }
  }



  getMaterial = () => {
    console.log('=== getMaterial called ===');
    console.log('Current jobId:', this.jobId);
    console.log('Current scopeId:', this.scopeId);
    console.log('Current vendorId:', this.vendorId);
    console.log('isEditMode:', this.isEditMode);

    // Validation checks
    if (!this.jobId) {
      console.error('ERROR: No jobId available for material fetch!');
      return;
    }

    // REMOVED scope validation - now we can fetch without scopes

    let base_url = 'purchase/orders/material?';
    const queryParams = [];

    queryParams.push(`jobids=${this.jobId}`);

    // Only add scope filter if scopes are selected
    if (this.scopeId && this.scopeId.length > 0) {
      queryParams.push(`jobscope_ids=${Array.isArray(this.scopeId) ? this.scopeId.join(',') : this.scopeId}`);
      console.log('Fetching materials WITH scope filter:', this.scopeId);
    } else {
      console.log('Fetching ALL materials (no scope filter)');
    }

    if (this.vendorId && this.vendorId.length > 0) {
      queryParams.push(`vendor_ids=${Array.isArray(this.vendorId) ? this.vendorId.join(',') : this.vendorId}`);
    }

    // Add date filter parameters if they exist
    if (this.exclude_date !== undefined && this.exclude_date !== null) {
      queryParams.push(`exclude_date=${this.exclude_date}`);
    }

    if (this.fromDate) {
      // Format date as YYYY-MM-DD if it's a Date object
      const formattedFromDate = this.fromDate instanceof Date
        ? this.fromDate.toISOString().split('T')[0]
        : this.fromDate;
      queryParams.push(`purchase_order_date_start=${formattedFromDate}`);
    }

    if (this.toDate) {
      // Format date as YYYY-MM-DD if it's a Date object
      const formattedToDate = this.toDate instanceof Date
        ? this.toDate.toISOString().split('T')[0]
        : this.toDate;
      queryParams.push(`purchase_order_date_end=${formattedToDate}`);
    }

    base_url += queryParams.join('&');
    
    this.backApiService.getApi(base_url).subscribe(
      (response: any) => {
        console.log('=== MATERIAL API RESPONSE ===');
        console.log('Full response:', response);
        console.log('Response success:', response?.success);
        console.log('Response data:', response?.data);
        console.log('Materials array:', response?.data?.data);
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          this.allMaterialSelected = false;
          const vendorColors: { [key: number]: string } = {};
          
          // First, add PO edit items if they exist
          if (this.isEditMode && this.poEditItemsData) {
            console.log('Adding PO edit items FIRST to material list');
            this.materialList = [];
            this.addPOEditItemsToMaterialList();
          } else {
            this.materialList = [];
          }
          
          // Then add the materials from the API
          const apiMaterials = response.data.data;
          
          // Add API materials to the existing list (after PO items)
          apiMaterials.forEach((apiMaterial: any) => {
            // Check if this material already exists by job_scope_item_id
            const existingIndex = this.materialList.findIndex((existing: any) => {
              // Match by job_scope_item_id which is unique
              return (existing.job_scope_item_id === apiMaterial.id || 
                      existing.id === apiMaterial.id);
            });
            
            if (existingIndex !== -1) {
              // Material exists (it's a PO item) - update its pending_purchase_quantity from API
              const existing = this.materialList[existingIndex];
              if (existing.isPOItem) {
                // Update the pending_purchase_quantity from API (backend knows the real available quantity)
                existing.pending_purchase_quantity = apiMaterial.pending_purchase_quantity || 0;
                existing.total_needed_quantity = apiMaterial.quantity || 0;
                console.log(`Updated PO item pending_purchase_quantity from API: ${existing.product_name}, available: ${existing.pending_purchase_quantity}`);
                
                // If there's pending quantity, add another entry for the available quantity
                // This handles the case where quantity was decreased and there's still some available
                if (apiMaterial.pending_purchase_quantity > 0) {
                  console.log(`${existing.product_name} has ${apiMaterial.pending_purchase_quantity} units still available to order`);
                  
                  // Create a new entry for the available quantity
                  const availableMaterial = {
                    ...apiMaterial,
                    product_name: apiMaterial.product_name || apiMaterial.material_name || apiMaterial.item_name,
                    vendor_name: apiMaterial.vendor?.name || apiMaterial.vendor_name || '',
                    vendor_id: apiMaterial.vendor_id || apiMaterial.vendor?.id,
                    scope_name: apiMaterial.scope?.name || apiMaterial.scope_name || '',
                    unit_price: apiMaterial.unit_price || apiMaterial.unit_cost || 0,
                    quantity: apiMaterial.pending_purchase_quantity, // Show only the available quantity
                    new_quantity: apiMaterial.pending_purchase_quantity,
                    quantity_on_hand: apiMaterial.quantity_on_hand || 0,
                    job_number: apiMaterial.job?.job_number || apiMaterial.job_number || '',
                    contract_price: apiMaterial.contract_price || apiMaterial.unit_price || 0,
                    new_quantity_total: 0,
                    previous_quantity_total: 0,
                    isSelected: false,
                    color: '',
                    job_id: apiMaterial.job_id,
                    scope_id: apiMaterial.scope_id || apiMaterial.job_scope?.id,
                    job_scope_item_id: apiMaterial.id,
                    material_id: apiMaterial.material_id || apiMaterial.product_id,
                    product_id: apiMaterial.product_id || apiMaterial.material_id,
                    isAvailableQuantity: true, // Mark this as available quantity entry
                    relatedPOItemIndex: existingIndex // Reference to the PO item
                  };
                  
                  // Use helper function to ensure all prices and totals are properly set
                  this.ensureMaterialPricesAndTotals(availableMaterial);
                  
                  // Add as a separate entry in the material list
                  this.materialList.push(availableMaterial);
                  console.log(`Added available quantity entry for ${availableMaterial.product_name}: ${availableMaterial.quantity} units`);
                }
              }
            } else {
              // New material from API, not in PO
              // Map scope to scope_name for display in the table
              if (apiMaterial.scope && !apiMaterial.scope_name) {
                apiMaterial.scope_name = apiMaterial.scope;
              }
              
              // Ensure quantity_on_hand and job_number are set
              if (!apiMaterial.quantity_on_hand && apiMaterial.quantity_on_hand !== 0) {
                apiMaterial.quantity_on_hand = 0;
              }
              if (!apiMaterial.job_number && apiMaterial.job_name) {
                apiMaterial.job_number = apiMaterial.job_name;
              }

              // Set pending_purchase_quantity for available items
              apiMaterial.available_quantity = apiMaterial.pending_purchase_quantity || apiMaterial.quantity || 0;

              // Stage 6.7: Use item-level tax data from API (job_scope_items table)
              // Backend now returns these fields directly from getMaterials()
              apiMaterial.estimated_vendor_tax = apiMaterial.estimated_vendor_tax || 0;
              apiMaterial.estimated_use_tax = apiMaterial.estimated_use_tax || 0;
              apiMaterial.vendor_state_code = apiMaterial.vendor_state_code || null;

              // Additional tax fields for future use
              apiMaterial.actual_vendor_tax_paid = apiMaterial.actual_vendor_tax_paid || 0;
              apiMaterial.vendor_jurisdiction = apiMaterial.vendor_jurisdiction || null;

              this.materialList.push(apiMaterial);
            }
          });
          
          console.log(`Processing ${this.materialList.length} materials (PO items + API materials)`);
          
          // Process all materials (both PO items and API materials)
          this.materialList.forEach((item: any) => {
            // Assign a color to the vendor_id if not already assigned
            if (!vendorColors[item.vendor_id]) {
              vendorColors[item.vendor_id] = getRandomLightColor();
            }
            item.originalColor = vendorColors[item.vendor_id];
            
            // For PO items from edit API, preserve their pre-selected state
            // For other items, check if they exist in vendorGroupList
            if (!item.isPOItem) {
              // Only override isSelected for non-PO items
              item.isSelected = this.vendorGroupList.some((group: any) =>
                group.materialList?.some((mat: any) => mat.id === item.id)
              );
            }
            // If item.isPOItem is true, keep the isSelected value that was set in addPOEditItemsToMaterialList
            
            // Ensure new_quantity is set
            item.new_quantity = item.quantity || 0;
            
            // In edit mode, ensure pending_purchase_quantity is set for available items
            if (this.isEditMode && !item.isPOItem) {
              // For non-PO items, pending_purchase_quantity shows what's available to order
              if (item.pending_purchase_quantity === undefined) {
                item.pending_purchase_quantity = item.quantity || 0;
              }
              item.available_quantity = item.pending_purchase_quantity;
            }
            
            // Calculate the total contract cost for this item
            // Use contract_price if available, otherwise fall back to unit_price or selling_price
            const unitPrice = item.contract_price || item.unit_price || item.selling_price || 0;
            const quantity = Number(item.quantity) || 0;
            const totalContractCost = Number(unitPrice) * quantity;
            
            // Set the totals
            item.previous_quantity_total = totalContractCost;
            item.new_quantity_total = totalContractCost;
            
            // Also ensure contract_price is set for display in CONTRACT COST column
            if (!item.contract_price && unitPrice) {
              item.contract_price = unitPrice;
            }
          });

          // Set allMaterialSelected to true if all items are selected
          this.allMaterialSelected = this.materialList.every((mat: any) => mat.isSelected);
          this.calculateMaterialTotal();
          
          console.log(`Materials loaded successfully: ${this.materialList.length} items`);
          console.log('First few materials:', this.materialList.slice(0, 3));
          
          // In edit mode, create vendor groups for pre-selected PO items after all materials are loaded
          if (this.isEditMode && this.pendingPOItemsForVendorGroups.length > 0) {
            console.log('Creating vendor groups for pre-selected PO items...');
            this.createVendorGroupsForPOItems(this.pendingPOItemsForVendorGroups);
            this.pendingPOItemsForVendorGroups = []; // Clear after processing
            
            // Recalculate material totals after vendor groups are created
            this.calculateMaterialTotal();
            console.log('Contract price total after vendor groups created:', this.calculation.contract_price_total);
          }
        } else {
          console.error('No materials data in response or invalid format');
          // Still show PO items even if no API materials
          if (this.isEditMode && this.poEditItemsData) {
            console.log('No API materials, but showing PO edit items');
            this.materialList = [];
            this.addPOEditItemsToMaterialList();
            
            // Create vendor groups for PO items even when no API materials
            if (this.pendingPOItemsForVendorGroups.length > 0) {
              console.log('Creating vendor groups for pre-selected PO items (no API materials case)...');
              this.createVendorGroupsForPOItems(this.pendingPOItemsForVendorGroups);
              this.pendingPOItemsForVendorGroups = [];
              
              // Recalculate material totals after vendor groups are created
              this.calculateMaterialTotal();
              console.log('Contract price total after vendor groups created (no API case):', this.calculation.contract_price_total);
            }
          } else {
            this.materialList = [];
          }
        }
      },
      (error: any) => {
        console.error('Error fetching materials:', error);
        // Still show PO items even on error
        if (this.isEditMode && this.poEditItemsData) {
          console.log('Error fetching materials, but showing PO edit items');
          this.materialList = [];
          this.addPOEditItemsToMaterialList();
          
          // Create vendor groups for PO items even on error
          if (this.pendingPOItemsForVendorGroups.length > 0) {
            console.log('Creating vendor groups for pre-selected PO items (error case)...');
            this.createVendorGroupsForPOItems(this.pendingPOItemsForVendorGroups);
            this.pendingPOItemsForVendorGroups = [];
            
            // Recalculate material totals after vendor groups are created
            this.calculateMaterialTotal();
            console.log('Contract price total after vendor groups created (error case):', this.calculation.contract_price_total);
          }
        } else {
          this.materialList = [];
        }
      }
    );
  };

  getVendorPricing = (vendor_id: any, material_id?: number, currentVendor?: any) => {
    // Check if we already have vendors cached for this material
    if (material_id && this.materialVendorListMap.has(material_id)) {
      console.log(`Using cached vendor list for material ${material_id}`);
      this.vendorList = this.materialVendorListMap.get(material_id);
      return;
    }

    // Use material_id for API call, not vendor_id (API expects product_id)
    if (!material_id) {
      console.error('material_id is required to fetch vendors');
      return;
    }

    let base_url = `purchase/orders/product-vendors/${material_id}`
    this.vendorLoadingMaterialIds.add(material_id);
    this.backApiService.getApi(base_url).subscribe((response: any) => {
      this.vendorLoadingMaterialIds.delete(material_id);
      let vendors = response.data?.vendors || [];

      // Check if the current vendor is already in the list
      const currentVendorExists = vendors.some((v: any) =>
        (v.id === vendor_id) || (v.vendor_id === vendor_id)
      );

      // If current vendor is not in the list, add it at the beginning
      if (!currentVendorExists && currentVendor) {
        const currentVendorObj = {
          id: currentVendor.vendor_id || vendor_id,
          vendor_id: currentVendor.vendor_id || vendor_id,
          name: currentVendor.vendor_name,
          unit_price: currentVendor.unit_price,
          // Mark as currently selected
          isCurrentVendor: true
        };
        vendors = [currentVendorObj, ...vendors];
        console.log(`Added current vendor to list:`, currentVendorObj);
      }

      // Store in both global vendorList (for backward compatibility) and in the map
      this.vendorList = vendors;

      // Cache the vendor list for this material to prevent overwriting
      if (material_id) {
        this.materialVendorListMap.set(material_id, vendors);
        console.log(`Cached vendor list for material ${material_id}:`, vendors);
      }
    }, () => {
      // Clear the flag so the menu stops showing "Loading…". The list is deliberately
      // left uncached so the next open retries instead of showing a permanent empty menu.
      this.vendorLoadingMaterialIds.delete(material_id);
    });
  };

  /**
   * Vendors available for a single material row.
   *
   * Reads the per-material cache rather than the shared `vendorList`, so each row's
   * dropdown is independent — otherwise a row shows the previously opened row's vendors
   * until its own request resolves. Populated by `getVendorPricing`, which the row's
   * menu trigger fires on open.
   */
  getVendorsFor(item: any): any[] {
    const materialId = item?.material_id || item?.product_id || item?.id;
    return this.materialVendorListMap.get(materialId) || [];
  }

  /**
   * True while this row's vendor fetch is in flight. The menu renders a placeholder row
   * instead of nothing, so it opens at a stable size rather than flashing empty and
   * then growing when the response lands.
   */
  isVendorListLoading(item: any): boolean {
    const materialId = item?.material_id || item?.product_id || item?.id;
    return this.vendorLoadingMaterialIds.has(materialId);
  }

  getVendorDiscountList() {
    let base_url = `purchase/orders/vendors-discount?jobids=${this.jobId}`
    this.backApiService.getApi(base_url).subscribe((response: any) => {
      this.discountList = response.data?.data;
      if (this.discountList?.length > 0) {
        this.showDiscountConfetti = true;
        setTimeout(() => this.showDiscountConfetti = false, 800);
      }
      this.discountList.forEach(x => {
        x.isSelected = false
      })

      // In edit mode, use the vendor discount data to populate the vendor field
      if (this.isEditMode && this.discountList.length > 0) {
        this.populateVendorFieldFromDiscountData();
      }
    });
  }

  /**
   * Populate vendor field from vendor discount data in edit mode
   */
  populateVendorFieldFromDiscountData(): void {
    if (!this.discountList || this.discountList.length === 0) return;

    console.log('Populating vendor field from discount data:', this.discountList);

    if (this.purchaseOrderObject.type === 'bulk') {
      // For bulk POs, use multiple vendor selection
      this.vendorDetail = this.discountList.map((vendor: any) => ({
        vendor_id: vendor.vendor_id,
        vendor_name: vendor.vendor_name
      }));
      
      // Trigger change detection for vendor field
      setTimeout(() => {
        this.vendorDetail = [...this.vendorDetail];
        this.cdr.detectChanges();
      }, 100);
    } else if (this.purchaseOrderObject.type === 'regular' && this.isEditMode) {
      // For regular POs in edit mode, find the matching vendor
      const poVendorId = this.purchaseOrderData?.vendor_id;
      if (poVendorId) {
        const matchingVendor = this.discountList.find((vendor: any) => vendor.vendor_id === poVendorId);
        if (matchingVendor) {
          this.vendorName = matchingVendor.vendor_name;
          console.log('Found matching vendor for regular PO:', this.vendorName);
          
          // Trigger change detection for vendor field
          setTimeout(() => {
            this.vendorName = matchingVendor.vendor_name;
            this.cdr.detectChanges();
          }, 100);
        }
      }
    }

    console.log('Set vendorDetail to:', this.vendorDetail);
    
    // Also set vendorId for material filtering
    this.vendorId = this.discountList.map((vendor: any) => vendor.vendor_id);
    
    console.log('Set vendorId to:', this.vendorId);
  }

  //#endregion


  //#region Selection Item or Remove Item Part Here

  selectVendorDiscount(event: any, item: any) {
    let materialList = this.materialList.filter((x: any) => x.vendor_id == item.vendor_id)
    this.discountList.forEach(x => {
      if (item.vendor_id === x.vendor_id && event) {
        x.isSelected = true
      } else {
        x.isSelected = false
      }
    })
    this.isDiscountApplied = this.discountList.some(x => x.isSelected)
    materialList.forEach((element: any) => {
      this.selectedMaterial(event, element, -1, event ? true : false)
    });

  }

  getUniqueCodes() {
    return this.separatePOJobList
      .filter((vendor: any) => vendor.uniqueCode)
      .map((vendor: any) => vendor.uniqueCode);
  }

  getNextSplitPoNumber(existingCodes: string[]): number {
    // Convert existing codes to numbers, filter out non-numeric codes
    const numericCodes = existingCodes
      .map(code => parseInt(code, 10))
      .filter(num => !isNaN(num))
      .sort((a, b) => a - b);

    // Find the next available number in sequence
    let nextNumber = 1;
    for (const code of numericCodes) {
      if (code === nextNumber) {
        nextNumber++;
      } else if (code > nextNumber) {
        break;
      }
    }

    return nextNumber;
  }

  togglePanel(item: any): void {
    // Toggle the expand state for the clicked panel
    item.isExpand = !item.isExpand;

    // Optionally, collapse all other panels if only one should be open at a time
    this.vendorGroupList.forEach((panel: any) => {
      if (panel !== item) {
        panel.isExpand = false;
      }
    });
  }

  splitPO(value: { target: string, selected?: string }) {
    if (!this.separatePOJobList) this.separatePOJobList = [];

    // CASE 1: MAIN → Move split materials back to main vendor list
    if (value?.target === 'main') {
      this.separatePOJobList.forEach((vendor: any) => {
        const splitMaterials = vendor.materialList.filter((mat: any) => mat.isSplitMaterial);
        if (splitMaterials.length) {
          let mainVendor = this.vendorGroupList.find((v: any) => (v.vendor_id === vendor.vendor_id || v.id === vendor.id));
          if (!mainVendor) {
            mainVendor = { ...vendor, materialList: [], quote_amount: 0 };
            this.vendorGroupList.push(mainVendor);
          }
          splitMaterials.forEach((mat: any) => {
            if (!mainVendor.materialList.some((m: any) => m.id === mat.id)) {
              mainVendor.materialList.push({ ...mat, isSplitMaterial: false, isGroupMaterial: false });
            }
          });
          vendor.materialList = vendor.materialList.filter((mat: any) => !mat.isSplitMaterial);
          vendor.quote_amount = vendor.materialList.reduce(
            (sum: number, m: any) => sum + (Number(m.amount) || 0), 0
          );
        }
      });
      this.separatePOJobList = this.separatePOJobList.filter((v: any) => v.materialList.length > 0);
    }

    // CASE 2: UNIQUE CODE → Move selected materials to existing split PO
    else if (value?.target !== 'new' && value?.target !== 'main') {
      let sourceVendor = this.separatePOJobList.find((v: any) => v.uniqueCode === value?.selected); 
      if (!sourceVendor && value?.selected) {
        return
      } else {
        sourceVendor = this.materialSplitGroupList;
      }; 
      const splitMaterials = sourceVendor.materialList.filter((mat: any) => mat.isSplitMaterial); 
      if (!splitMaterials.length) return;
      // Remove moved materials from source vendor 
      sourceVendor.materialList = sourceVendor.materialList.filter((mat: any) => !mat.isSplitMaterial); 
      
      // RESET SHIPPING for source vendor when materials are moved out
      sourceVendor.fee_ship = 0;
      sourceVendor.is_free_shipping = false;
      sourceVendor.materialList.forEach((mat: any) => {
        mat.allocated_fee_ship = 0;
      });

      this.separatePOJobList = this.separatePOJobList.filter((v: any) => v.materialList.length > 0);
      // Unset split flag for moved materials 
      
      splitMaterials.forEach((mat: any) => { 
        mat.isSplitMaterial = false; 
        mat.isGroupMaterial = false; 
        mat.allocated_fee_ship = 0; // Reset allocated shipping
      });
      // Find target vendor (same vendor_id but different uniqueCode) 
      const targetVendor = this.separatePOJobList.find((v: any) => v.vendor_id === sourceVendor.vendor_id && v.uniqueCode === value?.target);
      if (targetVendor) {
        // RESET SHIPPING for target vendor too
        targetVendor.fee_ship = 0;
        targetVendor.is_free_shipping = false;
        
        splitMaterials.forEach((mat: any) => {
          if (!targetVendor.materialList.some((m: any) => m.id === mat.id)) {
            const materialCopy = JSON.parse(JSON.stringify(mat));
            materialCopy.allocated_fee_ship = 0; // Ensure reset shipping
            targetVendor.materialList.push(materialCopy);
          }
        });
        targetVendor.quote_amount = targetVendor.materialList.reduce((sum: number, m: any) => sum + (Number(m.amount) || 0), 0);
      }
    }

    // CASE 3: NEW SPLIT → Create new vendor row with uniqueCode
    else if (value?.target === 'new') {
      if (this.materialSplitGroupList.length === 0) return;

      this.materialSplitGroupList.forEach((selectedVendor: any) => {
        if (!selectedVendor.uniqueCode) {
          const existingCodes = this.separatePOJobList.map((v: any) => v.uniqueCode).filter(Boolean);
          let nextNumber = this.getNextSplitPoNumber(existingCodes);
          selectedVendor.uniqueCode = nextNumber.toString();
        }

        // RESET SHIPPING CHARGES ON SPLIT
        selectedVendor.fee_ship = 0;
        selectedVendor.is_free_shipping = false;
        
        // Reset allocated shipping on all materials in the split
        selectedVendor.materialList.forEach((material: any) => {
          material.isSplitMaterial = false;
          material.isGroupMaterial = true;
          material.allocated_fee_ship = 0; // Reset allocated shipping
        });

        let targetVendor = this.separatePOJobList.find(
          (v: any) => v.id === selectedVendor.id && v.uniqueCode === selectedVendor.uniqueCode
        );

        if (targetVendor) {
          // Reset shipping for existing target vendor
          targetVendor.fee_ship = 0;
          targetVendor.is_free_shipping = false;
          
          selectedVendor.materialList.forEach((mat: any) => {
            if (!targetVendor.materialList.some((m: any) => m.id === mat.id)) {
              const materialCopy = JSON.parse(JSON.stringify(mat));
              materialCopy.allocated_fee_ship = 0; // Ensure reset shipping allocation
              targetVendor.materialList.push(materialCopy);
            }
          });
          targetVendor.quote_amount = targetVendor.materialList.reduce(
            (sum: number, m: any) => sum + (Number(m.amount) || 0), 0
          );
        } else {
          const newSplitVendor = JSON.parse(JSON.stringify(selectedVendor));
          // Ensure new split vendor has reset shipping
          newSplitVendor.fee_ship = 0;
          newSplitVendor.is_free_shipping = false;
          
          // ✅ CRITICAL FIX: Ensure materials have proper values for split PO
          newSplitVendor.materialList.forEach((mat: any) => {
            mat.allocated_fee_ship = 0;
            
            // Ensure previous_quantity_total is preserved for cost calculations
            if (!mat.previous_quantity_total && mat.quantity && mat.contract_price) {
              mat.previous_quantity_total = Number(mat.quantity) * Number(mat.contract_price);
            }
            
            // Initialize new_quantity_total if not set
            if (!mat.new_quantity_total) {
              mat.new_quantity_total = mat.previous_quantity_total || 0;
            }
            
            // Ensure material has all required price fields
            this.ensureMaterialPricesAndTotals(mat);
          });
          
          // ✅ CRITICAL FIX: Initialize calculation object properly for split PO
          if (!newSplitVendor.calculation) {
            newSplitVendor.calculation = {
              materialCost: 0,
              shippingCharges: 0,
              totalCost: 0,
              subTotalCost: 0,
              costSaving: 0,
              cost_escalation: 0,
              vendorTax: 0  // Stage 6.7
            };
          } else {
            // Reset the calculation object's shipping charges
            newSplitVendor.calculation.shippingCharges = 0;
            // Preserve cost escalation/saving if they exist
            newSplitVendor.calculation.cost_escalation = newSplitVendor.calculation.cost_escalation || 0;
            newSplitVendor.calculation.costSaving = newSplitVendor.calculation.costSaving || 0;
            newSplitVendor.calculation.vendorTax = newSplitVendor.calculation.vendorTax || 0;  // Stage 6.7
            // Include vendor tax in total calculations
            newSplitVendor.calculation.totalCost = (newSplitVendor.calculation.materialCost || 0) + (newSplitVendor.calculation.vendorTax || 0);
            newSplitVendor.calculation.subTotalCost = (newSplitVendor.calculation.materialCost || 0) + (newSplitVendor.calculation.vendorTax || 0);
          }
          
          // ✅ CRITICAL FIX: Calculate initial quote amount for split vendor
          newSplitVendor.quote_amount = newSplitVendor.materialList.reduce((total: any, material: any) => {
            return total + (Number(material.previous_quantity_total) || 0);
          }, 0);

          // Calculate vendor tax for split vendor from its materials
          newSplitVendor.vendor_tax = newSplitVendor.materialList.reduce((total: number, mat: any) => {
            return total + (Number(mat.estimated_vendor_tax) || 0);
          }, 0);
          
          // ✅ CRITICAL FIX: Calculate percentages for split vendor materials
          const totalAmount = newSplitVendor.quote_amount;
          if (totalAmount > 0) {
            newSplitVendor.materialList.forEach((material: any) => {
              material.percentage = ((Number(material.previous_quantity_total) || 0) / totalAmount) * 100;
                });
          }
          
          
          this.separatePOJobList.push(newSplitVendor);
        }

        const vendorInMainList = this.vendorGroupList.find((v: any) => v.vendor_id === selectedVendor.vendor_id);
        if (vendorInMainList) {
          vendorInMainList.materialList = vendorInMainList.materialList.filter(
            (mat: any) => !selectedVendor.materialList.some((selMat: any) => selMat.id === mat.id)
          );

          // RESET PARENT SHIPPING CHARGES when materials are split out
          vendorInMainList.fee_ship = 0;
          vendorInMainList.is_free_shipping = false;

          // Reset allocated shipping on remaining materials in parent
          vendorInMainList.materialList.forEach((mat: any) => {
            mat.allocated_fee_ship = 0;
          });

          // Recalculate vendor_tax for main vendor after materials are removed
          vendorInMainList.vendor_tax = vendorInMainList.materialList.reduce((total: number, mat: any) => {
            return total + (Number(mat.estimated_vendor_tax) || 0);
          }, 0);

          if (vendorInMainList.calculation) {
            vendorInMainList.calculation.vendorTax = vendorInMainList.vendor_tax;
          }
        }
      });

      this.vendorGroupList = this.vendorGroupList.filter((vendor: any) => vendor.materialList.length > 0);
    }

    // Reset selection & recalc totals
    this.materialSplitGroupList = [];
    this.getQuoteAmount();
    this.calculateMaterialTotal();
    this.calculateVendorCalculation();
    this.calculateJobListrCalculation();
  }




  selectedMaterial(event: any, item: any, index: number, dicount: boolean = false) {
    let vendorGroup = this.vendorGroupList.find((x: any) => x.name === item.vendor_name);

    let selectedJobGroup = this.selectedJobList.find((x: any) => x.id === item.job_id);


    if (event) {
      // Use helper function to ensure all prices and totals are properly set
      this.ensureMaterialPricesAndTotals(item);
      
      // Use consistent color per vendor
      if (!vendorGroup) {
        // If no vendor group exists, create one with a new color
        const vendorColor = getRandomLightColor();
        item.color = vendorColor;
        
        // ONLY in edit mode, check if this vendor is the existing vendor
        let isExistingVendor = false;
        let existingPoNumber = null;
        let existingPoId = null;
        
        if (this.isEditMode && this.purchaseOrderData) {
          isExistingVendor = (item.vendor_id === this.purchaseOrderData.vendor_id || 
                             item.vendor_id === this.purchaseOrderData.vendor?.id);
          if (isExistingVendor) {
            existingPoNumber = this.purchaseOrderData.po_number;
            existingPoId = this.purchaseOrderData.id;
          }
        }
        
        vendorGroup = {
          name: item.vendor_name,
          vendor_id: item.vendor_id,
          color: vendorColor,
          isExpand: false,
          is_free_shipping: this.isEditMode ? (this.poEditItemsData?.shipping_cost === 0 || false) : true,
          materialList: [item],
          // In edit mode, use values from PO edit data if available
          quote_number: this.isEditMode ? (this.poEditItemsData?.quote_number || '') : (item.vendor_quote_reference || 0),
          quotation_reference: this.isEditMode
            ? (this.poEditItemsData?.quotation_reference || '')
            : (this.vendorQuoteList && this.vendorQuoteList.length > 0 ? this.vendorQuoteList[0].quotation_reference : (item.vendor_quote_reference || '')),
          quote_amount: this.isEditMode ? (this.poEditItemsData?.quote_amount || 0) : 0,
          previous_quoute_amount: 0,
          is_discount: ((this.discountList.filter((x: any) => x.vendor_id == item.vendor_id && x.isSelected)?.length) ? true : false) || dicount,
          fee_ship: this.isEditMode ? (this.poEditItemsData?.shipping_cost || 0) : 0,
          // Initialize vendor_tax with the estimated vendor tax from the first material
          vendor_tax: Number(item.estimated_vendor_tax) || 0,
          // ONLY add these properties in edit mode
          ...(this.isEditMode ? {
            isExistingVendor: isExistingVendor,
            existingPoNumber: existingPoNumber,
            existingPoId: existingPoId
          } : {}),
          // Show PO number only for existing vendor in edit mode
          po_number: this.isEditMode && isExistingVendor ? existingPoNumber : null,
          calculation: {
            shippingCharges: this.isEditMode ? (this.poEditItemsData?.shipping_cost || 0) : 0, // Initialize with backend value in edit mode
            materialCost: 0, // Will be calculated by recalcVendor
            subTotalCost: this.isEditMode ? (this.poEditItemsData?.shipping_cost || 0) : 0, // Initial subtotal = shipping
            totalCost: this.isEditMode ? (this.poEditItemsData?.shipping_cost || 0) : 0, // Initial total = shipping
            costSaving: 0,
            vendorTax: Number(item.estimated_vendor_tax) || 0 // Initialize vendor tax
          }
        };

        this.vendorGroupList.push(vendorGroup);
      } else {
        // Vendor group exists, use its color for consistency
        item.color = vendorGroup.color;
        
        // Use helper function to ensure all prices and totals are properly set
        this.ensureMaterialPricesAndTotals(item);
        
        if (!vendorGroup.materialList.some((x: any) => x.id === item.id)) {
          //  If vendor group found, just add the item to the material list
          vendorGroup.materialList.push(item);

          // Update vendor_tax by adding this material's estimated vendor tax
          vendorGroup.vendor_tax = (Number(vendorGroup.vendor_tax) || 0) + (Number(item.estimated_vendor_tax) || 0);
          if (vendorGroup.calculation) {
            vendorGroup.calculation.vendorTax = vendorGroup.vendor_tax;
          }
        }

      }
      item.isSelected = true;
      item.isGroupMaterial = false;
      item.splitMaterialGroupList = [];
      
      // If re-selecting a previously deselected PO item, mark it as reselected
      if (this.isEditMode) {
        // Remove from deselected tracking if it was there
        const trackedIndex = this.deselectedPOItems.findIndex((tracked: any) => 
          (tracked.job_scope_item_id === item.job_scope_item_id) || 
          ((tracked.material_id || tracked.id) === (item.material_id || item.id))
        );
        
        if (trackedIndex !== -1) {
          this.deselectedPOItems.splice(trackedIndex, 1);
          console.log(`Re-selected item, removed from deselection tracking: ${item.product_name || item.material_name}`);
        }
        
        // Mark as selected PO item again if it was originally a PO item
        if (item.wasInPO) {
          item.isPOItem = true;
          item.wasInPO = false;
        }
      }
      
      if ((this.materialList.length == 1 && item.isSelected) || this.materialList.every((x: any) => x.isSelected)) {
        this.allMaterialSelected = true
      }
      vendorGroup.is_discount = ((this.discountList.filter((x: any) => x.vendor_id == item.vendor_id && x.isSelected)?.length) ? true : false) || dicount;
    } else {
      // Deselect the item - but KEEP it in the material list as available

      item.color = 'transparent';
      item.isSelected = false;
      
      // For edit mode: Track the deselection and mark item as available
      if (this.isEditMode) {
        // If this was a PO item, mark it as "was in PO but now available"
        if (item.isPOItem) {
          item.isPOItem = false;
          item.wasInPO = true; // New flag to indicate it was previously in the PO
          
          // Track the deselection for backend update
          const alreadyTracked = this.deselectedPOItems.some((tracked: any) => 
            (tracked.job_scope_item_id === item.job_scope_item_id) ||
            ((tracked.material_id || tracked.id) === (item.material_id || item.id))
          );
          
          if (!alreadyTracked) {
            this.deselectedPOItems.push({
              material_id: item.material_id || item.id,
              product_id: item.product_id,
              job_scope_item_id: item.job_scope_item_id || item.id,
              product_name: item.product_name || item.material_name,
              scope_id: item.scope_id,
              job_id: item.job_id
            });
            console.log(`Tracked deselected PO item (now available): ${item.product_name || item.material_name}`);
          }
        }
      }

      // IF Material UnChecked
      if (vendorGroup) {
        // Subtract the estimated vendor tax when removing material
        vendorGroup.vendor_tax = (Number(vendorGroup.vendor_tax) || 0) - (Number(item.estimated_vendor_tax) || 0);
        if (vendorGroup.calculation) {
          vendorGroup.calculation.vendorTax = vendorGroup.vendor_tax;
        }

        // Remove allocated shipping charges for this material before splicing
        if (vendorGroup.fee_ship && vendorGroup.materialList.length > 0) {
          // Remove the allocated fee_ship from the material being removed
          const mat = vendorGroup.materialList.find((x: any) => x.id === item.id);
          if (mat && mat.allocated_fee_ship) {
            mat.allocated_fee_ship = 0;
          }
        }

        const materialIndex = vendorGroup.materialList.findIndex((x: any) => x.id === item.id);
        if (vendorGroup.materialList?.length === 1) {
          if (materialIndex !== -1) {
            vendorGroup.materialList.splice(materialIndex, 1);
            let vendorIndex = this.vendorGroupList.findIndex((x: any) => x.vendor_id === vendorGroup.vendor_id)
            if (vendorIndex !== -1) {
              this.vendorGroupList.splice(vendorIndex, 1);  //Remove Vendor Also
            }
          }
        } else {
          vendorGroup.materialList.splice(materialIndex, 1);
        }

      }
      // Remove from separatePOJobList
      let vendorIndexInSeparate = this.separatePOJobList.findIndex(
        (v: any) => v?.vendor_id === vendorGroup?.vendor_id && v.materialList.some((m: any) => m.id === item.id)
      );

      if (vendorIndexInSeparate !== -1) {
        const separateVendorGroup = this.separatePOJobList[vendorIndexInSeparate];
        const materialIndex = separateVendorGroup.materialList.findIndex((x: any) => x.id === item.id);

        if (separateVendorGroup.materialList?.length === 1) {
          // Remove the last material and vendor row
          if (materialIndex !== -1) {
            separateVendorGroup.materialList.splice(materialIndex, 1);
            this.separatePOJobList.splice(vendorIndexInSeparate, 1); // Remove that vendor row only
          }
        } else {
          // Just remove the material from that vendor row
          separateVendorGroup.materialList.splice(materialIndex, 1);
        }
      }
      if (selectedJobGroup) {
        const jobIndex = selectedJobGroup.jobList.findIndex((x: any) => x.id === item.id);

        if (jobIndex !== -1) {
          // Remove allocated shipping charges for this material before splicing
          const mat = selectedJobGroup.jobList[jobIndex];
          if (mat && mat.allocated_fee_ship) {
            mat.allocated_fee_ship = 0;
          }
          selectedJobGroup.jobList.splice(jobIndex, 1);
        }
      }

      this.allMaterialSelected = false
      this.distributeShippingCost(vendorGroup);

      this.discountList.forEach(x => {
        if (x.vendor_id === vendorGroup.vendor_id) {
          x.isSelected = true
        } else {
          x.isSelected = false
        }
      })
    }


    if (event) { 
      // Ensure the material has proper totals before adding to job list
      this.ensureMaterialPricesAndTotals(item);
      this.appendJobList(item, vendorGroup) 
    }
    this.getQuoteAmount()
    this.calculateMaterialTotal()
    this.calculateVendorCalculation()
    this.calculateJobListrCalculation()
  }



  appendJobList(item: any, vendorGroup: any): void {
    if (this.purchaseOrderObject.type == 'bulk') {
      const jobIndex = this.selectedJobList.findIndex(x => x.id === item.job_id);

      if (jobIndex === -1) {
        // Add new job entry
        this.selectedJobList.push({
          name: item.job_number,
          id: item.job_id,
          color: item.color,
          jobList: [item], // Initialize with an array containing the item
          calculation: {
            shippingCharges: 0, // Initialize with default values
            materialCost: 0,
            vendorTax: 0, // Initialize vendor tax
            subTotalCost: 0,
            totalCost: 0,
            cost_escalation: 0,
            costSaving: 0
          },
          vendorGroup
        });
      } else {
        // Append item to the existing jobList
        this.selectedJobList[jobIndex].jobList.push(item);
      }
    }
  }



  removeVendor(item: any, index: number) {
    // Check if this is a split PO from separatePOJobList
    const isSplitPO = this.separatePOJobList && this.separatePOJobList.includes(item);

    if (isSplitPO) {
      // Handle split PO removal - move materials back to main vendor
      this.removeSplitPO(item, index);
    } else {
      // Handle regular vendor removal from main list
      const vendorMaterialList = this.vendorGroupList[index]?.materialList;

      if (!vendorMaterialList) return; // Ensure vendorMaterialList exists

      // Iterate through the materialList and deselect relevant items
      const vendorNamesToDeselect = new Set(vendorMaterialList.map((x: any) => x.vendor_name));

      this.materialList.forEach((element: any) => {
        if (vendorNamesToDeselect.has(element.vendor_name)) {
          element.isSelected = false;
          element.color = 'transparent';
        }
      });
      this.selectedJobList = this.selectedJobList.filter(x => !vendorMaterialList.some((y: any) => y.job_number == x.job_number))
      // Remove the vendor group
      this.vendorGroupList.splice(index, 1);
      this.allMaterialSelected = false
      
      // Turn off Group Material toggle when both vendor lists become empty
      if (this.vendorGroupList.length === 0 && (!this.separatePOJobList || this.separatePOJobList.length === 0)) {
        this.purchaseOrderObject.group_material_flag = false;
        this.allSplitMaterialSelected = false;
      }
      
      this.getQuoteAmount()
      this.calculateMaterialTotal()
      this.calculateJobListrCalculation();
    }
  }

  removeSplitPO(splitVendor: any, index: number) {
    if (!splitVendor || !splitVendor.materialList) return;

    // Find or create the main vendor group for this vendor
    let mainVendor = this.vendorGroupList.find((v: any) =>
      v.vendor_id === splitVendor.vendor_id || v.id === splitVendor.id
    );

    if (!mainVendor) {
      // Create new main vendor group if it doesn't exist
      mainVendor = {
        id: splitVendor.id,
        vendor_id: splitVendor.vendor_id,
        name: splitVendor.name,
        color: splitVendor.color,
        materialList: [],
        quote_amount: 0,
        fee_ship: 0,
        is_free_shipping: false,
        quote_number: '',
        calculation: {
          materialCost: 0,
          shippingCharges: 0,
          totalCost: 0,
          subTotalCost: 0,
          costSaving: 0,
          cost_escalation: 0
        },
        isExpand: true,
        isGroupMaterial: false
      };
      this.vendorGroupList.push(mainVendor);
    }

    // Move all materials from split PO back to main vendor
    splitVendor.materialList.forEach((material: any) => {
      // Check if material already exists in main vendor to avoid duplicates
      const existingMaterial = mainVendor.materialList.find((m: any) => m.id === material.id);
      if (!existingMaterial) {
        // Reset split-related flags
        const materialCopy = { ...material };
        materialCopy.isSplitMaterial = false;
        materialCopy.isGroupMaterial = false;
        mainVendor.materialList.push(materialCopy);
      }
    });

    // Remove the split PO from separatePOJobList
    if (this.separatePOJobList) {
      const splitIndex = this.separatePOJobList.findIndex((v: any) => v === splitVendor);
      if (splitIndex !== -1) {
        this.separatePOJobList.splice(splitIndex, 1);
      }
    }

    // Check if both vendor lists are empty and reset group material effects
    if ((!this.separatePOJobList || this.separatePOJobList.length === 0) && this.vendorGroupList.length === 0) {
      // Reset group material flag and related selections
      this.purchaseOrderObject.group_material_flag = false;
      this.allSplitMaterialSelected = false;

      // Reset all material group selections in main vendor list
      this.vendorGroupList.forEach((vendor: any) => {
        vendor.materialList.forEach((mat: any) => {
          mat.isGroupMaterial = false;
          mat.isSplitMaterial = false;
        });
      });
    }

    // Recalculate totals for the main vendor
    this.recalculateVendorTotals(mainVendor);

    // Update overall calculations
    this.getQuoteAmount();
    this.calculateMaterialTotal();
    this.calculateVendorCalculation();
    this.calculateJobListrCalculation();
  }


  allMaterialSelection(event: any) {
    if (!this.materialList?.length) {
      this.allMaterialSelected = false
      return
    }
    if (event) {
      this.materialList.forEach((element: any, index: number) => {
        this.selectedMaterial(true, element, index)
      });
    }
    else {
      this.materialList.forEach((element: any, index: number) => {
        this.selectedMaterial(false, element, index)
      });
      this.vendorGroupList = []
      this.selectedJobList = []

    }
  }

  selectedVendor(material: any, vendor: any) {
    console.log(`🔷 selectedVendor called`);
    console.log(`🔷 Material:`, {
      product_name: material.product_name,
      current_vendor_name: material.vendor_name,
      current_vendor_id: material.vendor_id,
      id: material.id,
      job_scope_item_id: material.job_scope_item_id
    });
    console.log(`🔷 Selected vendor object:`, vendor);
    console.log(`🔷 Vendor structure:`, {
      id: vendor.id,
      vendor_id: vendor.vendor_id,
      name: vendor.name,
      unit_price: vendor.unit_price
    });

    // ✅ CRITICAL FIX: Extract the correct vendor ID
    // The vendor object might have 'id' or 'vendor_id' depending on the API
    const newVendorId = vendor.vendor_id || vendor.id;
    const newVendorName = vendor.name;
    const newUnitPrice = vendor.unit_price;

    console.log(`🔷 Extracted values - ID: ${newVendorId}, Name: "${newVendorName}", Price: ${newUnitPrice}`);

    // ✅ Find the vendor group that contains THIS specific material
    let index = this.vendorGroupList.findIndex((vendorGroup: any) =>
      vendorGroup.materialList?.some((m: any) =>
        m.id === material.id ||
        m.job_scope_item_id === material.job_scope_item_id ||
        (m.product_name === material.product_name && m.vendor_id === material.vendor_id)
      )
    );

    console.log(`🔷 Found vendor group at index: ${index}`, index !== -1 ? this.vendorGroupList[index].name : 'NOT FOUND');

    // Store old vendor info for logging
    const oldVendorName = material.vendor_name;
    const oldVendorId = material.vendor_id;

    // Update material properties with extracted values
    material.vendor_name = newVendorName;
    material.unit_price = newUnitPrice;
    material.vendor_id = newVendorId;

    console.log(`🔷 Material updated - vendor_id: ${oldVendorId} → ${material.vendor_id}, vendor_name: "${oldVendorName}" → "${material.vendor_name}"`);

    // Update vendor group properties
    if (index !== -1) {
      const vendorGroup = this.vendorGroupList[index];

      // Check if ALL materials in this group have the same new vendor
      const allMaterialsSameVendor = vendorGroup.materialList.every(
        (m: any) => m.vendor_id === material.vendor_id
      );

      console.log(`🔷 All materials same vendor: ${allMaterialsSameVendor}`);

      // Only update vendor group if ALL materials now have the same vendor
      if (allMaterialsSameVendor) {
        vendorGroup.name = newVendorName;
        vendorGroup.vendor_id = newVendorId;
        console.log(`🔷 Vendor group updated - vendor_id: ${vendorGroup.vendor_id}, name: "${vendorGroup.name}"`);
      } else {
        console.warn(`🔷 Vendor group has mixed vendors - not updating group name/id`);
      }
    }

    this.getQuoteAmount()
    this.calculateMaterialTotal()
    this.calculateVendorCalculation()
    this.calculateJobListrCalculation();
  }

  // #endregion


  // #region Toogle Button PART HERE
  shippmentToggle(item: any) {
    item.is_free_shipping = !item.is_free_shipping
    item.fee_ship = 0
    this.getQuoteAmount()
    this.calculateMaterialTotal()
    this.calculateVendorCalculation()
    this.calculateJobListrCalculation();
    this.distributeShippingCost(item);
  }

  shippingChargesChange(item: any) {
    // Check if this is a split PO
    const isSplitPO = this.separatePOJobList &&
                     this.separatePOJobList.some((v: any) => v === item ||
                     (v.uniqueCode && v.uniqueCode === item.uniqueCode));

    this.distributeShippingCost(item);


    this.getQuoteAmount()
    this.calculateMaterialTotal()
    this.calculation = { ...this.calculation }
    this.calculateVendorCalculation()
    this.calculateJobListrCalculation();

  }

  vendorTaxChange(item: any) {
    // Update the vendor's calculation object
    if (item.calculation) {
      item.calculation.vendorTax = Number(item.vendor_tax) || 0;
    }

    // Recalculate totals to reflect the tax change
    this.getQuoteAmount();
    this.calculateMaterialTotal();
    this.calculation = { ...this.calculation };
    this.calculateVendorCalculation();
    this.calculateJobListrCalculation();
  }

  distributeShippingCost(item: any) {
    if (!item.materialList || !Array.isArray(item.materialList)) return;
    if (!item.fee_ship || isNaN(item.fee_ship) || item.fee_ship <= 0) {
      item.materialList.forEach((mat: any) => {
        mat.allocated_fee_ship = 0; // Reset allocated fee ship if fee ship is not valid
      });
      return;
    }

    const feeShip = Number(item.fee_ship);
    if (isNaN(feeShip) || feeShip === 0) return;
    const totalQty = item.materialList.reduce((sum: number, mat: any) => {
      return sum + (Number(mat.new_quantity) || 0);
    }, 0);
    if (totalQty === 0) return;

    item.materialList.forEach((mat: any) => {
      const qty = Number(mat.new_quantity) || 0;
      const share = (qty / totalQty) * feeShip;
      mat.allocated_fee_ship = Math.round(share * 10000) / 10000; // Round to 4 decimals
    });
  }

  groupMaterialToggle() {
    this.purchaseOrderObject.group_material_flag = !this.purchaseOrderObject.group_material_flag;

    // Jab toggle off ho, to sab selection reset
    if (!this.purchaseOrderObject.group_material_flag) {
      // Reset all group material flags in vendorGroupList
      this.vendorGroupList.forEach((vendor: any) => {
        vendor.isGroupMaterial = false;
        vendor.materialList.forEach((mat: any) => {
          mat.isGroupMaterial = false;
          mat.isSplitMaterial = false;
        });
      });
      
      // Reset all group material flags in separatePOJobList
      if (this.separatePOJobList && this.separatePOJobList.length > 0) {
        this.separatePOJobList.forEach((vendor: any) => {
          vendor.isGroupMaterial = false;
          vendor.materialList.forEach((mat: any) => {
            mat.isGroupMaterial = false;
            mat.isSplitMaterial = false;
          });
        });
      }
      
      // Clear the material split group list
      this.materialSplitGroupList = [];
      this.allSplitMaterialSelected = false;
      // this.selectedGroupMaterials = [];
    }
  }


  priceEngineeringToggle() {
    console.log('Price Engineering Toggle - Toggle state:', this.purchaseOrderObject.price_engineering_flag);
    
    if (this.purchaseOrderObject.price_engineering_flag) {
      // When toggling ON - preserve original contract_price values
      this.preserveOriginalContractPrices();
    } else {
      // When toggling OFF - restore original contract_price values
      this.restoreOriginalContractPrices();
    }
    
    // First, ensure all materials in the main material list have proper prices
    // This is crucial for Create mode where materials might not be in vendor groups yet
    if (this.materialList && this.materialList.length > 0) {
      this.materialList.forEach((material: any) => {
        this.ensureMaterialPricesAndTotals(material);
      });
    }
    
    // Ensure all materials in vendor groups have their totals properly set
    this.vendorGroupList.forEach((vendor: any) => {
      vendor.materialList?.forEach((material: any) => {
        this.ensureMaterialPricesAndTotals(material);
      });
    });
    
    // Also check separate PO job list if it exists
    if (this.separatePOJobList && this.separatePOJobList.length > 0) {
      this.separatePOJobList.forEach((vendor: any) => {
        vendor.materialList?.forEach((material: any) => {
          this.ensureMaterialPricesAndTotals(material);
        });
      });
    }
    
    this.getQuoteAmount()
    this.calculateMaterialTotal()
    this.calculateVendorCalculation()
    this.calculateJobListrCalculation();
  }

  /**
   * Preserve original contract_price values when toggling Price Engineering ON
   */
  preserveOriginalContractPrices() {
    // Preserve original values in main material list
    if (this.materialList && this.materialList.length > 0) {
      this.materialList.forEach((material: any) => {
        if (material.contract_price !== undefined && material.original_contract_price === undefined) {
          material.original_contract_price = material.contract_price;
        }
      });
    }
    
    // Preserve original values in vendor groups
    this.vendorGroupList.forEach((vendor: any) => {
      vendor.materialList?.forEach((material: any) => {
        if (material.contract_price !== undefined && material.original_contract_price === undefined) {
          material.original_contract_price = material.contract_price;
        }
      });
    });
    
    // Preserve original values in separate PO job list
    if (this.separatePOJobList && this.separatePOJobList.length > 0) {
      this.separatePOJobList.forEach((vendor: any) => {
        vendor.materialList?.forEach((material: any) => {
          if (material.contract_price !== undefined && material.original_contract_price === undefined) {
            material.original_contract_price = material.contract_price;
          }
        });
      });
    }
    
    console.log('Original contract prices preserved');
  }

  /**
   * Restore original contract_price values when toggling Price Engineering OFF
   */
  restoreOriginalContractPrices() {
    // Restore original values in main material list
    if (this.materialList && this.materialList.length > 0) {
      this.materialList.forEach((material: any) => {
        if (material.original_contract_price !== undefined) {
          material.contract_price = material.original_contract_price;
        }
      });
    }
    
    // Restore original values in vendor groups
    this.vendorGroupList.forEach((vendor: any) => {
      vendor.materialList?.forEach((material: any) => {
        if (material.original_contract_price !== undefined) {
          material.contract_price = material.original_contract_price;
        }
      });
    });
    
    // Restore original values in separate PO job list
    if (this.separatePOJobList && this.separatePOJobList.length > 0) {
      this.separatePOJobList.forEach((vendor: any) => {
        vendor.materialList?.forEach((material: any) => {
          if (material.original_contract_price !== undefined) {
            material.contract_price = material.original_contract_price;
          }
        });
      });
    }
    
    console.log('Original contract prices restored');
  }

  onChangeItemTotal(event: any, item: any) {
    
    // When user edits the total, we need to recalculate the contract_price
    if (item && item.new_quantity && item.new_quantity > 0) {
      // Recalculate contract_price based on new total and quantity
      item.contract_price = Number(item.new_quantity_total) / Number(item.new_quantity);
    }
    
    // CRITICAL FIX: Update the material in the correct vendor group (main or split)
    // Find which vendor group this material belongs to
    let vendorGroup = this.vendorGroupList.find((v: any) => 
      v.materialList?.some((m: any) => m.id === item.id || 
        (m.product_name === item.product_name && m.vendor_id === item.vendor_id))
    );
    
    let isSplitPO = false;
    if (!vendorGroup) {
      // Check in split PO list - also check by product name and vendor as backup
      vendorGroup = this.separatePOJobList?.find((v: any) => 
        v.materialList?.some((m: any) => m.id === item.id || 
          (m.product_name === item.product_name && m.vendor_id === item.vendor_id))
      );
      isSplitPO = !!vendorGroup;
    }
    
    // Also update ALL matching materials in ALL vendor groups (both main and split)
    // This ensures consistency across split POs
    const updateMaterialInGroups = (groups: any[], groupType: string) => {
      groups?.forEach((group: any) => {
        group.materialList?.forEach((mat: any) => {
          if (mat.id === item.id || (mat.product_name === item.product_name && mat.vendor_id === item.vendor_id)) {
            mat.new_quantity_total = item.new_quantity_total;
            mat.contract_price = item.contract_price;
          }
        });
      });
    };
    
    // Update in both lists to ensure consistency
    updateMaterialInGroups(this.vendorGroupList, 'main');
    updateMaterialInGroups(this.separatePOJobList, 'split');
    
    if (vendorGroup) {
      
      // Update the material in the vendor group's material list
      const materialInVendor = vendorGroup.materialList.find((m: any) => m.id === item.id);
      if (materialInVendor) {
        materialInVendor.new_quantity_total = item.new_quantity_total;
        materialInVendor.contract_price = item.contract_price;
        
        // CRITICAL: Recalculate quote amount for this vendor group
        // This ensures the vendor group's total reflects the price change
        if (this.purchaseOrderObject.price_engineering_flag) {
          vendorGroup.quote_amount = vendorGroup.materialList.reduce((total: any, material: any) => {
            return total + (Number(material.new_quantity_total) || 0);
          }, 0);
          
          
          // Recalculate percentages for the vendor group
          const totalAmount = vendorGroup.quote_amount;
          if (totalAmount > 0) {
            vendorGroup.materialList.forEach((material: any) => {
              // For price engineering, percentages should be based on new totals
              material.percentage = ((Number(material.new_quantity_total) || 0) / totalAmount) * 100;
            });
          }
        }
        
        // Log the expected calculations
        if (isSplitPO) {
          const previousTotal = vendorGroup.materialList.reduce((sum: number, m: any) => 
            sum + (Number(m.previous_quantity_total) || 0), 0) || 0;
          const newTotal = vendorGroup.materialList.reduce((sum: number, m: any) => 
            sum + (Number(m.new_quantity_total) || 0), 0) || 0;
          
        }
      }
    }
    
    this.getQuoteAmount()
    this.calculateMaterialTotal()
    this.calculateVendorCalculation()
    this.calculateJobListrCalculation();
  }

  //#endregion


  //#region Calculation PART HERE

  /**
   * Helper function to ensure material has all required price fields and totals calculated
   * This ensures consistency across Create and Edit modes
   */
  ensureMaterialPricesAndTotals(material: any): void {
    // Get the best available price - check all possible price fields
    const unitPrice = material.contract_price || material.unit_price || material.selling_price || 
                      material.unit_cost || material.po_contract_cost || 0;
    
    // Log for debugging in edit mode
    if (this.isEditMode && material.isPOItem) {
      console.log(`ensureMaterialPricesAndTotals for PO item ${material.product_name}:`, {
        contract_price: material.contract_price,
        unit_price: material.unit_price,
        selling_price: material.selling_price,
        unit_cost: material.unit_cost,
        po_contract_cost: material.po_contract_cost,
        final_unitPrice: unitPrice,
        quantity: material.quantity,
        new_quantity: material.new_quantity
      });
    }
    
    // Ensure all price fields are set for consistent display
    if (!material.contract_price && unitPrice > 0) {
      material.contract_price = unitPrice;
    }
    if (!material.unit_price && unitPrice > 0) {
      material.unit_price = unitPrice;
    }
    
    // Ensure quantities are initialized
    if (!material.quantity && material.quantity !== 0) {
      material.quantity = 0;
    }
    if (!material.new_quantity && material.new_quantity !== 0) {
      material.new_quantity = material.quantity || 0;
    }
    
    // Calculate totals
    const quantity = Number(material.quantity) || 0;
    const newQuantity = Number(material.new_quantity) || 0;
    
    material.previous_quantity_total = Number(unitPrice) * quantity;
    material.new_quantity_total = Number(unitPrice) * newQuantity;
    
    // Debug logging
    if (unitPrice === 0) {
      console.warn(`Material ${material.product_name} has no price set! Available fields:`, {
        contract_price: material.contract_price,
        unit_price: material.unit_price,
        selling_price: material.selling_price,
        unit_cost: material.unit_cost,
        po_contract_cost: material.po_contract_cost
      });
    } else if (this.isEditMode) {
      console.log(`Material ${material.product_name} totals calculated:`, {
        unitPrice,
        quantity,
        newQuantity,
        previous_total: material.previous_quantity_total,
        new_total: material.new_quantity_total
      });
    }
  }

  quantityChange(item: any) {
    // In edit mode, allow decreasing quantity but not below 0
    if (this.isEditMode) {
      // Store original order quantity if not already stored
      if (item.original_order_quantity === undefined) {
        item.original_order_quantity = item.quantity || 0;
      }
      
      // Allow any quantity >= 0 in edit mode
      if (item.new_quantity < 0) {
        item.new_quantity = 0;
      }
      
      // When quantity is decreased to 0, handle like deselection
      if (item.new_quantity === 0 && item.original_order_quantity > 0) {
        // Find the corresponding item in material list
        const materialItem = this.materialList.find((mat: any) => 
          mat.job_scope_item_id === item.job_scope_item_id || 
          mat.id === item.job_scope_item_id
        );
        
        if (materialItem) {
          // Make it available in material list like we do for deselected items
          materialItem.isSelected = false;
          materialItem.isPOItem = false;
          materialItem.wasInPO = true; // Mark that it was previously in PO
          materialItem.color = 'transparent';
          
          // Update available quantity - add back the original quantity
          materialItem.pending_purchase_quantity = (materialItem.pending_purchase_quantity || 0) + item.original_order_quantity;
          materialItem.quantity = materialItem.pending_purchase_quantity;
          
          // Track this as deselected item for backend update
          const alreadyTracked = this.deselectedPOItems.some((tracked: any) => 
            tracked.job_scope_item_id === materialItem.job_scope_item_id
          );
          
          if (!alreadyTracked) {
            this.deselectedPOItems.push({
              material_id: materialItem.material_id || materialItem.id,
              product_id: materialItem.product_id,
              job_scope_item_id: materialItem.job_scope_item_id || materialItem.id,
              product_name: materialItem.product_name || materialItem.material_name,
              scope_id: materialItem.scope_id,
              job_id: materialItem.job_id,
              quantity: item.original_order_quantity // Track the quantity that was removed
            });
            console.log(`Item quantity reduced to 0, added to deselection tracking: ${materialItem.product_name}`);
          }
          
          // Remove from vendor group
          const vendorGroup = this.vendorGroupList.find((v: any) => v.vendor_id === item.vendor_id);
          if (vendorGroup) {
            const itemIndex = vendorGroup.materialList.findIndex((m: any) => 
              m.job_scope_item_id === item.job_scope_item_id
            );
            if (itemIndex > -1) {
              vendorGroup.materialList.splice(itemIndex, 1);
            }
          }
        }
      } else if (item.new_quantity > 0 && item.original_order_quantity > 0) {
        // Quantity changed but not to 0 - update available quantity
        const quantityDiff = item.original_order_quantity - item.new_quantity;
        
        // Find the corresponding item in material list
        const materialItem = this.materialList.find((mat: any) => 
          mat.job_scope_item_id === item.job_scope_item_id || 
          mat.id === item.job_scope_item_id
        );
        
        if (materialItem && quantityDiff !== 0) {
          // Update the pending_purchase_quantity based on the difference
          materialItem.pending_purchase_quantity = (materialItem.pending_purchase_quantity || 0) + quantityDiff;
          
          // If it was previously removed from deselectedPOItems, update the tracking
          const trackedIndex = this.deselectedPOItems.findIndex((tracked: any) => 
            tracked.job_scope_item_id === materialItem.job_scope_item_id
          );
          
          if (trackedIndex !== -1) {
            // Remove from deselected tracking since it's back with quantity > 0
            this.deselectedPOItems.splice(trackedIndex, 1);
            console.log(`Item quantity increased from 0, removed from deselection tracking: ${materialItem.product_name}`);
          }
        }
      }
      
    } else {
      // Create mode: clamp new_quantity within valid range
      if (item.new_quantity > item.quantity || item.new_quantity < 1) {
        item.new_quantity = item.quantity;
      }
      if (!this.purchaseOrderObject.price_engineering_flag) {
        item.previous_quantity_total = Number(item.unit_price || item.contract_price || 0) * Number(item.quantity || 0);
      }
    }

    item.new_quantity_total = Number(item.contract_price) * Number(item.new_quantity) || 1
    this.getQuoteAmount()
    this.calculateMaterialTotal();
    this.calculateVendorCalculation();
    let vendorGroup = this.vendorGroupList.find((x: any) => x.name === item.vendor_name);
    this.distributeShippingCost(vendorGroup);
    this.calculateJobListrCalculation();
  }

  quoteAmountchange(vendorGroup: any) {
    
    // Check if this vendor is from separatePOJobList (split PO group)
    const isSplitPO = this.separatePOJobList && 
                     this.separatePOJobList.some((v: any) => v === vendorGroup || 
                     (v.uniqueCode && v.uniqueCode === vendorGroup.uniqueCode));
    
    let amount = Number(vendorGroup.quote_amount);
    
    // CRITICAL FIX: Don't corrupt the user's input
    
    // CRITICAL FIX FOR SPLIT PO: Ensure percentages are calculated before redistribution
    if (vendorGroup.materialList && vendorGroup.materialList.length > 0) {
        // Check if percentages exist, if not calculate them
        const hasPercentages = vendorGroup.materialList.every((item: any) => item.percentage !== undefined && item.percentage !== null);
        
        if (!hasPercentages || isSplitPO) {
            const totalPreviousAmount = vendorGroup.materialList.reduce((total: any, material: any) => {
                return total + (Number(material.previous_quantity_total) || 0);
            }, 0);
            
            if (totalPreviousAmount > 0) {
                vendorGroup.materialList.forEach((material: any) => {
                    material.percentage = ((Number(material.previous_quantity_total) || 0) / totalPreviousAmount) * 100;
                });
            } else {
                // Equal distribution if no previous totals
                const equalPercentage = 100 / vendorGroup.materialList.length;
                vendorGroup.materialList.forEach((material: any) => {
                    material.percentage = equalPercentage;
                });
            }
        }
        
        // Now redistribute amount across materials by their percentage
        vendorGroup.materialList.forEach((item: any) => {
            let oldTotal = item.new_quantity_total;
            item.new_quantity_total = (item.percentage / 100) * amount;
        });
    }
    
    // Recalculate everything with clean data
    this.calculateMaterialTotal();
    this.calculateVendorCalculation();
    this.calculateJobListrCalculation();
    
    
    // Log split PO calculations specifically
    if (isSplitPO) {
        // Debug: Check material values after redistribution
        const materialTotals = vendorGroup.materialList?.map((m: any) => ({
            name: m.product_name,
            previous: m.previous_quantity_total,
            new: m.new_quantity_total,
            percentage: m.percentage
        }));
        
        const totalPrevious = vendorGroup.materialList?.reduce((sum: number, m: any) => 
            sum + (Number(m.previous_quantity_total) || 0), 0) || 0;
        const totalNew = vendorGroup.materialList?.reduce((sum: number, m: any) => 
            sum + (Number(m.new_quantity_total) || 0), 0) || 0;
        
        
    }
  }

  calculateVendorQuantity(item: any) {
    this.vendorGroupList.forEach((vendorGroup: any) => {
      vendorGroup.materialList.forEach((material: any) => {
        if (material.id === item.id) {
          material.new_quantity = item.new_quantity;
          material.new_quantity_total = Number(material.contract_price) * Number(item.new_quantity) || 0;
        }
      });
    });
  }



  calculateMaterialTotal() {
    // For contract_price_total, match what's displayed in CONTRACT COST column
    // The column shows: price_engineering_flag ? contract_price : previous_quantity_total
    // Both contract_price and previous_quantity_total should be TOTALS, not unit prices
    this.calculation.contract_price_total = this.materialList?.reduce((total: any, material: any) => {
      let contractCost = 0;

      if (this.purchaseOrderObject.price_engineering_flag) {
        // When price engineering is ON, column shows contract_price (which should be a total)
        contractCost = Number(material.contract_price || 0);
      } else {
        // When price engineering is OFF, column shows previous_quantity_total
        contractCost = Number(material.previous_quantity_total || 0);
      }

      return total + contractCost;
    }, 0) || 0;

    this.calculation.previous_total = this.materialList?.reduce((total: any, material: any) => {
      return total + (Number(material.previous_quantity_total) || 0);
    }, 0) || 0;
    this.calculation.new_total = this.materialList?.reduce((total: any, material: any) => {
      return total + (Number(material.new_quantity_total) || 0);
    }, 0) || 0;

    // Trigger change detection by creating a new object reference
    this.calculation = { ...this.calculation };

    // Force Angular change detection
    this.cdr.detectChanges();
  }

  // Stage 6.7 Phase 4: Tax total calculation helper methods
  getTotalVendorTax(): number {
    return this.materialList?.reduce((total: number, material: any) => {
      return total + (Number(material.actual_vendor_tax_paid) || 0);
    }, 0) || 0;
  }

  getTotalEstimatedUseTax(): number {
    return this.materialList?.reduce((total: number, material: any) => {
      return total + (Number(material.estimated_use_tax) || 0);
    }, 0) || 0;
  }

  getTotalEstimatedVendorTax(): number {
    return this.materialList?.reduce((total: number, material: any) => {
      return total + (Number(material.estimated_vendor_tax) || 0);
    }, 0) || 0;
  }

  getTotalUseTaxOwed(): number {
    return this.materialList?.reduce((total: number, material: any) => {
      return total + (Number(material.actual_use_tax) || 0);
    }, 0) || 0;
  }

  getTotalTaxVariance(): number {
    return this.materialList?.reduce((total: number, material: any) => {
      return total + (Number(material.use_tax_variance) || 0);
    }, 0) || 0;
  }

  calculateVendorCalculation() {
    this.vendorGroupList.forEach((element: any) => {

      // CORRECT: Calculate material cost as sum of material totals
      element.calculation.materialCost = element.materialList?.reduce((total: any, material: any) => {
        return total + (Number(material.new_quantity_total) || 0);
      }, 0) || 0;

      // CORRECT: Shipping charges = fee_ship (user input, not allocated amounts)
      element.calculation.shippingCharges = Number(element.fee_ship) || 0;

      // Stage 6.7: Add vendor tax to calculation
      element.calculation.vendorTax = Number(element.vendor_tax) || 0;

      // CORRECT: Total cost = material cost + shipping charges + vendor tax
      element.calculation.totalCost = element.calculation.materialCost + element.calculation.shippingCharges + element.calculation.vendorTax;
      element.calculation.subTotalCost = element.calculation.materialCost + element.calculation.shippingCharges + element.calculation.vendorTax;

      let previous_total = element.materialList?.reduce((total: any, material: any) => {
        return total + (Number(material.previous_quantity_total) || 0); // Add unit_price, default to 0 if undefined
      }, 0);

      let new_total = element.materialList?.reduce((total: any, material: any) => {
        return total + (Number(material.new_quantity_total) || 0); // Add unit_price, default to 0 if undefined
      }, 0);

      // CRITICAL FIX: Only calculate cost escalation/saving when price engineering is enabled
      if (this.purchaseOrderObject.price_engineering_flag) {
        
        if (new_total > previous_total) {
          // Price increased = Cost Escalation
          element.calculation.cost_escalation = Number(new_total) - Number(previous_total);
          element.calculation.costSaving = 0;
        } else if (previous_total > new_total) {
          // Price decreased = Cost Saving  
          element.calculation.costSaving = Number(previous_total) - Number(new_total);
          element.calculation.cost_escalation = 0;
        } else {
          // No change
          element.calculation.cost_escalation = 0;
          element.calculation.costSaving = 0;
        }
      } else {
        // Price engineering OFF = Reset values
        element.calculation.cost_escalation = 0;
        element.calculation.costSaving = 0;
      }
      
    });

    // CRITICAL FIX: Also calculate for split PO groups
    this.separatePOJobList?.forEach((element: any) => {
      
      // IMPORTANT: Only ensure material prices are set, but DON'T overwrite new_quantity_total
      // when price engineering is enabled as it's controlled by quote amount changes
      element.materialList?.forEach((material: any) => {
        // Ensure basic price fields are set
        if (!material.contract_price && material.unit_price) {
          material.contract_price = material.unit_price;
        }
        
        // Only recalculate new_quantity_total if price engineering is OFF
        // When price engineering is ON, new_quantity_total is controlled by quote amount changes
        if (!this.purchaseOrderObject.price_engineering_flag) {
          this.ensureMaterialPricesAndTotals(material);
          material.new_quantity_total = Number(material.contract_price) * Number(material.new_quantity || material.quantity || 1);
        }
        
        // Always ensure previous_quantity_total exists for comparison
        if (!material.previous_quantity_total) {
          material.previous_quantity_total = Number(material.quantity || 1) * Number(material.contract_price || material.unit_price || 0);
        }
      });
      
      // CORRECT: Calculate material cost as sum of material totals
      element.calculation.materialCost = element.materialList?.reduce((total: any, material: any) => {
        return total + (Number(material.new_quantity_total) || 0);
      }, 0) || 0;

      // CORRECT: Shipping charges = fee_ship (user input, not allocated amounts)
      element.calculation.shippingCharges = Number(element.fee_ship) || 0;

      // Stage 6.7: Add vendor tax to calculation
      element.calculation.vendorTax = Number(element.vendor_tax) || 0;

      // CORRECT: Total cost = material cost + shipping charges + vendor tax
      element.calculation.totalCost = element.calculation.materialCost + element.calculation.shippingCharges + element.calculation.vendorTax;
      element.calculation.subTotalCost = element.calculation.materialCost + element.calculation.shippingCharges + element.calculation.vendorTax;

      let previous_total = element.materialList?.reduce((total: any, material: any) => {
        return total + (Number(material.previous_quantity_total) || 0);
      }, 0);

      let new_total = element.materialList?.reduce((total: any, material: any) => {
        return total + (Number(material.new_quantity_total) || 0);
      }, 0);


      // CRITICAL FIX: Only calculate cost escalation/saving when price engineering is enabled
      if (this.purchaseOrderObject.price_engineering_flag) {
        
        if (new_total > previous_total) {
          // Price increased = Cost Escalation
          element.calculation.cost_escalation = Number(new_total) - Number(previous_total);
          element.calculation.costSaving = 0;
        } else if (previous_total > new_total) {
          // Price decreased = Cost Saving  
          element.calculation.costSaving = Number(previous_total) - Number(new_total);
          element.calculation.cost_escalation = 0;
        } else {
          // No change
          element.calculation.cost_escalation = 0;
          element.calculation.costSaving = 0;
        }
      } else {
        // Price engineering OFF = Reset values
        element.calculation.cost_escalation = 0;
        element.calculation.costSaving = 0;
      }
      
    });
  }

  getQuoteAmount() {
    this.vendorGroupList.forEach((element: any) => {
      
      if (this.purchaseOrderObject.price_engineering_flag) {
        // CORRECT: Quote amount = material totals only (exclude shipping)
        element.quote_amount = element.materialList?.reduce((total: any, material: any) => {
          return total + (Number(material.new_quantity_total) || 0);
        }, 0) || 0;
        
        let previous_quote_amount = element.materialList?.reduce((total: any, material: any) => {
          return total + (Number(material.previous_quantity_total) || 0);
        }, 0) || 0;
        this.calculatePercentages(element.materialList, previous_quote_amount, true)
      } else {
        // CORRECT: Quote amount = material totals only (exclude shipping)
        element.quote_amount = element.materialList?.reduce((total: any, material: any) => {
          return total + (Number(material.previous_quantity_total) || 0);
        }, 0) || 0;
      }
      
    });
    
    // CORRECT: Apply same logic to separate PO list
    this.separatePOJobList?.forEach((element: any) => {
      if (this.purchaseOrderObject.price_engineering_flag) {
        // CORRECT: Quote amount = material totals only (exclude shipping)
        element.quote_amount = element.materialList?.reduce((total: any, material: any) => {
          return total + (Number(material.new_quantity_total) || 0);
        }, 0) || 0;
        
        let previous_quote_amount = element.materialList?.reduce((total: any, material: any) => {
          return total + (Number(material.previous_quantity_total) || 0);
        }, 0) || 0;
        this.calculatePercentages(element.materialList, previous_quote_amount, true)
      } else {
        // CORRECT: Quote amount = material totals only (exclude shipping)
        element.quote_amount = element.materialList?.reduce((total: any, material: any) => {
          return total + (Number(material.previous_quantity_total) || 0);
        }, 0) || 0;
      }
    });
  }

  calculatePercentages(items: any, totalAmount: any, isPriceEngineering: boolean) {
    
    if (!items || !Array.isArray(items) || items.length === 0) {
      return;
    }
    
    // Validate total amount
    const validTotalAmount = Number(totalAmount);
    if (!validTotalAmount || validTotalAmount <= 0) {
      // Fallback: equal distribution
      const equalPercentage = 100 / items.length;
      items.forEach((item: any) => {
        item.percentage = equalPercentage;
      });
      return;
    }
    
    items.forEach((item: any) => {
      if (isPriceEngineering) {
        const itemTotal = Number(item?.previous_quantity_total) || 0;
        item.percentage = (itemTotal / validTotalAmount) * 100;
      } else {
        // When price engineering is OFF, percentage based on current totals
        const itemTotal = Number(item?.new_quantity_total || item?.previous_quantity_total) || 0;
        item.percentage = (itemTotal / validTotalAmount) * 100;
      }
    });
    
    // Validate percentages add up to ~100%
    const totalPercentage = items.reduce((total: number, item: any) => {
      return total + (Number(item.percentage) || 0);
    }, 0);
    
    if (Math.abs(totalPercentage - 100) > 0.01) {
      
      // Adjust the largest item to make total exactly 100%
      const largestItem = items.reduce((max: any, item: any) => 
        (item.percentage > max.percentage) ? item : max
      );
      if (largestItem) {
        const adjustment = 100 - totalPercentage;
        largestItem.percentage += adjustment;
      }
    }
    
  }

  calculateJobListrCalculation() {

    if (this.selectedJobList[0]?.jobList?.length) {
      this.selectedJobList.forEach((element: any) => {
        let previous_total = element.jobList?.reduce((total: any, material: any) => {
          return total + (Number(material.previous_quantity_total) || 0);
        }, 0);

        let new_total = element.jobList?.reduce((total: any, material: any) => {
          return total + (Number(material.new_quantity_total) || 0);
        }, 0);

        element.calculation.materialCost = new_total;

        // Calculate vendor tax from jobList materials
        let vendorTaxFromJobList = element.jobList.reduce((total: any, material: any) => {
          return total + (Number(material.estimated_vendor_tax) || 0);
        }, 0);

        // CRITICAL FIX: Calculate shipping charges from jobList materials
        let shippingFromJobList = element.jobList.reduce((total: any, material: any) => {
          return total + (Number(material.allocated_fee_ship) || 0);
        }, 0);
        
        // CRITICAL FIX: Also include shipping charges and vendor tax from split PO groups for this job
        let shippingFromSplitPOs = 0;
        let vendorTaxFromSplitPOs = 0;
        if (this.separatePOJobList && this.separatePOJobList.length > 0) {
          this.separatePOJobList.forEach((splitVendor: any) => {
            // Check if this split vendor has materials for this job
            splitVendor.materialList?.forEach((material: any) => {
              if (material.job_id === element.id) {
                // Add allocated shipping for this material
                shippingFromSplitPOs += Number(material.allocated_fee_ship) || 0;
                // Add vendor tax for this material
                vendorTaxFromSplitPOs += Number(material.estimated_vendor_tax) || 0;
              }
            });
            
            // If this split vendor has materials for this job, we might need to add proportional shipping
            // Check if any material in this split vendor belongs to this job
            const jobMaterialsInSplit = splitVendor.materialList?.filter((m: any) => m.job_id === element.id) || [];
            if (jobMaterialsInSplit.length > 0 && splitVendor.fee_ship > 0) {
              // Calculate the proportion of shipping for this job's materials
              const totalMaterialCost = splitVendor.materialList?.reduce((sum: number, m: any) => 
                sum + (Number(m.new_quantity_total) || 0), 0) || 0;
              const jobMaterialCost = jobMaterialsInSplit.reduce((sum: number, m: any) => 
                sum + (Number(m.new_quantity_total) || 0), 0);
              
              if (totalMaterialCost > 0) {
                const proportionalShipping = (jobMaterialCost / totalMaterialCost) * Number(splitVendor.fee_ship);
                // Use the higher of allocated or proportional (to avoid double counting)
                const allocatedForThisJob = jobMaterialsInSplit.reduce((sum: number, m: any) => 
                  sum + (Number(m.allocated_fee_ship) || 0), 0);
                if (proportionalShipping > allocatedForThisJob) {
                  shippingFromSplitPOs += (proportionalShipping - allocatedForThisJob);
                }
              }
            }
          });
        }
        
        element.calculation.shippingCharges = shippingFromJobList + shippingFromSplitPOs;
        element.calculation.vendorTax = vendorTaxFromJobList + vendorTaxFromSplitPOs;

        element.calculation.totalCost = new_total + (element.calculation.shippingCharges || 0) + (element.calculation.vendorTax || 0);
        element.calculation.subTotalCost = new_total + (element.calculation.shippingCharges || 0) + (element.calculation.vendorTax || 0);
        if (!this.purchaseOrderObject?.price_engineering_flag) {
          if (new_total < previous_total) {
            element.calculation.costSaving = previous_total - new_total;
            element.calculation.cost_escalation = 0;
          } else {
            element.calculation.cost_escalation = new_total - previous_total;
            element.calculation.costSaving = 0;
          }
        } else {
          // Price engineering mode - calculate based on contract price vs actual price
          // This should match the vendor calculation logic
          if (new_total < previous_total) {
            element.calculation.costSaving = previous_total - new_total;
            element.calculation.cost_escalation = 0;
          } else {
            element.calculation.cost_escalation = new_total - previous_total;
            element.calculation.costSaving = 0;
          }
        }
      });
    } else {
      // Don't reset the main calculation totals - they were already calculated in calculateMaterialTotal()
      // Only reset selectedJobList-specific calculations if there are any
      this.selectedJobList.forEach((element: any) => {
        if (element.calculation) {
          element.calculation.materialCost = 0;
          element.calculation.shippingCharges = 0;
          element.calculation.vendorTax = 0;
          element.calculation.totalCost = 0;
          element.calculation.subTotalCost = 0;
          element.calculation.costSaving = 0;
          element.calculation.cost_escalation = 0;
        }
      });
      // Don't clear selectedJobList here as it might be needed for other operations
    }

  }


  //#endregion

  //#region Validation and Debugging Helpers
  
  /**
   * Debug helper specifically for split PO issues
   * Call this when split PO calculations aren't working as expected
   */
  debugSplitPOCalculations() {
    
    if (!this.separatePOJobList || this.separatePOJobList.length === 0) {
      return;
    }
    
    this.separatePOJobList.forEach((vendor: any, index: number) => {
      
      if (vendor.materialList && vendor.materialList.length > 0) {
        vendor.materialList.forEach((mat: any) => {
        });
        
        // Verify percentages
        const totalPercentage = vendor.materialList.reduce((sum: number, mat: any) => 
          sum + (Number(mat.percentage) || 0), 0);
      }
      
      if (vendor.calculation) {
        
        // Check if calculations are correct
        const expectedMaterialCost = vendor.materialList?.reduce((sum: number, mat: any) => 
          sum + (Number(mat.new_quantity_total) || 0), 0) || 0;
        
        if (Math.abs(vendor.calculation.materialCost - expectedMaterialCost) > 0.01) {
        }
        
        if (this.purchaseOrderObject.price_engineering_flag) {
          const previousTotal = vendor.materialList?.reduce((sum: number, mat: any) => 
            sum + (Number(mat.previous_quantity_total) || 0), 0) || 0;
          const newTotal = vendor.materialList?.reduce((sum: number, mat: any) => 
            sum + (Number(mat.new_quantity_total) || 0), 0) || 0;
          
          
          if (newTotal > previousTotal) {
            const expectedEscalation = newTotal - previousTotal;
            if (Math.abs(vendor.calculation.cost_escalation - expectedEscalation) > 0.01) {
            }
          } else if (previousTotal > newTotal) {
            const expectedSaving = previousTotal - newTotal;
            if (Math.abs(vendor.calculation.costSaving - expectedSaving) > 0.01) {
            }
          }
        }
      } else {
      }
    });
    
  }
  
  /**
   * Validates and logs the current calculation state for debugging
   * Call this method to verify cost escalation/saving calculations are working
   */
  validateCalculationState() {
    
    // Validate main vendor groups
    this.vendorGroupList.forEach((vendor: any, index: number) => {
      
      if (vendor.calculation) {
      }
      
      // Validate material percentages
      if (vendor.materialList && vendor.materialList.length > 0) {
        const totalPercentage = vendor.materialList.reduce((total: number, material: any) => {
          return total + (Number(material.percentage) || 0);
        }, 0);
        if (Math.abs(totalPercentage - 100) > 0.01) {
        }
      }
    });
    
    // Validate split PO groups
    if (this.separatePOJobList && this.separatePOJobList.length > 0) {
      this.separatePOJobList.forEach((vendor: any, index: number) => {
        
        if (vendor.calculation) {
        }
      });
    }
  }
  
  //#endregion

  //#region  Submit PART HERE
  save(purchaseForm: NgForm) {
    if (purchaseForm.form?.valid) {
      this.createPurchaseOrder()
    }
  }

  //#endregion

}
